import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// RTL languages
export const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'zh', 'vi', 'ko', 'tl', 'ar'];

// Check if current language is RTL
export const isRTL = (lang) => RTL_LANGUAGES.includes(lang);

// Map browser language codes to our supported languages
const mapBrowserLanguage = (browserLang) => {
  if (!browserLang) return 'en';
  const lang = browserLang.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(lang) ? lang : 'en';
};

// Translation resources
const resources = {
  en: {
    translation: {
      // Common
      "app.name": "Goodwin Cares",
      "app.tagline": "Compassionate Hospice Support",
      "common.loading": "Loading...",
      "common.save": "Save",
      "common.cancel": "Cancel",
      "common.delete": "Delete",
      "common.edit": "Edit",
      "common.close": "Close",
      "common.submit": "Submit",
      "common.back": "Back",
      "common.next": "Next",
      "common.yes": "Yes",
      "common.no": "No",
      "common.search": "Search",
      "common.filter": "Filter",
      "common.refresh": "Refresh",
      "common.settings": "Settings",
      "common.profile": "Profile",
      "common.important": "Important",
      "common.returnHome": "Return Home",
      
      // Command Center
      "commandCenter.title": "Command Center",
      "commandCenter.subtitle": "Real-time operations dashboard • AI-powered automation",
      "commandCenter.status.live": "LIVE",
      "commandCenter.status.polling": "POLLING",
      "commandCenter.status.connected": "WebSocket connected",
      "commandCenter.autoRefresh": "Auto-refresh",
      "commandCenter.tabs.overview": "Overview",
      "commandCenter.tabs.alerts": "Alerts",
      "commandCenter.tabs.scheduling": "Scheduling",
      "commandCenter.tabs.vitals": "Vitals",
      "commandCenter.tabs.predictive": "Predictive AI",
      "commandCenter.stats.criticalAlerts": "Critical Alerts",
      "commandCenter.stats.activeAlerts": "Active Alerts",
      "commandCenter.stats.resolvedThisWeek": "Resolved This Week",
      "commandCenter.stats.avgResolutionTime": "Avg Resolution",
      "commandCenter.stats.automationActions": "Automation Actions",
      "commandCenter.intervention.title": "Trigger Interventions",
      "commandCenter.intervention.description": "Run AI-powered intervention protocols for at-risk patients",
      
      // Notification Center
      "notificationCenter.title": "Notification Center",
      "notificationCenter.subtitle": "Monitor and manage patient communication",
      "notificationCenter.loadError": "Failed to load notification data",
      "notificationCenter.connected": "Connected",
      "notificationCenter.disconnected": "Disconnected",
      "notificationCenter.active": "Active",
      "notificationCenter.inactive": "Inactive",
      "notificationCenter.tabs.dashboard": "Dashboard",
      "notificationCenter.tabs.history": "History",
      "notificationCenter.stats.totalSent": "Total Sent",
      "notificationCenter.stats.deliveryRate": "Delivery Rate",
      "notificationCenter.stats.emailSent": "Emails Sent",
      "notificationCenter.stats.smsSent": "SMS Sent",
      "notificationCenter.channelBreakdown": "Channel Breakdown",
      "notificationCenter.deliveryStatus": "Delivery Status",
      "notificationCenter.status.successful": "Successful",
      "notificationCenter.status.pending": "Pending",
      "notificationCenter.status.failed": "Failed",
      "notificationCenter.searchPlaceholder": "Search by patient ID...",
      "notificationCenter.filterChannel": "Channel",
      "notificationCenter.filterStatus": "Status",
      "notificationCenter.historyTitle": "Notification History",
      "notificationCenter.historyDescription": "View and manage sent notifications",
      "notificationCenter.noHistory": "No notifications found",
      "notificationCenter.resend": "Resend",
      "notificationCenter.resendTitle": "Resend Notification",
      "notificationCenter.resendDescription": "Send this notification again to the patient",
      "notificationCenter.resendSuccess": "Notification resent successfully",
      "notificationCenter.resendError": "Failed to resend notification",
      
      // Email Analytics
      "emailAnalytics.title": "Email Analytics",
      "emailAnalytics.subtitle": "Track email engagement and identify communication issues",
      "emailAnalytics.loadError": "Failed to load analytics",
      "emailAnalytics.period.7days": "Last 7 days",
      "emailAnalytics.period.14days": "Last 14 days",
      "emailAnalytics.period.30days": "Last 30 days",
      "emailAnalytics.period.90days": "Last 90 days",
      "emailAnalytics.stats.sent": "Emails Sent",
      "emailAnalytics.stats.deliveryRate": "Delivery Rate",
      "emailAnalytics.stats.openRate": "Open Rate",
      "emailAnalytics.stats.clickRate": "Click Rate",
      "emailAnalytics.stats.bounceRate": "Bounce Rate",
      "emailAnalytics.lowEngagementAlert": "Low Engagement Detected",
      "emailAnalytics.patientsLowEngagement": "patients have low email engagement. Consider phone calls or alternative communication.",
      "emailAnalytics.tabs.overview": "Overview",
      "emailAnalytics.tabs.engagement": "Patient Engagement",
      "emailAnalytics.funnel.title": "Email Funnel",
      "emailAnalytics.funnel.description": "Track emails from send to click",
      "emailAnalytics.funnel.sent": "Sent",
      "emailAnalytics.funnel.delivered": "Delivered",
      "emailAnalytics.funnel.opened": "Opened",
      "emailAnalytics.funnel.clicked": "Clicked",
      "emailAnalytics.trends.title": "Daily Trends",
      "emailAnalytics.trends.date": "Date",
      "emailAnalytics.trends.sent": "Sent",
      "emailAnalytics.trends.delivered": "Delivered",
      "emailAnalytics.trends.opened": "Opened",
      "emailAnalytics.trends.clicked": "Clicked",
      "emailAnalytics.trends.bounced": "Bounced",
      "emailAnalytics.trends.openRate": "Open %",
      "emailAnalytics.trends.clickRate": "Click %",
      "emailAnalytics.engagement.title": "Patient Email Engagement",
      "emailAnalytics.engagement.description": "Ranked by engagement score (opens + clicks)",
      "emailAnalytics.engagement.sent": "Sent",
      "emailAnalytics.engagement.opened": "Opened",
      "emailAnalytics.engagement.clicked": "Clicked",
      "emailAnalytics.engagement.openRate": "Open Rate",
      "emailAnalytics.engagement.lastOpened": "Last opened",
      
      // Staff Tools
      "staffTools.title": "Staff Tools",
      "staffTools.subtitle": "Time Off Requests, SOP Library, and Global Search",
      "staffTools.tabs.timeOff": "Time Off",
      "staffTools.tabs.sops": "SOPs",
      "staffTools.tabs.search": "Search",
      "staffTools.timeOff.myRequests": "My Requests",
      "staffTools.timeOff.requestTimeOff": "Request Time Off",
      "staffTools.timeOff.noRequests": "No time off requests",
      "staffTools.sop.title": "SOP & Reference Documents",
      "staffTools.sop.description": "Search standard operating procedures and reference documents",
      "staffTools.search.title": "Global Search",
      "staffTools.search.description": "Search across patients, staff, documents, and more",
      
      // Visit Frequency
      "visitFrequency.title": "Visit Frequency Tracker",
      "visitFrequency.subtitle": "Monitor visit compliance and track missed visits across all disciplines",
      "visitFrequency.tabs.dashboard": "Dashboard",
      "visitFrequency.tabs.missed": "Missed Visits",
      "visitFrequency.tabs.reminders": "Reminders",
      "visitFrequency.stats.compliant": "Compliant Patients",
      "visitFrequency.stats.atRisk": "At Risk",
      "visitFrequency.stats.critical": "Critical",
      "visitFrequency.stats.total": "Total Patients",
      
      // Patient Portal
      "patientPortal.title": "Patient Portal",
      "patientPortal.subtitle": "Connect with your care team, share feedback, and manage your care",
      "patientPortal.tabs.messages": "Messages",
      "patientPortal.tabs.visits": "Visits",
      "patientPortal.tabs.telehealth": "Telehealth",
      "patientPortal.tabs.feedback": "Feedback",
      "patientPortal.recognizeTeam": "Recognize Care Team",
      
      // Navigation
      "nav.home": "Home",
      "nav.dashboard": "Dashboard",
      "nav.chat": "Get AI Help",
      "nav.symptoms": "Manage Symptoms",
      "nav.journal": "Log Care Notes",
      "nav.medications": "Medications",
      "nav.calendar": "Calendar",
      "nav.resources": "Find Support",
      "nav.education": "Education",
      "nav.videos": "Watch & Learn",
      "nav.contact": "Contact",
      "nav.messages": "Message Team",
      "nav.memories": "My Story",
      "nav.compliance": "Security",
      "nav.signIn": "Sign In",
      "nav.signOut": "Sign Out",
      "nav.demo": "Take the Tour",
      "nav.programs": "Complementary Services",
      
      // Chat/AI
      "chat.topics": "Chat Topics",
      "chat.goalsOfCare": "Goals of Care",
      "chat.goalsDescription": "Discuss end-of-life wishes and advance care planning",
      "chat.goalsWelcome": "Hello, I'm here to help you navigate goals of care and advanced care planning conversations. These discussions can be difficult, but they're so important. What would you like to talk about today?",
      "chat.hospiceInfo": "Hospice Info",
      "chat.hospiceDescription": "Learn about hospice care and its benefits",
      "chat.hospiceWelcome": "Welcome! I'm here to help you understand hospice care - what it is, how it helps, and what to expect. What questions do you have about hospice?",
      "chat.caregiverSupport": "Caregiver Support",
      "chat.caregiverDescription": "Get support and resources for caregivers",
      "chat.caregiverWelcome": "Hello, caregiver. I want you to know that what you're doing matters deeply. I'm here to support you with resources, tips, and a compassionate ear. How can I help you today?",
      "chat.symptomHelp": "Symptom Help",
      "chat.symptomDescription": "Get guidance on managing symptoms and when to call",
      "chat.symptomWelcome": "I'm here to help you understand and manage hospice-related symptoms. I'll provide guidance on comfort measures and clearly tell you when to call your hospice team. What symptom or concern would you like to discuss?",
      "chat.signInSave": "Sign in to save conversations",
      "chat.historyPersist": "Your chat history will persist across sessions",
      "chat.previousConversations": "Previous Conversations",
      "chat.clear": "Clear",
      "chat.new": "New",
      "chat.thinking": "Thinking...",
      "chat.placeholder": "Type your message or use voice...",
      "chat.disclaimer": "This is for informational purposes only. Always consult your hospice team for medical advice.",
      "chat.recordingStarted": "Recording started - speak now",
      "chat.microphoneDenied": "Microphone access denied. Please enable microphone permissions.",
      "chat.transcriptionComplete": "Transcription complete",
      "chat.noSpeechDetected": "No speech detected. Please try again.",
      "chat.transcriptionFailed": "Failed to transcribe audio. Please try again.",
      "chat.recording": "Recording",
      "chat.transcribing": "Transcribing...",
      "chat.speaking": "Speaking...",
      "chat.ttsDisabled": "Voice responses disabled",
      "chat.ttsEnabled": "Voice responses enabled",
      "chat.ttsOn": "Voice On",
      "chat.ttsOff": "Voice Off",
      "chat.speakNow": "Speak now...",
      "chat.voiceCommand.cleared": "Chat cleared by voice command",
      "chat.voiceCommand.newStarted": "New conversation started by voice command",
      "chat.voiceCommand.stopped": "Voice stopped by command",
      "chat.voiceCommand.hint": "Try saying: \"clear chat\", \"new conversation\", or \"stop speaking\"",
      
      // Profile
      "profile.title": "My Profile",
      "profile.subtitle": "Manage your account settings and preferences",
      "profile.verified": "Verified",
      "profile.pendingApproval": "Pending Approval",
      "profile.profile": "Profile",
      "profile.security": "Security",
      "profile.notifications": "Notifications",
      "profile.profileInfo": "Profile Information",
      "profile.updatePersonal": "Update your personal information",
      "profile.fullName": "Full Name",
      "profile.email": "Email",
      "profile.phone": "Phone",
      "profile.organization": "Organization",
      "profile.orgPlaceholder": "Organization name",
      "profile.specialty": "Specialty",
      "profile.specialtyPlaceholder": "e.g., Internal Medicine",
      "profile.npiNumber": "NPI Number",
      "profile.npiPlaceholder": "10-digit NPI",
      "profile.saveChanges": "Save Changes",
      "profile.changePassword": "Change Password",
      "profile.passwordDescription": "Update your password to keep your account secure",
      "profile.currentPassword": "Current Password",
      "profile.newPassword": "New Password",
      "profile.confirmPassword": "Confirm New Password",
      "profile.updatePassword": "Update Password",
      "profile.notificationPrefs": "Notification Preferences",
      "profile.notificationDescription": "Manage how you receive notifications",
      "profile.emailNotifications": "Email Notifications",
      "profile.emailNotificationsDesc": "Receive updates via email",
      "profile.smsNotifications": "SMS Notifications",
      "profile.smsNotificationsDesc": "Receive text messages for urgent updates",
      "profile.pushNotifications": "Push Notifications",
      "profile.pushNotificationsDesc": "Receive in-app notifications",
      "profile.savePreferences": "Save Preferences",
      
      // Dashboard
      "dashboard.greeting.morning": "Good morning",
      "dashboard.greeting.afternoon": "Good afternoon",
      "dashboard.greeting.evening": "Good evening",
      "dashboard.checkin": "Check In",
      "dashboard.checkedIn": "Checked In",
      "dashboard.streak": "day streak",
      "dashboard.medications": "Medications",
      "dashboard.upcoming": "Upcoming",
      "dashboard.careTeam": "Care Team",
      "dashboard.quickActions": "Quick Actions",
      "dashboard.askAI": "Ask AI",
      "dashboard.insights": "Care Insights",
      "dashboard.generateInsights": "Generate",
      "dashboard.manual": "Manual",
      "dashboard.voice": "Voice",
      "dashboard.welcomeTitle": "Welcome to Your Care Dashboard",
      "dashboard.signInPrompt": "Sign in to access your personalized care tools",
      "dashboard.today": "today",
      "dashboard.thisWeek": "this week",
      "dashboard.assigned": "assigned",
      "dashboard.insightsDescription": "AI-powered observations based on your data",
      "dashboard.noInsightsYet": "No insights yet. Keep tracking your daily check-ins!",
      "dashboard.needMoreData": "We need at least 3 days of data to generate insights.",
      "dashboard.yourTrends": "Your Trends",
      "dashboard.trendsDescription": "Based on your last {{count}} check-ins",
      "dashboard.avg": "avg",
      "dashboard.mostReportedSymptoms": "Most Reported Symptoms",
      "dashboard.scheduledVisits": "Scheduled Visits",
      "dashboard.tomorrow": "Tomorrow",
      "dashboard.urgent": "Urgent",
      "dashboard.moreVisits": "more visits scheduled",
      "dashboard.noVisits": "No upcoming visits scheduled",
      "dashboard.teamWillSchedule": "Your care team will schedule visits as needed",
      "dashboard.needToTalk": "Need to Talk?",
      "dashboard.requestCallbackDesc": "Request a callback from our care team",
      "dashboard.inQueue": "You're in the queue!",
      "dashboard.queuePosition": "Position",
      "dashboard.estWait": "Est. wait",
      "dashboard.soon": "Soon",
      "dashboard.requestCallback": "Request Callback",
      "dashboard.emergency": "Emergency",
      "dashboard.manageContacts": "Manage Contacts",
      "dashboard.viewAll": "View All",
      "dashboard.noUpcomingEvents": "No upcoming events",
      
      // Callback Dialog
      "callback.title": "Request a Callback",
      "callback.description": "Our care team will call you back as soon as possible",
      "callback.whatHelp": "What do you need help with?",
      "callback.describeConcern": "Describe your concern or question...",
      "callback.howUrgent": "How urgent is this?",
      "callback.normal": "Normal - Within a few hours",
      "callback.urgent": "Urgent - Within 30 minutes",
      "callback.emergencyOption": "Emergency - Call me immediately",
      "callback.phoneNumber": "Phone number",
      "callback.useRegistered": "Use my registered number",
      "callback.leaveBlank": "Leave blank to use your registered phone number",
      "callback.bestTime": "Best time to call",
      "callback.asap": "As soon as possible",
      "callback.morning": "Morning (8am - 12pm)",
      "callback.afternoon": "Afternoon (12pm - 5pm)",
      "callback.evening": "Evening (5pm - 8pm)",
      "callback.lifeThreatening": "For life-threatening emergencies:",
      "callback.call911": "Please call 911 immediately",
      
      // Common additions
      "common.getStarted": "Get Started",
      "common.new": "New",
      "common.manage": "Manage",
      "common.optional": "optional",
      
      // Check-in additions
      "checkin.veryPoor": "Very Poor",
      "checkin.excellent": "Excellent",
      "checkin.noPain": "No Pain",
      "checkin.severe": "Severe",
      "checkin.notesPlaceholder": "How are you really feeling today?",
      
      // Check-in
      "checkin.title": "Daily Check-in",
      "checkin.overall": "How are you feeling overall?",
      "checkin.pain": "Pain Level",
      "checkin.sleep": "Sleep Quality",
      "checkin.appetite": "Appetite",
      "checkin.mobility": "Mobility",
      "checkin.mood": "Current Mood",
      "checkin.symptoms": "Any symptoms today?",
      "checkin.notes": "Any notes?",
      "checkin.complete": "Complete Check-in",
      "checkin.voiceTitle": "Voice Check-in",
      
      // Moods
      "mood.peaceful": "Peaceful",
      "mood.happy": "Happy",
      "mood.okay": "Okay",
      "mood.tired": "Tired",
      "mood.anxious": "Anxious",
      "mood.sad": "Sad",
      
      // Symptoms
      "symptom.pain": "Pain",
      "symptom.nausea": "Nausea",
      "symptom.fatigue": "Fatigue",
      "symptom.breathlessness": "Shortness of Breath",
      "symptom.constipation": "Constipation",
      "symptom.anxiety": "Anxiety",
      "symptom.confusion": "Confusion",
      "symptom.appetiteLoss": "Loss of Appetite",
      "symptom.insomnia": "Insomnia",
      "symptom.weakness": "Weakness",
      "symptom.restlessness": "Restlessness",
      "symptom.skinChanges": "Skin Changes",
      
      // Medications
      "medications.title": "Medications",
      "medications.add": "Add Medication",
      "medications.todaySchedule": "Today's Schedule",
      "medications.allMedications": "All Medications",
      "medications.markTaken": "Mark Taken",
      "medications.skip": "Skip",
      "medications.taken": "Taken",
      "medications.name": "Medication Name",
      "medications.dosage": "Dosage",
      "medications.frequency": "Frequency",
      "medications.times": "Schedule Times",
      "medications.instructions": "Special Instructions",
      "medications.trackSchedule": "Track your medication schedule",
      "medications.noScheduled": "No medications scheduled",
      "medications.signInToManage": "Sign in to manage your medications",
      "medications.addNew": "Add New Medication",
      "medications.namePlaceholder": "e.g., Morphine",
      "medications.dosagePlaceholder": "e.g., 10mg",
      "medications.instructionsPlaceholder": "e.g., Take with food",
      "medications.startDate": "Start Date",
      "medications.of": "of",
      "medications.completed": "completed",
      "medications.scheduled": "Scheduled",
      "medications.activeMedications": "active medications",
      "medications.noAdded": "No medications added yet",
      "medications.addFirst": "Add Your First Medication",
      "medications.onceDaily": "Once Daily",
      "medications.twiceDaily": "Twice Daily",
      "medications.threeTimes": "Three Times Daily",
      "medications.fourTimes": "Four Times Daily",
      "medications.asNeeded": "As Needed",
      "medications.weekly": "Weekly",
      
      // Symptom Guide
      "symptomGuide.title": "Symptom Management Guide",
      "symptomGuide.caregiverTraining": "Caregiver Training",
      "symptomGuide.description": "Comprehensive guidance on managing common hospice symptoms. Learn comfort measures and know exactly when to call your hospice team.",
      "symptomGuide.alertMessage": "When in doubt, always call your hospice team. They are available 24/7 and want to hear from you. Never hesitate to reach out.",
      "symptomGuide.downloadComplete": "Download Complete Guide (PDF)",
      "symptomGuide.loading": "Loading symptom guides...",
      "symptomGuide.symptoms": "Symptoms",
      "symptomGuide.comfortMeasures": "Comfort Measures",
      "symptomGuide.whenToCall": "When to Call the Hospice Team",
      "symptomGuide.callImmediately": "CALL IMMEDIATELY If...",
      "symptomGuide.emergencyNote": "These situations require immediate attention. Call your hospice team or 911 right away.",
      "symptomGuide.needMoreGuidance": "Need More Guidance?",
      "symptomGuide.ctaDescription": "Chat with our AI assistant for personalized guidance on managing symptoms and understanding what to expect.",
      "symptomGuide.askAboutSymptoms": "Ask About Symptoms",
      
      // Messages
      "messages.title": "Messages",
      "messages.subtitle": "Communicate directly with your care team",
      "messages.messageYourTeam": "Message Your Care Team",
      "messages.signInToMessage": "Sign in to send messages directly to your hospice office staff and get responses in real-time.",
      "messages.newMessage": "New Message",
      "messages.newToStaff": "New Message to Staff",
      "messages.subject": "Subject",
      "messages.subjectPlaceholder": "What's this about?",
      "messages.message": "Message",
      "messages.messagePlaceholder": "Type your message...",
      "messages.sendMessage": "Send Message",
      "messages.conversations": "Conversations",
      "messages.noConversations": "No conversations yet",
      "messages.startNew": "Start a new message to your care team",
      "messages.open": "Open",
      "messages.assignedTo": "Assigned to {{name}}",
      "messages.waitingForStaff": "Waiting for staff response",
      "messages.selectConversation": "Select a conversation or start a new one",
      
      // Calendar
      "calendar.title": "Care Calendar",
      "calendar.addEvent": "Add Event",
      "calendar.upcoming": "Upcoming Events",
      "calendar.appointment": "Appointment",
      "calendar.visit": "Home Visit",
      "calendar.reminder": "Reminder",
      "calendar.milestone": "Milestone",
      "calendar.medication": "Medication",
      "calendar.signInToView": "Sign in to view your calendar",
      "calendar.manageEvents": "Manage appointments and events",
      "calendar.addNewEvent": "Add New Event",
      "calendar.eventTitle": "Event Title",
      "calendar.eventTitlePlaceholder": "e.g., Doctor Visit",
      "calendar.eventType": "Event Type",
      "calendar.startTime": "Start Time",
      "calendar.endTime": "End Time",
      "calendar.location": "Location",
      "calendar.locationPlaceholder": "e.g., Goodwin Hospice Office",
      "calendar.description": "Description",
      "calendar.descriptionPlaceholder": "Any additional details...",
      "calendar.reminderLabel": "Reminder",
      "calendar.15min": "15 minutes before",
      "calendar.30min": "30 minutes before",
      "calendar.1hour": "1 hour before",
      "calendar.1day": "1 day before",
      "calendar.next7days": "Next 7 days",
      "calendar.at": "at",
      "calendar.noUpcoming": "No upcoming events",
      
      // Patient Dashboard
      "patientDashboard.welcome": "Welcome",
      "patientDashboard.patient": "Patient",
      "patientDashboard.subtitle": "Your hospice care dashboard",
      "patientDashboard.familyPortal": "Family Portal",
      "patientDashboard.messageTeam": "Message Team",
      "patientDashboard.telehealth": "Telehealth",
      "patientDashboard.viewSchedule": "View Schedule",
      "patientDashboard.notifications": "Notifications",
      "patientDashboard.overview": "Overview",
      "patientDashboard.careTeam": "Care Team",
      "patientDashboard.medications": "Medications",
      "patientDashboard.schedule": "Schedule",
      "patientDashboard.upcomingVisits": "Upcoming Visits",
      "patientDashboard.noUpcomingVisits": "No upcoming visits scheduled",
      "patientDashboard.recentActivity": "Recent Activity",
      "patientDashboard.currentMedications": "Current Medications",
      "patientDashboard.yourMedications": "Your Medications",
      "patientDashboard.medicationsDescription": "Current medication list managed by your care team",
      "patientDashboard.visitSchedule": "Visit Schedule",
      "patientDashboard.visitScheduleDescription": "Upcoming visits from your care team",
      "patientDashboard.noVisitsScheduled": "No visits scheduled",
      
      // Emergency
      "emergency.title": "Emergency Contacts",
      "emergency.hospice": "Goodwin Hospice",
      "emergency.nurseLine": "24/7 Nurse Line",
      "emergency.call911": "Call 911",
      
      // Voice Commands
      "voice.listening": "Listening...",
      "voice.speak": "Speak",
      "voice.stop": "Stop",
      "voice.repeat": "Repeat",
      "voice.notSupported": "Voice recognition not supported",
      "voice.micDenied": "Microphone access denied",
      
      // Auth
      "auth.signIn": "Sign In",
      "auth.signUp": "Sign Up",
      "auth.email": "Email",
      "auth.password": "Password",
      "auth.name": "Name",
      "auth.forgotPassword": "Forgot your password?",
      "auth.welcomeBack": "Welcome Back",
      "auth.register": "Register",
      "auth.reset": "Reset",
      "auth.signInDescription": "Sign in to access your care portal, messages, and health resources",
      "auth.emailPlaceholder": "your@email.com",
      "auth.signInToPortal": "Sign In to Portal",
      "auth.createAccount": "Create Account",
      "auth.fullName": "Full Name",
      "auth.iAmA": "I am a...",
      "auth.selectRole": "Select your role",
      "auth.newPassword": "New Password",
      "auth.confirmPassword": "Confirm Password",
      "auth.resetPassword": "Reset Password",
      "auth.passwordResetSuccess": "Password Reset Successfully!",
      "auth.signInNow": "Sign In Now",
      "auth.needHelp": "Need help accessing your account?",
      "auth.resetDescription": "Enter your email address and we will help you reset your password.",
      
      // Landing Page
      "landing.hero.badge": "Serving Northern Virginia",
      "landing.hero.subtitle": "Compassionate Hospice Care for You and Your Family",
      "landing.hero.description": "Expert care when it matters most. Our dedicated team provides comfort, dignity, and support — with 24/7 access to nurses, AI-powered guidance, and a family portal to keep everyone connected.",
      "landing.hero.cta.chat": "Talk to Our AI Guide",
      "landing.hero.cta.demo": "Watch Our Story",
      "landing.hero.callUs": "Call us anytime:",
      "landing.hero.alwaysHere": "Always Here",
      "landing.hero.support247": "24/7 Support Available",
      "landing.hero.imageAlt": "Compassionate caregiver support",
      "landing.stats.support247": "24/7 Nurse Hotline",
      "landing.stats.team": "Interdisciplinary Team",
      "landing.stats.rating": "",
      "landing.features.aiSupport.title": "AI-Powered Support",
      "landing.features.aiSupport.description": "Get answers to your hospice questions with our compassionate AI assistant. Disease-specific guidance available.",
      "landing.features.videos.title": "Training Videos",
      "landing.features.videos.description": "Watch expert-led videos on wound care, turning & positioning, medication administration, and more.",
      "landing.features.symptoms.title": "Symptom Management",
      "landing.features.symptoms.description": "Comprehensive guides on managing symptoms with clear 'when to call' alerts. Export as PDF.",
      "landing.features.programs.title": "Complementary Programs",
      "landing.features.programs.description": "Massage therapy, end-of-life doula services, and aftercare support - beyond traditional hospice care.",
      "landing.features.journal.title": "Caregiver Journal",
      "landing.features.journal.description": "Track daily observations, mood, symptoms, and medications. Share insights with your care team.",
      "landing.features.resources.title": "Community Resources",
      "landing.features.resources.description": "Find local support services in Northern Virginia - transportation, meals, counseling, and more with click-to-call.",
      "landing.stats.support": "AI Support",
      "landing.stats.resources": "Local Resources",
      "landing.stats.compassionate": "Compassionate",
      "landing.sections.features": "How We Help",
      "landing.sections.featuresSubtitle": "Comprehensive Support for Your Journey",
      "landing.sections.learn": "Learn More",
      
      // Journal Page
      "journal.title": "Caregiver Journal",
      "journal.subtitle": "Track daily observations, mood, symptoms, and medications to share with your hospice team.",
      "journal.personalTracking": "Personal Tracking",
      "journal.signInRequired": "Sign in to access your personal caregiver journal and track daily observations, mood, and symptoms.",
      "journal.selectDate": "Select Date",
      "journal.dailyEntry": "Daily Entry",
      "journal.history": "History",
      "journal.weeklySummary": "Weekly Summary",
      "journal.patientMood": "Patient's Mood Today",
      "journal.dailyObservations": "Daily Observations",
      "journal.observationsPlaceholder": "How was your loved one today? Any changes you noticed? Tap the mic to speak.",
      "journal.symptomsObserved": "Symptoms Observed",
      "journal.rateSeverity": "Rate severity (1-5):",
      "journal.medicationLog": "Medication Log",
      "journal.medication": "Medication",
      "journal.dose": "Dose",
      "journal.time": "Time",
      "journal.additionalNotes": "Additional Notes",
      "journal.notesPlaceholder": "Any other notes for your hospice team... Tap the mic to speak.",
      "journal.saveEntry": "Save Entry for",
      "journal.journalHistory": "Journal History",
      "journal.historyDescription": "View and manage your past entries",
      "journal.noEntries": "No journal entries yet",
      "journal.moodTrend": "Mood Trend",
      "journal.averageMood": "Average mood (1-5)",
      "journal.symptomFrequency": "Symptom Frequency",
      "journal.noData": "No data available",
      "journal.noSymptomsLogged": "No symptoms logged this week",
      "journal.mood.veryLow": "Very Low",
      "journal.mood.low": "Low",
      "journal.mood.neutral": "Neutral",
      "journal.mood.good": "Good",
      "journal.mood.veryGood": "Very Good",
      
      // Session
      "session.expiring": "Session Expiring Soon",
      "session.extend": "Extend Session",
      "session.remaining": "Time remaining",
      
      // Compliance
      "compliance.title": "HIPAA Compliance",
      "compliance.2fa": "Two-Factor Authentication",
      "compliance.enabled": "Enabled",
      "compliance.disabled": "Disabled",
      "compliance.sessionTimeout": "Session Timeout",
      "compliance.auditLogging": "Audit Logging",
      "compliance.encryption": "Encrypted Data",
      
      // Family Portal
      "familyPortal.title": "Family Portal",
      "familyPortal.subtitle": "Stay connected with your loved one's care team",
      "familyPortal.newNotifications": "new notifications",
      "familyPortal.enhancedCare": "Enhanced Comfort Care",
      "familyPortal.enhancedCareDesc": "Our care team is providing enhanced comfort-focused care. We are here to support both your loved one and your family during this time.",
      "familyPortal.nurseLineLabel": "24/7 Nurse Line",
      "familyPortal.messageTeam": "Message Team",
      "familyPortal.sendMessage": "Send Message to Care Team",
      "familyPortal.messageDesc": "Your message will be sent to the entire care team",
      "familyPortal.yourName": "Your Name",
      "familyPortal.yourNamePlaceholder": "Your name",
      "familyPortal.relationship": "Relationship",
      "familyPortal.relationshipPlaceholder": "e.g., Daughter, Son, Spouse",
      "familyPortal.markUrgent": "Mark as urgent",
      "familyPortal.callNurse": "Call Nurse",
      "familyPortal.requestChaplain": "Request Chaplain",
      "familyPortal.scheduleVideoCall": "Schedule Video Call",
      "familyPortal.overview": "Overview",
      "familyPortal.updates": "Updates",
      "familyPortal.resources": "Resources",
      "familyPortal.careTimeline": "Care Timeline",
      "familyPortal.yourCareTeam": "Your Care Team",
      "familyPortal.primaryNurse": "Primary Nurse",
      "familyPortal.attendingPhysician": "Attending Physician",
      "familyPortal.socialWorker": "Social Worker",
      "familyPortal.supportServices": "Support Services",
      "familyPortal.contact": "Contact",
      "familyPortal.message": "Message",
      "familyPortal.request": "Request",
      "familyPortal.upcomingVisits": "Upcoming Visits",
      "familyPortal.scheduled": "Scheduled",
      "familyPortal.visitScheduleAppear": "Visit schedule will appear here",
      "familyPortal.currentFocusAreas": "Current Focus Areas",
      "familyPortal.recentNotifications": "Recent Notifications",
      "familyPortal.noNotifications": "No notifications yet",
      "familyPortal.careUpdates": "Care Updates",
      "familyPortal.recentUpdates": "Recent updates from your care team",
      "familyPortal.noUpdates": "No updates yet",
      "familyPortal.from": "From",
      "familyPortal.download": "Download",
      "familyPortal.careTimelineActivities": "Recent care activities",
      "familyPortal.timelineAppear": "Care timeline will appear here",
      "familyPortal.emergencyLine": "24/7 Emergency Line",
      "familyPortal.callNow": "Call Now",
      
      // Resources Page
      "resources.location": "Northern Virginia",
      "resources.title": "Community",
      "resources.titleHighlight": "Resources",
      "resources.description": "Local support services for hospice families in the Northern Virginia area. Find transportation, meals, counseling, and more.",
      "resources.searchPlaceholder": "Search resources...",
      "resources.allResources": "All Resources",
      "resources.noResults": "No resources found matching your criteria.",
      "resources.needHelp": "Need Help Finding Resources?",
      "resources.helpDescription": "Our AI assistant can help you find the right community resources based on your specific needs. Just describe what you're looking for.",
      "resources.chatWithAI": "Chat with AI Assistant",
      
      // Staff Roster
      "staffRoster.title": "Staff Roster",
      "staffRoster.subtitle": "All staff members signed into the system",
      "staffRoster.loading": "Loading Staff Roster...",
      "staffRoster.totalStaff": "Total Staff",
      "staffRoster.searchPlaceholder": "Search by name, email, or discipline...",
      "staffRoster.allRoles": "All Roles",
      "staffRoster.allStatus": "All Status",
      "staffRoster.clearFilters": "Clear Filters",
      "staffRoster.staffMembers": "Staff Members",
      "staffRoster.noResults": "No staff members found matching your filters",
      "staffRoster.lastLogin": "Last login",
      "staffRoster.completed": "completed",
      "staffRoster.pending": "pending",
      "staffRoster.sendEmail": "Send Email",
      "staffRoster.viewTraining": "View Training",
      "staffRoster.viewProfile": "View profile for",
      "staffRoster.roles.admin": "Administrator",
      "staffRoster.roles.nurse": "Nurse",
      "staffRoster.roles.rn": "RN",
      "staffRoster.roles.lpn": "LPN",
      "staffRoster.roles.cna": "CNA",
      "staffRoster.roles.socialWorker": "Social Worker",
      "staffRoster.roles.chaplain": "Chaplain",
      "staffRoster.roles.physician": "Physician",
      "staffRoster.roles.staff": "Staff",
      "staffRoster.roles.volunteer": "Volunteer",
      "staffRoster.status.active": "Active",
      "staffRoster.status.inactive": "Inactive",
      "staffRoster.status.onLeave": "On Leave",
      
      // Family Portal Enhanced
      "familyPortalEnhanced.title": "Family Care Portal",
      "familyPortalEnhanced.subtitle": "Stay connected with your loved one's care journey",
      "familyPortalEnhanced.linkedPatients": "Linked Patients",
      "familyPortalEnhanced.visitsThisWeek": "Visits This Week",
      "familyPortalEnhanced.upcomingVisits": "Upcoming Visits",
      "familyPortalEnhanced.unreadMessages": "Unread Messages",
      "familyPortalEnhanced.selectLovedOne": "Select Loved One",
      "familyPortalEnhanced.careStatus": "Care Status",
      "familyPortalEnhanced.aiCareTips": "AI Care Tips",
      "familyPortalEnhanced.updates": "Updates",
      "familyPortalEnhanced.careTeam": "Care Team",
      "familyPortalEnhanced.messages": "Messages",
      "familyPortalEnhanced.comfortLevel": "Comfort Level",
      "familyPortalEnhanced.painStatus": "Pain Status",
      "familyPortalEnhanced.alertness": "Alertness",
      "familyPortalEnhanced.appetite": "Appetite",
      "familyPortalEnhanced.mobility": "Mobility",
      "familyPortalEnhanced.recentVisits": "Recent Visits",
      "familyPortalEnhanced.currentMedications": "Current Medications",
      "familyPortalEnhanced.sendMessage": "Send Message",
      "familyPortalEnhanced.sendToCareTeam": "Send to Care Team",
      "familyPortalEnhanced.typeMessage": "Type your message to the care team...",
      "familyPortalEnhanced.hotline": "24/7 Hospice Hotline",
      "familyPortalEnhanced.urgentCall": "For urgent concerns, call:",
      "familyPortalEnhanced.refreshRecommendations": "Refresh Recommendations",
      "familyPortalEnhanced.noRecommendations": "No AI recommendations available yet",
      "familyPortalEnhanced.linkPatient": "Link a patient to receive personalized care tips",
      "familyPortalEnhanced.noCareStatus": "No care status information available",
      "familyPortalEnhanced.contactCareTeam": "Please contact the care team to link your account",
      "familyPortalEnhanced.questionsToAsk": "Questions to Ask During Visits",
      "familyPortalEnhanced.careTips": "Care Tips",
      "familyPortalEnhanced.communicationTips": "Communication Tips",
      "familyPortalEnhanced.helpfulResources": "Helpful Resources",
      
      // Staff Portal
      "staffPortal.title": "Staff Portal",
      "staffPortal.subtitle": "Manage your caseload and communications",
      "staffPortal.loading": "Loading staff portal...",
      "staffPortal.accessDenied": "Access Denied",
      "staffPortal.accessDeniedDesc": "You need staff credentials to access this portal.",
      "staffPortal.signIn": "Sign In",
      "staffPortal.tabs.caseload": "My Caseload",
      "staffPortal.tabs.messages": "Messages",
      "staffPortal.tabs.alerts": "Clinical Alerts",
      "staffPortal.tabs.appreciations": "Appreciations",
      "staffPortal.tabs.settings": "Settings",
      "staffPortal.tabs.admin": "Admin Tools",
      "staffPortal.caseload.title": "Patient Caseload",
      "staffPortal.caseload.search": "Search patients...",
      "staffPortal.caseload.assignPatient": "Assign Patient",
      "staffPortal.caseload.importCaseload": "Import Caseload",
      "staffPortal.caseload.downloadTemplate": "Download Template",
      "staffPortal.caseload.total": "Total",
      "staffPortal.caseload.primary": "Primary",
      "staffPortal.caseload.backup": "Backup",
      "staffPortal.caseload.noPatients": "No patients in your caseload",
      "staffPortal.caseload.getStarted": "Get started by importing patients or assigning them manually",
      "staffPortal.patient.details": "Patient Details",
      "staffPortal.patient.status": "Status",
      "staffPortal.patient.diagnosis": "Diagnosis",
      "staffPortal.patient.admissionDate": "Admission Date",
      "staffPortal.patient.address": "Address",
      "staffPortal.patient.phone": "Phone",
      "staffPortal.patient.assignedStaff": "Assigned Staff",
      "staffPortal.patient.recentJournal": "Recent Journal Entry",
      "staffPortal.patient.noJournalEntries": "No journal entries yet",
      "staffPortal.patient.viewFullProfile": "View Full Profile",
      "staffPortal.patient.startConversation": "Start Conversation",
      "staffPortal.messages.title": "Team Conversations",
      "staffPortal.messages.newConversation": "New Conversation",
      "staffPortal.messages.noConversations": "No conversations yet",
      "staffPortal.messages.startNew": "Start a new conversation with a patient's family or care team",
      "staffPortal.messages.typeMessage": "Type a message...",
      "staffPortal.messages.send": "Send",
      "staffPortal.alerts.title": "Clinical Alerts",
      "staffPortal.alerts.noAlerts": "No active alerts",
      "staffPortal.alerts.allClear": "All your patients are doing well!",
      "staffPortal.alerts.dismiss": "Dismiss",
      "staffPortal.alerts.viewPatient": "View Patient",
      "staffPortal.appreciations.title": "Staff Appreciations",
      "staffPortal.appreciations.noAppreciations": "No appreciations yet",
      "staffPortal.appreciations.keepUp": "Keep up the great work!",
      "staffPortal.settings.title": "SMS Notifications",
      "staffPortal.settings.enableSms": "Enable SMS Notifications",
      "staffPortal.settings.phoneNumber": "Phone Number",
      "staffPortal.settings.saveSettings": "Save Settings",
      "staffPortal.admin.title": "Admin Tools",
      "staffPortal.admin.quickStart": "Quick Start Wizard",
      "staffPortal.admin.ehrFeatures": "EHR Features",
      "staffPortal.admin.createPatient": "Create Patient",
      "staffPortal.import.title": "Import Caseload",
      "staffPortal.import.selectFile": "Select CSV file",
      "staffPortal.import.import": "Import",
      "staffPortal.import.success": "Import successful",
      "staffPortal.import.failed": "Import failed",
      "staffPortal.assign.title": "Assign Patient",
      "staffPortal.assign.selectStaff": "Select Staff Member",
      "staffPortal.assign.selectPatient": "Select Patient",
      "staffPortal.assign.assignmentType": "Assignment Type",
      "staffPortal.assign.notes": "Notes (optional)",
      "staffPortal.assign.assign": "Assign",
      "staffPortal.orderSupplies": "Order Supplies",
      "staffPortal.admin.addToEHR": "Add to EHR",
      "staffPortal.stats.myPatients": "My Patients",
      "staffPortal.stats.viewCaseload": "View caseload",
      "staffPortal.stats.activeThisWeek": "Active This Week",
      "staffPortal.stats.viewScheduled": "View scheduled",
      "staffPortal.stats.conversations": "Conversations",
      "staffPortal.stats.viewAllMessages": "View all messages",
      "staffPortal.stats.unreadMessages": "Unread Messages",
      "staffPortal.stats.viewUnread": "View unread",
      "staffPortal.tabs.moodTrends": "Mood Trends",
      "staffPortal.tabs.system": "System",
      "staffPortal.caseload.assignedPatients": "Assigned Patients",
      "staffPortal.caseload.description": "View and manage your assigned patient caseload",
      "staffPortal.caseload.addPatient": "Add Patient",
      "staffPortal.caseload.noPatientsAssigned": "No patients assigned",
      "staffPortal.caseload.importOrAssign": "Import a caseload or assign patients manually.",
      "staffPortal.caseload.contactAdmin": "Contact your administrator to get patients assigned.",
      "staffPortal.caseload.noCheckins": "No check-ins",
      "staffPortal.caseload.lastActivity": "Last activity",
      "staffPortal.messages.conversations": "Conversations",
      "staffPortal.academy.title": "Hospice Academy",
      "staffPortal.academy.description": "Access training courses and CEU tracking",
      "staffPortal.academy.open": "Open Academy",
      "staffPortal.clinicalAlerts.title": "Clinical Documentation Alerts",
      "staffPortal.clinicalAlerts.description": "Important clinical information that should be documented in the medical record",
      "staffPortal.clinicalAlerts.viewPatient": "View Patient",
      "staffPortal.clinicalAlerts.dismiss": "Dismiss",
      "staffPortal.clinicalAlerts.allClear": "All Clear!",
      "staffPortal.clinicalAlerts.noAlerts": "No clinical documentation alerts at this time",
      "staffPortal.documentation.bestPractices": "Documentation Best Practices",
      "staffPortal.documentation.patientCheckins": "Patient Check-ins",
      "staffPortal.documentation.painLevels": "Document pain levels above 5/10",
      "staffPortal.documentation.moodChanges": "Note mood changes and interventions",
      "staffPortal.documentation.symptomProgression": "Record symptom progression",
      "staffPortal.documentation.medicationCompliance": "Medication Compliance",
      "staffPortal.documentation.missedMedications": "Document missed medications",
      "staffPortal.documentation.nonCompliance": "Note reasons for non-compliance",
      "staffPortal.documentation.prnEffectiveness": "Record PRN medication effectiveness",
      "staffPortal.documentation.vitalSigns": "Vital Signs",
      "staffPortal.documentation.vitalChanges": "Record significant vital changes",
      "staffPortal.documentation.weightFluctuations": "Document weight fluctuations",
      "staffPortal.documentation.respiratoryStatus": "Note respiratory status changes",
      "staffPortal.documentation.familyCommunication": "Family Communication",
      "staffPortal.documentation.familyConcerns": "Document family concerns",
      "staffPortal.documentation.educationProvided": "Record education provided",
      "staffPortal.documentation.carePlanDiscussions": "Note care plan discussions",
      "staffPortal.moodTrends.attentionNeeded": "Attention Needed",
      "staffPortal.moodTrends.distribution": "Mood Distribution (Last 30 Days)",
      "staffPortal.moodTrends.checkinsFrom": "{{count}} check-ins from {{patients}} patients",
      "staffPortal.moodTrends.noData": "No mood data available",
      "staffPortal.moodTrends.latestPatientMoods": "Latest Patient Moods",
      "staffPortal.moodTrends.pain": "Pain",
      "staffPortal.moodTrends.noRecentData": "No recent mood data",
      "staffPortal.appreciation.yourStats": "Your Appreciation Stats",
      "staffPortal.appreciation.fromCount": "from {{count}} appreciation(s)",
      "staffPortal.appreciation.byCategory": "By Category",
      "staffPortal.appreciation.recentAppreciations": "Recent Appreciations",
      "staffPortal.appreciation.from": "From",
      "staffPortal.appreciation.noAppreciations": "No appreciations yet",
      "staffPortal.appreciation.keepUp": "Keep up the great work!",
      "staffPortal.smsSettings.title": "SMS Notification Settings",
      "staffPortal.smsSettings.description": "Receive text message alerts when patients send you messages",
      "staffPortal.smsSettings.phoneNumber": "Phone Number",
      "staffPortal.smsSettings.usPhoneNumber": "US phone number for receiving SMS notifications",
      "staffPortal.smsSettings.textMessageNotifications": "Text Message Notifications",
      "staffPortal.smsSettings.receiveAlerts": "Receive SMS alerts when patients send you messages",
      "staffPortal.smsSettings.howItWorks": "How it works",
      "staffPortal.smsSettings.howItWorksDescription": "When a patient sends you a message through the portal, you'll receive a text message with a preview of their message. This helps you respond quickly to patient needs.",
      "staffPortal.smsSettings.saveSettings": "Save Settings",
      "staffPortal.patientDetails.title": "Patient Details",
      "staffPortal.patientDetails.description": "View comprehensive patient information and activity",
      "staffPortal.patientDetails.email": "Email",
      "staffPortal.patientDetails.condition": "Condition",
      "staffPortal.patientDetails.notSpecified": "Not specified",
      "staffPortal.patientDetails.phone": "Phone",
      "staffPortal.patientDetails.recentCheckins": "Recent Check-ins",
      "staffPortal.patientDetails.painLevel": "Pain",
      "staffPortal.patientDetails.mood": "Mood",
      "staffPortal.patientDetails.symptoms": "Symptoms",
      "staffPortal.patientDetails.noCheckins": "No check-ins recorded",
      "staffPortal.patientDetails.currentMedications": "Current Medications",
      "staffPortal.patientDetails.noMedications": "No medications listed",
      "staffPortal.patientDetails.recentJournals": "Recent Journals",
      "staffPortal.patientDetails.noJournals": "No journal entries",
      "staffPortal.patientDetails.aiCareInsights": "AI Care Insights",
      "staffPortal.import.title": "Import Caseload",
      "staffPortal.import.description": "Upload a CSV or Excel file with staff-patient assignments",
      "staffPortal.import.selectFile": "Select File",
      "staffPortal.import.downloadTemplate": "Download Template",
      "staffPortal.import.results": "Import Results",
      "staffPortal.import.successful": "successful",
      "staffPortal.import.failed": "failed",
      "staffPortal.import.assignmentsCreated": "Assignments Created",
      "staffPortal.import.errors": "Errors",
      "staffPortal.import.row": "Row",
      "staffPortal.import.import": "Import",
      "staffPortal.assign.title": "Assign Patient to Staff",
      "staffPortal.assign.description": "Create a new staff-patient assignment",
      "staffPortal.assign.staffMember": "Staff Member",
      "staffPortal.assign.selectStaff": "Select staff...",
      "staffPortal.assign.patients": "patients",
      "staffPortal.assign.patient": "Patient",
      "staffPortal.assign.selectPatient": "Select patient...",
      "staffPortal.assign.assignmentType": "Assignment Type",
      "staffPortal.assign.primary": "Primary",
      "staffPortal.assign.secondary": "Secondary",
      "staffPortal.assign.backup": "Backup",
      "staffPortal.assign.notes": "Notes (optional)",
      "staffPortal.assign.notesPlaceholder": "Add any notes about this assignment...",
      "staffPortal.assign.assign": "Assign",
      "staffPortal.newConversation.title": "New Conversation",
      "staffPortal.newConversation.description": "Start a conversation with an assigned patient",
      "staffPortal.newConversation.patient": "Patient",
      "staffPortal.newConversation.selectPatient": "Select patient...",
      "staffPortal.newConversation.subject": "Subject",
      "staffPortal.newConversation.subjectPlaceholder": "Conversation subject...",
      "staffPortal.newConversation.message": "Message",
      "staffPortal.newConversation.messagePlaceholder": "Your message...",
      "staffPortal.newConversation.start": "Start Conversation",
      "staffPortal.addPatient.title": "Add New Patient",
      "staffPortal.addPatient.description": "Manually create a new patient record. A temporary password will be generated.",
      "staffPortal.addPatient.success": "Patient Created Successfully!",
      "staffPortal.addPatient.name": "Name",
      "staffPortal.addPatient.emailLabel": "Email",
      "staffPortal.addPatient.tempPassword": "Temporary Password",
      "staffPortal.addPatient.smsNotification": "SMS Notification",
      "staffPortal.addPatient.sentToPatient": "✓ Sent to patient",
      "staffPortal.addPatient.notSent": "Not sent",
      "staffPortal.addPatient.shareCredentials": "Please share these login credentials with the patient. They will be asked to change their password on first login.",
      "staffPortal.addPatient.credentialsSent": "Credentials have been sent to the patient's phone via SMS.",
      "staffPortal.addPatient.done": "Done",
      "staffPortal.addPatient.fullName": "Full Name",
      "staffPortal.addPatient.fullNamePlaceholder": "John Doe",
      "staffPortal.addPatient.email": "Email Address",
      "staffPortal.addPatient.emailPlaceholder": "patient@example.com",
      "staffPortal.addPatient.phone": "Phone",
      "staffPortal.addPatient.dob": "Date of Birth",
      "staffPortal.addPatient.address": "Address",
      "staffPortal.addPatient.addressPlaceholder": "123 Main St, City, State",
      "staffPortal.addPatient.diagnosis": "Primary Diagnosis",
      "staffPortal.addPatient.diagnosisPlaceholder": "Enter primary diagnosis...",
      "staffPortal.addPatient.emergencyContactName": "Emergency Contact Name",
      "staffPortal.addPatient.emergencyNamePlaceholder": "Jane Doe",
      "staffPortal.addPatient.emergencyContactPhone": "Emergency Contact Phone",
      "staffPortal.addPatient.notes": "Notes",
      "staffPortal.addPatient.notesPlaceholder": "Additional notes about the patient...",
      "staffPortal.addPatient.createPatient": "Create Patient",
      
      // Moods (shared across pages)
      "moods.happy": "Happy",
      "moods.calm": "Calm",
      "moods.grateful": "Grateful",
      "moods.hopeful": "Hopeful",
      "moods.content": "Content",
      "moods.anxious": "Anxious",
      "moods.sad": "Sad",
      "moods.frustrated": "Frustrated",
      "moods.tired": "Tired",
      "moods.scared": "Scared",
      "moods.angry": "Angry",
      
      // Hospice Academy Page
      "academy.accessRestricted": "Access restricted to staff and administrators.",
      "academy.tabs.courses": "Courses",
      "academy.tabs.certificates": "My Certificates",
      "academy.tabs.achievements": "Achievements",
      "academy.tabs.assignments": "Assignments",
      "academy.tabs.admin": "Admin",
      "academy.elnec.title": "ELNEC Core Curriculum",
      "academy.elnec.description": "End-of-Life Nursing Education Consortium - 8.0 CEU Credits",
      "academy.modules": "Modules",
      "academy.completed": "✓ Completed",
      "academy.startElnec": "Start ELNEC",
      "academy.searchCourses": "Search courses...",
      "academy.required": "Required",
      "academy.review": "Review",
      "academy.takeQuiz": "Take Quiz",
      "academy.watchVideo": "Watch Video",
      "academy.certificates.title": "My Certificates & CEU Transcript",
      "academy.certificates.subtitle": "Download or print your completion certificates",
      "academy.ceuCreditsEarned": "CEU Credits Earned",
      "academy.ofTotalAvailable": "of {{total}} total available",
      "academy.elnecCertificate": "ELNEC Core Completion Certificate",
      "academy.elnecCertDesc": "End-of-Life Nursing Education Consortium • 8.0 CEU Credits",
      "academy.downloadCertificate": "Download Certificate",
      "academy.individualCertificates": "Individual Course Certificates",
      "academy.completeToCertify": "Complete courses to earn certificates",
      "academy.browseCourses": "Browse Courses",
      "academy.completedOn": "Completed",
      "academy.achievements.title": "Achievements & Badges",
      "academy.achievements.subtitle": "{{earned}} of {{total}} badges earned",
      "academy.earned": "Earned",
      "academy.assignments.title": "Training Assignments",
      "academy.assignments.subtitle": "Monitor and manage staff training progress",
      "academy.assignments.filter.all": "All Assignments",
      "academy.assignments.filter.active": "Active (Outstanding)",
      "academy.assignments.filter.due": "Due Soon",
      "academy.assignments.filter.overdue": "Overdue",
      "academy.assignments.filter.completed": "Completed",
      "academy.processing": "Processing...",
      "academy.sendReminders": "Send Reminders",
      "academy.assignTraining": "Assign Training",
      "academy.stats.active": "Active/Outstanding",
      "academy.stats.dueSoon": "Due Soon",
      "academy.stats.overdue": "Overdue",
      "academy.stats.completed": "Completed",
      "academy.assignments.label": "Assignments",
      "academy.assignments.none": "No {{filter}} assignments found",
      "academy.unknownStaff": "Unknown Staff",
      "academy.dueDate": "Due Date",
      "academy.status.overdue": "Overdue",
      "academy.status.dueSoon": "Due Soon",
      "academy.analytics.title": "Staff Training Analytics",
      "academy.analytics.subtitle": "Monitor completion rates and track team progress",
      "academy.analytics.totalCourses": "Total Courses",
      "academy.analytics.totalCeus": "Total CEUs Available",
      "academy.analytics.requiredCourses": "Required Courses",
      "academy.analytics.trainingTracks": "Training Tracks",
      "academy.analytics.tracksOverview": "Training Tracks Overview",
      "academy.analytics.tracksDesc": "Courses and CEU credits by discipline",
      "academy.courses": "courses",
      "academy.certificate.title": "Certificate of Completion",
      "academy.certificate.subtitle": "Print or download your certificate",
      "academy.printCertificate": "Print Certificate",
      
      // Programs Page
      "programs.hero.badge": "Beyond Traditional Care",
      "programs.hero.title": "Complementary",
      "programs.hero.titleHighlight": "Programs",
      "programs.hero.description": "At Goodwin Hospice, our care is rooted in the belief that every person has the right to die pain-free and with dignity. We offer therapies and services that go above and beyond traditional hospice care.",
      "programs.benefits": "Benefits",
      "programs.servicesWeHelp": "Services We Help With",
      "programs.call": "Call",
      "programs.visitWebsite": "Visit Website",
      "programs.contactToLearnMore": "Contact us to learn more about this program",
      "programs.cta.title": "Have Questions About Our Programs?",
      "programs.cta.description": "Our team is here to help you understand how these complementary programs can support you and your loved ones during this journey.",
      "programs.cta.callButton": "Call 703.578.7108",
      "programs.cta.contactOnline": "Contact Us Online",
      "programs.massage.title": "End-of-Life Massage Therapy",
      "programs.massage.subtitle": "Comfort Through Healing Touch",
      "programs.massage.description": "Physical touch can have a profound impact on the overall wellbeing of someone who is dealing with serious medical issues. Research indicates that massage therapy can improve the quality of life for those receiving hospice care.",
      "programs.massage.quote": "One comment we hear most often from patients is how much they enjoyed the company of our therapists. They can benefit from the social interaction as well as the therapeutic massage.",
      "programs.massage.quoteAuthor": "Laura Bryant-Earner, Service Director at Healwell",
      "programs.massage.benefits.1": "Improves quality of life for hospice patients",
      "programs.massage.benefits.2": "Reduces anxiety and stress levels",
      "programs.massage.benefits.3": "Alleviates pain, loss of sleep, nausea, and fatigue",
      "programs.massage.benefits.4": "Helps muscles relax and release tension",
      "programs.massage.benefits.5": "Provides comforting social interaction",
      "programs.massage.benefits.6": "Promotes feelings of peace and dignity",
      "programs.massage.details": "Anyone receiving care from Goodwin Hospice can request massage therapy, and they will receive up to three sessions, which can be shared between patients and family members.",
      "programs.massage.highlight": "Offered at no additional cost to patients",
      "programs.doula.title": "End-of-Life Doula Services",
      "programs.doula.subtitle": "Compassionate Guidance Through Transition",
      "programs.doula.description": "An end-of-life doula guides a person who is transitioning to death and their loved ones through the dying process. Doulas help families cope with death by recognizing it as a natural and important part of life.",
      "programs.doula.quote": "An end-of-life doula guides a person who is transitioning to death and their loved ones through the dying process.",
      "programs.doula.quoteAuthor": "The International End-of-Life Doula Association (INELDA)",
      "programs.doula.benefits.1": "Emotional, spiritual, and physical support at an intensely personal time",
      "programs.doula.benefits.2": "Assists with planning how last days will unfold",
      "programs.doula.benefits.3": "Supports loved ones through the dying process",
      "programs.doula.benefits.4": "Eases the suffering of grief",
      "programs.doula.benefits.5": "Makes transition less chaotic and more focused on compassion",
      "programs.doula.benefits.6": "Sits with dying loved ones or guides through meditation",
      "programs.doula.details": "Doulas are considered part of the interdisciplinary team at Goodwin Hospice. They fill in as needed at the family's request, providing presence, guidance, and support.",
      "programs.doula.highlight": "Provided through generous donor support",
      "programs.aftercare.title": "Full Circle Aftercare",
      "programs.aftercare.subtitle": "We Can Be Your Guide",
      "programs.aftercare.description": "The loss of a loved one is difficult. You may not know what to do next. We provide resources to help guide you through the complexities of wrapping up your loved one's estate.",
      "programs.aftercare.details": "Further assistance from a trained estate specialist is available to help you navigate this difficult time.",
      "programs.aftercare.highlight": "Expert guidance when you need it most",
      "programs.aftercare.services.government": "Government",
      "programs.aftercare.services.socialSecurity": "Social Security",
      "programs.aftercare.services.va": "Veterans Administration",
      "programs.aftercare.services.opm": "OPM",
      "programs.aftercare.services.finances": "Finances",
      "programs.aftercare.services.pension": "Pension Plans",
      "programs.aftercare.services.401k": "401K/IRA",
      "programs.aftercare.services.creditCards": "Credit Cards",
      "programs.aftercare.services.bankAccounts": "Bank Accounts",
      "programs.aftercare.services.insurance": "Insurance",
      "programs.aftercare.services.lifeInsurance": "Life Insurance",
      "programs.aftercare.services.medical": "Medical/Health",
      "programs.aftercare.services.carHome": "Car/Home",
      "programs.aftercare.services.beneficiaries": "Change Beneficiaries",
      "programs.aftercare.services.estate": "Estate",
      "programs.aftercare.services.wills": "Wills/Trusts/Probate",
      "programs.aftercare.services.realEstate": "Real Estate Title",
      "programs.aftercare.services.vehicle": "Vehicle Title",
      "programs.aftercare.services.protection": "Protection",
      "programs.aftercare.services.creditBureau": "Credit Bureau/Fraud Alert",
      "programs.aftercare.services.deceased": "Deceased Registry",
      "programs.aftercare.services.junkMail": "Stop Junk Mail",
      "programs.aftercare.services.subscriptions": "Cancel Subscriptions",
      
      // Reset Password Page
      "resetPassword.title": "Create New Password",
      "resetPassword.description": "Enter your new password below.",
      "resetPassword.completeTitle": "Password Reset Complete",
      "resetPassword.completeDescription": "Your password has been successfully reset.",
      "resetPassword.invalidLink": "Invalid reset link. Please request a new password reset.",
      "resetPassword.passwordsMismatch": "Passwords do not match",
      "resetPassword.passwordTooShort": "Password must be at least 8 characters long",
      "resetPassword.successMessage": "Password reset successfully!",
      "resetPassword.failedMessage": "Failed to reset password. The link may have expired.",
      "resetPassword.canSignIn": "You can now sign in with your new password.",
      "resetPassword.goToSignIn": "Go to Sign In",
      "resetPassword.newPassword": "New Password",
      "resetPassword.enterNewPassword": "Enter new password",
      "resetPassword.minChars": "Minimum 8 characters",
      "resetPassword.confirmPassword": "Confirm Password",
      "resetPassword.confirmNewPassword": "Confirm new password",
      "resetPassword.resetButton": "Reset Password",
      "resetPassword.resetting": "Resetting...",
      "resetPassword.rememberPassword": "Remember your password?",
      
      // Joyful Memories Page
      "memories.title": "Joyful",
      "memories.titleHighlight": "Memories",
      "memories.celebrateLife": "Celebrate Life",
      "memories.description": "Capture and share the beautiful moments, stories, and milestones that make your loved one's life special. Every memory is a treasure worth preserving.",
      "memories.shareTitle": "Share Joyful Memories",
      "memories.signInPrompt": "Sign in to create and share beautiful memories of your loved ones.",
      "memories.createNew": "Create New Memory",
      "memories.dialogTitle": "Create a Joyful Memory",
      "memories.memoryType": "Memory Type",
      "memories.forPatientLabel": "For (Patient Name)",
      "memories.patientPlaceholder": "Who is this memory for?",
      "memories.titleLabel": "Title",
      "memories.titlePlaceholder": "Give this memory a title",
      "memories.contentLabel": "Your Memory",
      "memories.contentPlaceholder": "Share your beautiful memory...",
      "memories.enhanceAI": "Enhance with AI",
      "memories.dateLabel": "When did this happen? (Optional)",
      "memories.shareWithCommunity": "Share with Community",
      "memories.shareDescription": "Allow others to see and appreciate this memory",
      "memories.saveMemory": "Save Memory",
      "memories.myMemories": "My Memories",
      "memories.community": "Community",
      "memories.noMemoriesYet": "No memories yet. Create your first one!",
      "memories.noCommunityMemories": "No shared memories from the community yet.",
      "memories.forPatient": "For {{name}}",
      "memories.shared": "Shared",
      "memories.private": "Private",
      "memories.defaultLabel": "Memory",
      "memories.fillRequired": "Please fill in all required fields",
      "memories.saveSuccess": "Memory saved beautifully!",
      "memories.saveFailed": "Failed to save memory",
      "memories.writeFirst": "Please write something first",
      "memories.enhanceSuccess": "Memory enhanced with AI magic!",
      "memories.enhanceFailed": "Failed to enhance memory",
      "memories.ctaTitle": "Every Memory Matters",
      "memories.ctaDescription": "The stories we share and the moments we remember become the legacy we leave behind. Take a moment to capture something beautiful today.",
      "memories.types.story": "Story",
      "memories.types.storyDesc": "Share a cherished memory or story",
      "memories.types.milestone": "Milestone",
      "memories.types.milestoneDesc": "Celebrate an important moment",
      "memories.types.gratitude": "Gratitude",
      "memories.types.gratitudeDesc": "Express thankfulness",
      "memories.types.photo": "Photo Memory",
      "memories.types.photoDesc": "Describe a treasured photo",
      
      // Telehealth Page
      "telehealth.title": "Telehealth Video Calls",
      "telehealth.subtitle": "HIPAA-compliant video consultations with patients and families",
      "telehealth.connected": "Connected",
      "telehealth.connecting": "Connecting...",
      "telehealth.endCall": "End Call",
      "telehealth.addNotes": "Add notes before ending the call",
      "telehealth.callNotes": "Call Notes (Optional)",
      "telehealth.notesPlaceholder": "Add any notes about this call...",
      "telehealth.cancel": "Cancel",
      "telehealth.addParticipant": "Add Participant",
      "telehealth.inviteDescription": "Invite another person to join this call",
      "telehealth.selectParticipant": "Select Participant",
      "telehealth.chooseParticipant": "Choose a participant...",
      "telehealth.consultation": "Telehealth consultation",
      "telehealth.tips.title": "Video Call Tips",
      "telehealth.tips.lighting": "Ensure good lighting on your face",
      "telehealth.tips.headphones": "Use headphones for better audio",
      "telehealth.tips.testConnection": "Test your connection before the call",
      "telehealth.tips.recording": "Recording is stored securely",
      
      // Call Triage Page
      "callTriage.title": "Call Triage Center",
      "callTriage.subtitle": "Always-on connection for instant patient call handling",
      "callTriage.alwaysConnected": "Call Center Always Connected - You will receive instant notifications for incoming calls",
      "callTriage.enableAlerts": "Enable Alerts",
      "callTriage.notificationsOn": "Notifications On",
      "callTriage.dial": "Dial",
      "callTriage.newCall": "New Call",
      "callTriage.addToEHR": "Add to EHR",
      "callTriage.dialing": "Dialing...",
      "callTriage.ringing": "Ringing...",
      "callTriage.callConnected": "Call Connected",
      "callTriage.callFailed": "Call Failed",
      "callTriage.endCall": "End Call",
      "callTriage.filterDate": "Filter by Date:",
      "callTriage.clear": "Clear",
      "callTriage.inQueue": "In Queue",
      "callTriage.viewQueue": "View queue",
      "callTriage.activeCalls": "Active Calls",
      "callTriage.viewActive": "View active",
      "callTriage.availableAgents": "Available Agents",
      "callTriage.viewAgents": "View agents",
      "callTriage.callsToday": "Calls Today",
      "callTriage.viewHistory": "View history",
      "callTriage.myStatus": "My Status",
      "callTriage.online": "Online",
      "callTriage.away": "Away",
      "callTriage.clickToggle": "Click to toggle",
      "callTriage.activeCall": "Active Call",
      "callTriage.patientName": "Patient Name",
      "callTriage.phone": "Phone",
      "callTriage.callNow": "Call Now",
      "callTriage.reasonForCall": "Reason for Call",
      "callTriage.notes": "Notes",
      "callTriage.transferHistory": "Transfer History",
      "callTriage.completeCall": "Complete Call",
      "callTriage.transfer": "Transfer",
      "callTriage.callQueue": "Call Queue",
      "callTriage.live": "Live",
      "callTriage.noCallsWaiting": "No calls waiting - Queue monitored in real-time",
      "callTriage.callsWaiting": "{{count}} call(s) waiting",
      "callTriage.noCallsInQueue": "No calls in queue",
      "callTriage.newCallsAppear": "New calls will appear here instantly",
      "callTriage.monitoring": "Monitoring for incoming calls...",
      "callTriage.accept": "Accept",
      "callTriage.teamStatus": "Team Status",
      "callTriage.noAgents": "No agents connected",
      "callTriage.quickActions": "Quick Actions",
      "callTriage.addCallToQueue": "Add Call to Queue",
      "callTriage.refreshQueue": "Refresh Queue",
      "callTriage.reconnect": "Reconnect",
      "callTriage.alwaysOnStatus": "Always-On Status",
      "callTriage.webSocket": "WebSocket",
      "callTriage.disconnected": "Disconnected",
      "callTriage.notifications": "Notifications",
      "callTriage.enabled": "Enabled",
      "callTriage.disabled": "Disabled",
      "callTriage.autoReconnect": "Auto-reconnect",
      "callTriage.active": "Active",
      "callTriage.lastPing": "Last ping",
      "callTriage.triageTips": "Triage Tips",
      "callTriage.tip1": "Emergency calls bypass the queue",
      "callTriage.tip2": "Keep this tab open for instant alerts",
      "callTriage.tip3": "Enable browser notifications",
      "callTriage.tip4": "Set status to Away during breaks",
      
      // Translation Dashboard
      "translationDashboard.title": "Translation Progress Dashboard",
      "translationDashboard.subtitle": "Track internationalization coverage across all pages and languages",
      "translationDashboard.coverage": "Coverage",
      "translationDashboard.keysTranslated": "keys translated",
      "translationDashboard.pages": "pages",
      "translationDashboard.totalPages": "Total Pages/Components",
      "translationDashboard.languagesComplete": "Languages 90%+ Complete",
      "translationDashboard.missingKeys": "Missing Keys",
      "translationDashboard.copyMissing": "Copy Missing",
      "translationDashboard.exportSuccess": "Missing keys exported successfully",
      "translationDashboard.copiedToClipboard": "Missing keys copied to clipboard",
      "translationDashboard.copyFailed": "Failed to copy to clipboard",
      
      // Video Library
      "videoLibrary.backToHome": "Back to Home",
      "videoLibrary.title": "Video Library",
      "videoLibrary.description": "Explore our collection of full-length feature demos. Each video provides a complete, seamless experience showcasing Goodwin Cares capabilities.",
      "videoLibrary.fullVideos": "{{count}} Full Videos",
      "videoLibrary.takeInteractiveTour": "Take Interactive Tour",
      "videoLibrary.searchPlaceholder": "Search videos...",
      "videoLibrary.fullLengthTitle": "Full-Length Feature Videos",
      "videoLibrary.fullLengthDescription": "Each video plays seamlessly without interruptions - brand intro, complete feature showcase, and professional outro. Experience the full story of each feature in ~36 seconds.",
      "videoLibrary.searchResults": "Search results for \"{{query}}\"",
      "videoLibrary.allFeatures": "All Features",
      "videoLibrary.videoCount": "{{count}} videos",
      "videoLibrary.noVideosFound": "No videos found",
      "videoLibrary.tryAdjusting": "Try adjusting your search or filter criteria",
      "videoLibrary.cta.title": "Ready to Experience Compassionate Care?",
      "videoLibrary.cta.description": "Join thousands of families and healthcare professionals who trust Goodwin Cares for their hospice care journey.",
      "videoLibrary.cta.getStarted": "Get Started",
      "videoLibrary.cta.takeTour": "Take the Tour",
      "videoLibrary.category.all": "All",
      "videoLibrary.category.overview": "Overview",
      "videoLibrary.category.communication": "Communication",
      "videoLibrary.category.scheduling": "Scheduling",
      "videoLibrary.category.clinical": "Clinical",
      "videoLibrary.category.training": "Training",
      "videoLibrary.category.analytics": "Analytics",
      "videoLibrary.skipIntro": "Skip Intro",
      
      // Admin Dashboard
      "adminDashboard.title": "Admin Dashboard",
      "adminDashboard.subtitle": "Monitor site analytics, chat usage, and contact messages",
      "adminDashboard.refreshData": "Refresh Data",
      "adminDashboard.accessRequired": "Admin Access Required",
      "adminDashboard.noPrivileges": "Your account does not have admin privileges. Please contact an administrator.",
      "adminDashboard.pleaseSignIn": "Please sign in with an admin account to access the dashboard.",
      "adminDashboard.signInAsAdmin": "Sign In as Admin",
      "adminDashboard.status.new": "New",
      "adminDashboard.status.inProgress": "In Progress",
      "adminDashboard.status.resolved": "Resolved",
      "adminDashboard.status.archived": "Archived",
      "adminDashboard.stats.totalVisits": "Total Page Visits",
      "adminDashboard.stats.chatSessions": "Chat Sessions",
      "adminDashboard.stats.totalMessages": "Total Messages",
      "adminDashboard.stats.uniqueUsers": "Unique Users",
      "adminDashboard.stats.contactMessages": "Contact Messages",
      "adminDashboard.tabs.overview": "Overview",
      "adminDashboard.tabs.contacts": "Contact Messages",
      "adminDashboard.tabs.system": "System Features",
      
      // Demo Page - Interactive Tour
      "demo.welcome.title": "Welcome to Goodwin Cares",
      "demo.welcome.subtitle": "Compassionate Care, Powered by Technology",
      "demo.welcome.description": "A comprehensive hospice and caregiver support platform that combines AI-powered intelligence with human compassion to provide the best possible care experience.",
      "demo.welcome.patientCare": "Patient-Centered Care",
      "demo.welcome.hipaa": "HIPAA Compliant",
      "demo.welcome.aiInsights": "AI-Powered Insights",
      "demo.welcome.familyEngagement": "Family Engagement",
      "demo.patientCare.title": "Comprehensive Patient Care",
      "demo.patientCare.subtitle": "Everything families need in one place",
      "demo.patientCare.description": "Patients and caregivers can track symptoms, medications, and communicate directly with their care team through our intuitive dashboard.",
      "demo.patientCare.checkins": "Daily Check-ins",
      "demo.patientCare.checkinsDesc": "Track pain, mood, and symptoms",
      "demo.patientCare.medications": "Medication Reminders",
      "demo.patientCare.medicationsDesc": "Never miss a dose",
      "demo.patientCare.messaging": "Direct Messaging",
      "demo.patientCare.messagingDesc": "Chat with your care team",
      "demo.patientCare.symptoms": "Symptom Tracking",
      "demo.patientCare.symptomsDesc": "Visual health trends",
      "demo.communication.title": "Real-Time Communication",
      "demo.communication.subtitle": "Stay connected with your care team",
      "demo.communication.description": "Secure messaging, video calls, and instant notifications keep everyone informed and connected, reducing anxiety and improving care coordination.",
      "demo.communication.telehealth": "Telehealth Visits",
      "demo.communication.telehealthDesc": "Face-to-face from home",
      "demo.communication.secureMsg": "Secure Messaging",
      "demo.communication.secureMsgDesc": "HIPAA-compliant chat",
      "demo.communication.notifications": "Push Notifications",
      "demo.communication.notificationsDesc": "Real-time alerts",
      "demo.communication.triage": "Call Triage",
      "demo.communication.triageDesc": "24/7 urgent support",
      "demo.ai.title": "AI-Powered Intelligence",
      "demo.ai.subtitle": "Smart features that make a difference",
      "demo.ai.description": "Our AI analyzes patient data to provide actionable insights, predict needs, and help staff deliver proactive, personalized care.",
      "demo.ai.chatAssistant": "AI Chat Assistant",
      "demo.ai.chatAssistantDesc": "Get instant answers",
      "demo.ai.suggestions": "Smart Suggestions",
      "demo.ai.suggestionsDesc": "AI message responses",
      "demo.ai.predictive": "Predictive Alerts",
      "demo.ai.predictiveDesc": "Early warning system",
      "demo.ai.autoDocs": "Auto Documentation",
      "demo.ai.autoDocsDesc": "Streamlined notes",
      "demo.scheduling.title": "Smart Scheduling",
      "demo.scheduling.subtitle": "Optimize care delivery",
      "demo.scheduling.description": "AI-optimized visit scheduling ensures the right staff reaches the right patient at the right time, with geographic clustering and priority-based routing.",
      "demo.scheduling.calendar": "Calendar View",
      "demo.scheduling.calendarDesc": "Visual schedule management",
      "demo.scheduling.geo": "Geographic Optimization",
      "demo.scheduling.geoDesc": "Efficient routing",
      "demo.scheduling.aiSuggestions": "AI Suggestions",
      "demo.scheduling.aiSuggestionsDesc": "Optimal time slots",
      "demo.scheduling.staff": "Staff Management",
      "demo.scheduling.staffDesc": "Availability & time-off",
      "demo.training.title": "Hospice Academy",
      "demo.training.subtitle": "Continuous learning for excellence",
      "demo.training.description": "Comprehensive training platform with ELNEC-aligned curriculum, interactive courses, quizzes, and CEU certificate generation for all disciplines.",
      "demo.training.courses": "56+ Courses",
      "demo.training.coursesDesc": "11 training tracks",
      "demo.training.ceu": "CEU Certificates",
      "demo.training.ceuDesc": "Downloadable credentials",
      "demo.training.badges": "Achievement Badges",
      "demo.training.badgesDesc": "Gamified learning",
      "demo.training.progress": "Progress Tracking",
      "demo.training.progressDesc": "Individual & team",
      "demo.analytics.title": "Analytics & Insights",
      "demo.analytics.subtitle": "Data-driven care improvement",
      "demo.analytics.description": "Comprehensive dashboards for staff performance, family engagement, CAHPS score prediction, and operational metrics to continuously improve care quality.",
      "demo.analytics.performance": "Staff Performance",
      "demo.analytics.performanceDesc": "Response times & outcomes",
      "demo.analytics.engagement": "Family Engagement",
      "demo.analytics.engagementDesc": "Interaction metrics",
      "demo.analytics.cahps": "CAHPS Predictor",
      "demo.analytics.cahpsDesc": "Quality scores",
      "demo.analytics.clinical": "Clinical Insights",
      "demo.analytics.clinicalDesc": "Care patterns",
      "demo.getStarted.title": "Ready to Transform Care?",
      "demo.getStarted.subtitle": "Join the Goodwin Cares family",
      "demo.getStarted.description": "Whether you're a patient, caregiver, or healthcare professional, Goodwin Cares provides the tools you need to deliver and receive exceptional hospice care.",
      "demo.getStarted.forPatients": "For Patients",
      "demo.getStarted.forPatientsDesc": "Sign up for care support",
      "demo.getStarted.forProviders": "For Providers",
      "demo.getStarted.forProvidersDesc": "Join our care network",
      "demo.getStarted.forPartners": "For Partners",
      "demo.getStarted.forPartnersDesc": "Healthcare integration",
      "demo.getStarted.forFamilies": "For Families",
      "demo.getStarted.forFamiliesDesc": "Caregiver resources",
      
      // Contact Page
      "contact.caption": "Get in Touch",
      "contact.title": "We're Here to Help",
      "contact.description": "Have questions about hospice care, need support, or want to learn more about our services? Reach out to us anytime. We're here for you.",
      "contact.phone": "Phone",
      "contact.available247": "Available 24/7",
      "contact.email": "Email",
      "contact.responseTime": "We respond within 24 hours",
      "contact.hours": "Hours",
      "contact.24hours": "24 hours a day",
      "contact.7days": "7 days a week",
      "contact.compassionateNote": "We understand that reaching out during this time can be difficult. Please know that our team is here with compassion and without judgment. Your questions and concerns matter to us.",
      "contact.sendMessage": "Send Us a Message",
      "contact.thankYou": "Thank You",
      "contact.messageReceived": "Your message has been received. We'll be in touch with you soon.",
      "contact.sendAnother": "Send Another Message",
      "contact.fullName": "Full Name",
      "contact.namePlaceholder": "Your name",
      "contact.emailAddress": "Email Address",
      "contact.emailPlaceholder": "your@email.com",
      "contact.phoneNumber": "Phone Number",
      "contact.subject": "Subject",
      "contact.subjectPlaceholder": "How can we help?",
      "contact.message": "Message",
      "contact.messagePlaceholder": "Tell us how we can help you...",
      "contact.sending": "Sending...",
      "contact.sendMessageBtn": "Send Message",
      
      // Language Tour
      "languageTour.title": "Language Changed!",
      "languageTour.welcome": "Welcome",
      "languageTour.keyPhrases": "Key phrases in your language:",
      "languageTour.home": "Home",
      "languageTour.help": "Get Help",
      "languageTour.messages": "Messages",
      "languageTour.profile": "Profile",
      "languageTour.gotIt": "Got it!",
      
      // 404 Not Found
      "notFound.title": "Page Not Found",
      "notFound.description": "Sorry, the page you're looking for doesn't exist or has been moved.",
      "notFound.goHome": "Go Home",
      "notFound.contactUs": "Contact Us",
      "notFound.goBack": "Go back to previous page",
      
      // Physician Portal
      "physician.portal.title": "Physician Portal",
      "physician.portal.subtitle": "Join Goodwin Hospice as a Hospice Champion. Access patient insights, submit referrals easily, and make a difference in end-of-life care.",
      "physician.portal.championProgram": "Hospice Champion Program",
      "physician.signIn": "Sign In",
      "physician.signIn.title": "Physician Sign In",
      "physician.signIn.description": "Access your patient panel and hospice tools",
      "physician.register": "Join Champion Program",
      "physician.register.title": "Join the Hospice Champion Program",
      "physician.register.description": "Register your practice to become a hospice champion and improve end-of-life care",
      "physician.email": "Email",
      "physician.password": "Password",
      "physician.forgotPassword": "Forgot Password?",
      "physician.signingIn": "Signing In...",
      "physician.submitting": "Submitting...",
      "physician.benefits.recognition": "Earn Recognition",
      "physician.benefits.recognitionDesc": "Champion tiers & CME credits",
      "physician.benefits.insights": "AI Patient Insights",
      "physician.benefits.insightsDesc": "Predictive eligibility analysis",
      "physician.benefits.referrals": "Easy Referrals",
      "physician.benefits.referralsDesc": "One-click patient referrals",
      "physician.benefits.support": "Priority Support",
      "physician.benefits.supportDesc": "Dedicated hospice liaison",
      "physician.practice.title": "Practice Information",
      "physician.practice.name": "Practice Name",
      "physician.practice.type": "Practice Type",
      "physician.practice.specialty": "Specialty",
      "physician.practice.npi": "Practice NPI",
      "physician.address.title": "Practice Address",
      "physician.address.street": "Street Address",
      "physician.address.city": "City",
      "physician.address.state": "State",
      "physician.address.zip": "ZIP",
      "physician.address.phone": "Phone",
      "physician.address.fax": "Fax",
      "physician.info.title": "Physician Information",
      "physician.info.name": "Physician Name",
      "physician.info.npi": "Individual NPI",
      "physician.info.email": "Physician Email",
      "physician.info.phone": "Physician Phone",
      "physician.account.title": "Account Credentials",
      "physician.account.email": "Account Email",
      "physician.account.password": "Password",
      "physician.account.confirm": "Confirm Password",
      "physician.champion.enroll": "Enroll in Hospice Champion Program",
      "physician.champion.enrollDesc": "Earn recognition, CME credits, and priority support for early hospice referrals. Progress through Bronze, Silver, Gold, and Platinum tiers.",
      "physician.pending.title": "Registration Pending Approval",
      "physician.pending.message": "Thank you for joining our Hospice Champion program! Your application is being reviewed.",
      "physician.pending.notice": "You'll receive an email once approved. Typically 1-2 business days.",
      "physician.pending.pendingApproval": "Your registration is pending approval.",
      "physician.goToLogin": "Go to Login",
      
      // Physician Dashboard
      "physician.dashboard.title": "Physician Dashboard",
      "physician.dashboard.welcome": "Welcome",
      "physician.dashboard.addPatient": "Add Patient",
      "physician.dashboard.logout": "Logout",
      "physician.dashboard.loading": "Loading dashboard...",
      "physician.dashboard.championProgress": "Hospice Champion Progress",
      "physician.dashboard.referrals": "referrals",
      "physician.dashboard.earlyReferrals": "early referrals",
      "physician.dashboard.progressTo": "Progress to",
      "physician.dashboard.referralsNeeded": "referrals needed",
      "physician.dashboard.viewBenefits": "View Benefits",
      "physician.dashboard.myPatients": "My Patients",
      "physician.dashboard.highRisk": "High Risk Patients",
      "physician.dashboard.pendingOrders": "Pending Orders",
      "physician.dashboard.totalReferrals": "Total Referrals",
      "physician.dashboard.criteriaTool": "Hospice Criteria Tool",
      "physician.dashboard.criteriaDesc": "AI eligibility assessment",
      "physician.dashboard.goalsOfCare": "Goals of Care",
      "physician.dashboard.goalsDesc": "Document discussions",
      "physician.dashboard.callHospice": "Call Hospice",
      "physician.tabs.patients": "Patient Panel",
      "physician.tabs.referrals": "Referrals",
      "physician.tabs.orders": "Orders",
      "physician.patients.title": "Patient Panel",
      "physician.patients.description": "Patients with predictive hospice insights",
      "physician.patients.search": "Search patients...",
      "physician.patients.noPatients": "No Patients Yet",
      "physician.patients.noPatientsDesc": "Add patients to track their hospice eligibility",
      "physician.patients.addFirst": "Add First Patient",
      "physician.patients.patient": "Patient",
      "physician.patients.diagnosis": "Diagnosis",
      "physician.patients.status": "Status",
      "physician.patients.eligibility": "Eligibility",
      "physician.patients.alerts": "Alerts",
      "physician.patients.actions": "Actions",
      "physician.patients.view": "View",
      "physician.patients.refer": "Refer",
      "physician.referrals.title": "Referral History",
      "physician.referrals.description": "Track your hospice referrals",
      "physician.referrals.noReferrals": "No Referrals Yet",
      "physician.referrals.noReferralsDesc": "Referrals will appear here once submitted",
      "physician.orders.title": "Orders from Hospice",
      "physician.orders.description": "Review and complete orders from Goodwin Hospice",
      "physician.orders.noOrders": "No Orders",
      "physician.orders.noOrdersDesc": "Orders from hospice will appear here",
      "physician.orders.acknowledge": "Acknowledge",
      "physician.orders.markComplete": "Mark Complete",
      "physician.orders.viewDetails": "View Details",
      
      // Facility Portal
      "facility.portal.title": "Facility Partner Portal",
      "facility.portal.subtitle": "Join Goodwin Hospice as a referral partner. Submit patient referrals securely, access hospice eligibility tools, and connect with our care team.",
      "facility.portal.partnerPortal": "Healthcare Partner Portal",
      "facility.signIn": "Sign In",
      "facility.signIn.title": "Facility Partner Sign In",
      "facility.signIn.description": "Access your facility dashboard to manage referrals and use hospice tools",
      "facility.register": "Register Your Facility",
      "facility.register.title": "Register Your Facility",
      "facility.register.description": "Complete the form below to become a Goodwin Hospice referral partner. All registrations require admin approval.",
      "facility.email": "Email",
      "facility.password": "Password",
      "facility.forgotPassword": "Forgot Password?",
      "facility.signingIn": "Signing In...",
      "facility.submitting": "Submitting...",
      "facility.submitRegistration": "Submit Registration",
      "facility.info.title": "Facility Information",
      "facility.info.name": "Facility Name",
      "facility.info.type": "Facility Type",
      "facility.info.npi": "NPI Number",
      "facility.info.taxId": "Tax ID",
      "facility.address.title": "Address",
      "facility.address.street": "Street Address",
      "facility.address.city": "City",
      "facility.address.state": "State",
      "facility.address.zip": "ZIP Code",
      "facility.address.phone": "Phone",
      "facility.address.fax": "Fax",
      "facility.address.website": "Website",
      "facility.contact.title": "Primary Contact",
      "facility.contact.name": "Contact Name",
      "facility.contact.titleField": "Title",
      "facility.contact.email": "Contact Email",
      "facility.contact.phone": "Contact Phone",
      "facility.account.title": "Account Credentials",
      "facility.account.email": "Account Email",
      "facility.account.password": "Password (min 8 characters)",
      "facility.account.confirm": "Confirm Password",
      "facility.certifications.title": "Certifications",
      "facility.certifications.medicare": "Medicare Certified",
      "facility.certifications.medicaid": "Medicaid Certified",
      "facility.certifications.jointCommission": "Joint Commission Accredited",
      "facility.pending.title": "Registration Pending Approval",
      "facility.pending.message": "Thank you for registering as a Goodwin Hospice partner facility. Your application is being reviewed by our admin team.",
      "facility.pending.notice": "You will receive an email notification once your account is approved. This typically takes 1-2 business days.",
      "facility.pending.pendingApproval": "Your facility registration is pending admin approval. You'll be notified when approved.",
      "facility.goToLogin": "Go to Login",
      
      // Facility Dashboard
      "facility.dashboard.title": "Facility Dashboard",
      "facility.dashboard.welcome": "Welcome back",
      "facility.dashboard.logout": "Logout",
      "facility.dashboard.loading": "Loading dashboard...",
      "facility.dashboard.totalReferrals": "Total Referrals",
      "facility.dashboard.pendingReview": "Pending Review",
      "facility.dashboard.accepted": "Accepted",
      "facility.dashboard.thisMonth": "This Month",
      "facility.dashboard.newReferral": "New Patient Referral",
      "facility.dashboard.newReferralDesc": "Submit a secure patient referral",
      "facility.dashboard.criteriaTool": "Hospice Criteria Tool",
      "facility.dashboard.criteriaDesc": "AI-powered eligibility assessment",
      "facility.dashboard.callReferral": "Call Referral Line",
      "facility.dashboard.callReferralDesc": "703-578-7108 (24/7)",
      "facility.dashboard.recentReferrals": "Recent Referrals",
      "facility.dashboard.recentReferralsDesc": "Track your patient referral submissions",
      "facility.dashboard.viewAll": "View All",
      "facility.dashboard.noReferrals": "No Referrals Yet",
      "facility.dashboard.noReferralsDesc": "Submit your first patient referral to get started",
      "facility.dashboard.newReferralBtn": "New Referral",
      "facility.referrals.patient": "Patient",
      "facility.referrals.diagnosis": "Diagnosis",
      "facility.referrals.urgency": "Urgency",
      "facility.referrals.status": "Status",
      "facility.referrals.date": "Date",
      "facility.referrals.view": "View",
      "facility.status.submitted": "Submitted",
      "facility.status.underReview": "Under Review",
      "facility.status.accepted": "Accepted",
      "facility.status.declined": "Declined",
      
      // Family Hub
      "familyHub.title": "Family Care Hub",
      "familyHub.subtitle": "Your care hub • We're here for you",
      "familyHub.loading": "Loading your dashboard...",
      "familyHub.dailyCheckin": "Daily Check-in",
      "familyHub.checkinQuestion": "How are you feeling?",
      "familyHub.aiAssistant": "AI Assistant",
      "familyHub.aiAssistantDesc": "Ask any question",
      "familyHub.medications": "Medications",
      "familyHub.medicationsDesc": "Refills & reminders",
      "familyHub.voiceMode": "Voice Mode",
      "familyHub.voiceModeDesc": "Hands-free control",
      "familyHub.videoCall": "Video Call",
      "familyHub.videoCallDesc": "Talk to care team",
      "familyHub.selfCare": "Self-Care",
      "familyHub.selfCareDesc": "Caregiver wellness",
      "familyHub.handsFreeMode": "Hands-Free Mode",
      "familyHub.handsFreeDesc": "Say \"pain level 5\" or \"my mood is okay\" to check in without touching your device",
      "familyHub.tryVoice": "Try Voice",
      "familyHub.nextVisit": "Your Next Visit",
      "familyHub.today": "Today!",
      "familyHub.tomorrow": "Tomorrow",
      "familyHub.upcomingSchedule": "Upcoming Schedule",
      "familyHub.resources": "Helpful Resources",
      "familyHub.videoLibrary": "Video Library",
      "familyHub.videoLibraryDesc": "Educational content",
      "familyHub.aiCompanion": "AI Companion",
      "familyHub.aiCompanionDesc": "Chat anytime",
      "familyHub.careGuide": "Care Guide",
      "familyHub.careGuideDesc": "Tips & guidance",
      "familyHub.myRecords": "My Records",
      "familyHub.myRecordsDesc": "Documents",
      "familyHub.notifications": "Notifications",
      "familyHub.noNotifications": "No notifications",
      "familyHub.careTeam": "Your Care Team",
      "familyHub.noCareTeam": "Care team info will appear here",
      "familyHub.messages": "Messages",
      "familyHub.newMessage": "New",
      "familyHub.noMessages": "No messages yet",
      "familyHub.sendMessage": "Send a message to your care team",
      "familyHub.supportLine": "24/7 Support Line",
      "familyHub.checkin.title": "Daily Check-in",
      "familyHub.checkin.description": "How are you feeling today? This helps your care team support you better.",
      "familyHub.checkin.painLevel": "Pain Level",
      "familyHub.checkin.mood": "How's your mood?",
      "familyHub.checkin.symptoms": "Any symptoms today?",
      "familyHub.checkin.notes": "Anything else to share?",
      "familyHub.checkin.notesPlaceholder": "Optional notes for your care team...",
      "familyHub.checkin.submit": "Submit Check-in",
      "familyHub.wellness.title": "Caregiver Wellness Check",
      "familyHub.wellness.description": "Taking care of yourself is important too. Let us know how you're doing.",
      "familyHub.wellness.stressLevel": "Stress Level",
      "familyHub.wellness.sleepHours": "Hours of Sleep Last Night",
      "familyHub.wellness.hadRespite": "Had any break/respite this week?",
      "familyHub.wellness.needsSupport": "Would you like support resources?",
      "familyHub.wellness.howSupport": "How can we support you?",
      "familyHub.wellness.supportPlaceholder": "Any concerns or needs...",
      "familyHub.message.title": "Message Your Care Team",
      "familyHub.message.description": "Send a secure message to your care team. They typically respond within 24 hours.",
      "familyHub.message.subject": "Subject",
      "familyHub.message.subjectPlaceholder": "What is this about?",
      "familyHub.message.messagePlaceholder": "Type your message here...",
      "familyHub.message.send": "Send Message",
      
      // Family Symptom Portal
      "familySymptom.title": "Care Status",
      "familySymptom.lastUpdated": "Last updated",
      "familySymptom.recently": "Recently",
      "familySymptom.infoTitle": "Understanding This Information",
      "familySymptom.infoDesc": "These symptom scores help you understand how your loved one is feeling. Lower scores mean symptoms are well-controlled. The care team monitors these closely and adjusts care as needed.",
      "familySymptom.currentStatus": "Current Symptom Status",
      "familySymptom.overallSummary": "Overall Care Summary",
      "familySymptom.wellControlled": "Well Controlled",
      "familySymptom.beingMonitored": "Being Monitored",
      "familySymptom.nextVisit": "Next Visit",
      "familySymptom.symptoms": "symptoms",
      "familySymptom.trendTitle": "2-Week Symptom Trends",
      "familySymptom.trendDesc": "See how symptoms have changed over time",
      "familySymptom.trendMeaning": "What do these trends mean?",
      "familySymptom.trendStable": "Stable or decreasing trends show good symptom control",
      "familySymptom.trendSlight": "Slight increases are normal and the care team is aware",
      "familySymptom.trendSignificant": "Significant changes prompt the team to adjust the care plan",
      "familySymptom.careTeam": "Your Care Team",
      "familySymptom.careTeamDesc": "Contact any team member with questions or concerns",
      "familySymptom.urgentConcerns": "For Urgent Concerns",
      "familySymptom.urgentCall": "Call the 24/7 nurse line:",
      "familySymptom.resources": "Helpful Resources",
      "familySymptom.painGuide": "Pain Management Guide",
      "familySymptom.breathingTips": "Breathing Comfort Tips",
      "familySymptom.caregiverSupport": "Caregiver Support",
      
      // Patient Registration
      "patientReg.title": "Join Goodwin Cares",
      "patientReg.subtitle": "Create your account to access personalized support and resources",
      "patientReg.createAccount": "Create Your Account",
      "patientReg.createAccountDesc": "Whether you're a patient or a family caregiver, we're here to support you on this journey.",
      "patientReg.iAmA": "I am a...",
      "patientReg.patient": "I am a Patient",
      "patientReg.patientDesc": "Receiving hospice or palliative care",
      "patientReg.caregiver": "I am a Family Caregiver",
      "patientReg.caregiverDesc": "Caring for a loved one",
      "patientReg.aboutRole": "About Your Role as Caregiver",
      "patientReg.relationship": "Your Relationship",
      "patientReg.selectRelationship": "Select your relationship...",
      "patientReg.patientName": "Patient's Name",
      "patientReg.patientNamePlaceholder": "Name of person you're caring for",
      "patientReg.diagnosis": "Primary Diagnosis",
      "patientReg.patientDiagnosis": "Patient's Primary Diagnosis",
      "patientReg.selectCondition": "Select condition...",
      "patientReg.diagnosisHelp": "This helps us personalize your resources and support",
      "patientReg.fullName": "Your Full Name",
      "patientReg.enterName": "Enter your full name",
      "patientReg.email": "Email",
      "patientReg.phone": "Phone",
      "patientReg.password": "Password",
      "patientReg.passwordMin": "Min. 8 characters",
      "patientReg.confirmPassword": "Confirm",
      "patientReg.confirmPasswordPlaceholder": "Confirm password",
      "patientReg.creating": "Creating Account...",
      "patientReg.createBtn": "Create Account",
      "patientReg.alreadyHave": "Already have an account?",
      "patientReg.signInHere": "Sign in here",
      "patientReg.healthcarePartner": "Are you a healthcare partner?",
      "patientReg.registerPartner": "Register as Partner",
      "patientReg.needHelp": "Need help? Call",
      "patientReg.selectRole": "Please select whether you are a patient or caregiver",
      "patientReg.fillRequired": "Please fill in all required fields",
      "patientReg.passwordMismatch": "Passwords do not match",
      "patientReg.passwordLength": "Password must be at least 8 characters",
      "patientReg.selectRelationshipError": "Please select your relationship to the patient"
    }
  },
  es: {
    translation: {
      // Common
      "app.name": "Goodwin Cares",
      "app.tagline": "Apoyo Compasivo de Hospicio",
      "common.loading": "Cargando...",
      "common.save": "Guardar",
      "common.cancel": "Cancelar",
      "common.delete": "Eliminar",
      "common.edit": "Editar",
      "common.close": "Cerrar",
      "common.submit": "Enviar",
      "common.back": "Atrás",
      "common.next": "Siguiente",
      "common.yes": "Sí",
      "common.no": "No",
      "common.search": "Buscar",
      "common.filter": "Filtrar",
      "common.refresh": "Actualizar",
      "common.settings": "Configuración",
      "common.profile": "Perfil",
      "common.important": "Importante",
      "common.returnHome": "Volver al Inicio",
      
      // Command Center (Spanish)
      "commandCenter.title": "Centro de Comando",
      "commandCenter.subtitle": "Panel de operaciones en tiempo real • Automatización con IA",
      "commandCenter.status.live": "EN VIVO",
      "commandCenter.status.polling": "CONSULTANDO",
      "commandCenter.status.connected": "WebSocket conectado",
      "commandCenter.autoRefresh": "Auto-actualizar",
      "commandCenter.tabs.overview": "Resumen",
      "commandCenter.tabs.alerts": "Alertas",
      "commandCenter.tabs.scheduling": "Programación",
      "commandCenter.tabs.vitals": "Signos Vitales",
      "commandCenter.tabs.predictive": "IA Predictiva",
      "commandCenter.stats.criticalAlerts": "Alertas Críticas",
      "commandCenter.stats.activeAlerts": "Alertas Activas",
      "commandCenter.stats.resolvedThisWeek": "Resueltas Esta Semana",
      "commandCenter.stats.avgResolutionTime": "Tiempo Promedio",
      "commandCenter.stats.automationActions": "Acciones Automáticas",
      "commandCenter.intervention.title": "Activar Intervenciones",
      "commandCenter.intervention.description": "Ejecutar protocolos de intervención con IA para pacientes en riesgo",
      
      // Notification Center (Spanish)
      "notificationCenter.title": "Centro de Notificaciones",
      "notificationCenter.subtitle": "Monitorear y gestionar comunicación con pacientes",
      "notificationCenter.loadError": "Error al cargar datos de notificaciones",
      "notificationCenter.connected": "Conectado",
      "notificationCenter.disconnected": "Desconectado",
      "notificationCenter.active": "Activo",
      "notificationCenter.inactive": "Inactivo",
      "notificationCenter.tabs.dashboard": "Panel",
      "notificationCenter.tabs.history": "Historial",
      "notificationCenter.stats.totalSent": "Total Enviados",
      "notificationCenter.stats.deliveryRate": "Tasa de Entrega",
      "notificationCenter.stats.emailSent": "Correos Enviados",
      "notificationCenter.stats.smsSent": "SMS Enviados",
      "notificationCenter.channelBreakdown": "Desglose por Canal",
      "notificationCenter.deliveryStatus": "Estado de Entrega",
      "notificationCenter.status.successful": "Exitosos",
      "notificationCenter.status.pending": "Pendientes",
      "notificationCenter.status.failed": "Fallidos",
      "notificationCenter.searchPlaceholder": "Buscar por ID de paciente...",
      "notificationCenter.filterChannel": "Canal",
      "notificationCenter.filterStatus": "Estado",
      "notificationCenter.historyTitle": "Historial de Notificaciones",
      "notificationCenter.historyDescription": "Ver y gestionar notificaciones enviadas",
      "notificationCenter.noHistory": "No se encontraron notificaciones",
      "notificationCenter.resend": "Reenviar",
      "notificationCenter.resendTitle": "Reenviar Notificación",
      "notificationCenter.resendDescription": "Enviar esta notificación nuevamente al paciente",
      "notificationCenter.resendSuccess": "Notificación reenviada exitosamente",
      "notificationCenter.resendError": "Error al reenviar notificación",
      
      // Email Analytics (Spanish)
      "emailAnalytics.title": "Análisis de Correos",
      "emailAnalytics.subtitle": "Rastrear participación por correo e identificar problemas de comunicación",
      "emailAnalytics.loadError": "Error al cargar análisis",
      "emailAnalytics.period.7days": "Últimos 7 días",
      "emailAnalytics.period.14days": "Últimos 14 días",
      "emailAnalytics.period.30days": "Últimos 30 días",
      "emailAnalytics.period.90days": "Últimos 90 días",
      "emailAnalytics.stats.sent": "Correos Enviados",
      "emailAnalytics.stats.deliveryRate": "Tasa de Entrega",
      "emailAnalytics.stats.openRate": "Tasa de Apertura",
      "emailAnalytics.stats.clickRate": "Tasa de Clics",
      "emailAnalytics.stats.bounceRate": "Tasa de Rebote",
      "emailAnalytics.lowEngagementAlert": "Baja Participación Detectada",
      "emailAnalytics.patientsLowEngagement": "pacientes tienen baja participación por correo. Considere llamadas telefónicas o comunicación alternativa.",
      "emailAnalytics.tabs.overview": "Resumen",
      "emailAnalytics.tabs.engagement": "Participación de Pacientes",
      "emailAnalytics.funnel.title": "Embudo de Correos",
      "emailAnalytics.funnel.description": "Rastrear correos desde envío hasta clic",
      "emailAnalytics.funnel.sent": "Enviados",
      "emailAnalytics.funnel.delivered": "Entregados",
      "emailAnalytics.funnel.opened": "Abiertos",
      "emailAnalytics.funnel.clicked": "Clicados",
      "emailAnalytics.trends.title": "Tendencias Diarias",
      "emailAnalytics.engagement.title": "Participación de Pacientes por Correo",
      "emailAnalytics.engagement.description": "Clasificado por puntuación de participación (aperturas + clics)",
      "emailAnalytics.engagement.lastOpened": "Última apertura",
      
      // Staff Tools (Spanish)
      "staffTools.title": "Herramientas de Personal",
      "staffTools.subtitle": "Solicitudes de Tiempo Libre, Biblioteca de POE y Búsqueda Global",
      "staffTools.tabs.timeOff": "Tiempo Libre",
      "staffTools.tabs.sops": "POE",
      "staffTools.tabs.search": "Buscar",
      "staffTools.timeOff.myRequests": "Mis Solicitudes",
      "staffTools.timeOff.requestTimeOff": "Solicitar Tiempo Libre",
      "staffTools.sop.title": "POE y Documentos de Referencia",
      "staffTools.sop.description": "Buscar procedimientos operativos estándar y documentos de referencia",
      "staffTools.search.title": "Búsqueda Global",
      "staffTools.search.description": "Buscar pacientes, personal, documentos y más",
      
      // Visit Frequency (Spanish)
      "visitFrequency.title": "Rastreador de Frecuencia de Visitas",
      "visitFrequency.subtitle": "Monitorear cumplimiento de visitas y rastrear visitas perdidas por disciplina",
      "visitFrequency.tabs.dashboard": "Panel",
      "visitFrequency.tabs.missed": "Visitas Perdidas",
      "visitFrequency.tabs.reminders": "Recordatorios",
      "visitFrequency.stats.compliant": "Pacientes Cumplidos",
      "visitFrequency.stats.atRisk": "En Riesgo",
      "visitFrequency.stats.critical": "Críticos",
      "visitFrequency.stats.total": "Total de Pacientes",
      
      // Patient Portal (Spanish)
      "patientPortal.title": "Portal del Paciente",
      "patientPortal.subtitle": "Conectar con su equipo de atención, compartir comentarios y gestionar su cuidado",
      "patientPortal.tabs.messages": "Mensajes",
      "patientPortal.tabs.visits": "Visitas",
      "patientPortal.tabs.telehealth": "Telesalud",
      "patientPortal.tabs.feedback": "Comentarios",
      "patientPortal.recognizeTeam": "Reconocer Equipo de Atención",
      
      "common.getStarted": "Comenzar",
      "common.new": "Nuevo",
      "common.manage": "Administrar",
      "common.optional": "opcional",
      
      // Navigation
      "nav.home": "Inicio",
      "nav.dashboard": "Panel",
      "nav.chat": "Obtener Ayuda IA",
      "nav.symptoms": "Manejar Síntomas",
      "nav.journal": "Registrar Notas",
      "nav.medications": "Medicamentos",
      "nav.calendar": "Calendario",
      "nav.resources": "Encontrar Apoyo",
      "nav.education": "Educación",
      "nav.videos": "Ver y Aprender",
      "nav.contact": "Contacto",
      "nav.messages": "Mensajear Equipo",
      "nav.memories": "Mi Historia",
      "nav.compliance": "Seguridad",
      "nav.signIn": "Iniciar Sesión",
      "nav.signOut": "Cerrar Sesión",
      "nav.demo": "Ver el Tour",
      "nav.programs": "Explorar Programas",
      
      // Dashboard
      "dashboard.greeting.morning": "Buenos días",
      "dashboard.greeting.afternoon": "Buenas tardes",
      "dashboard.greeting.evening": "Buenas noches",
      "dashboard.checkin": "Registrarse",
      "dashboard.checkedIn": "Registrado",
      "dashboard.streak": "días seguidos",
      "dashboard.medications": "Medicamentos",
      "dashboard.upcoming": "Próximos",
      "dashboard.careTeam": "Equipo de Cuidado",
      "dashboard.quickActions": "Acciones Rápidas",
      "dashboard.askAI": "Preguntar IA",
      "dashboard.insights": "Perspectivas de Cuidado",
      "dashboard.generateInsights": "Generar",
      "dashboard.manual": "Manual",
      "dashboard.voice": "Voz",
      "dashboard.welcomeTitle": "Bienvenido a Su Panel de Cuidado",
      "dashboard.signInPrompt": "Inicie sesión para acceder a sus herramientas de cuidado personalizadas",
      "dashboard.today": "hoy",
      "dashboard.thisWeek": "esta semana",
      "dashboard.assigned": "asignados",
      "dashboard.insightsDescription": "Observaciones impulsadas por IA basadas en sus datos",
      "dashboard.noInsightsYet": "Aún no hay perspectivas. ¡Siga registrando sus chequeos diarios!",
      "dashboard.needMoreData": "Necesitamos al menos 3 días de datos para generar perspectivas.",
      "dashboard.yourTrends": "Sus Tendencias",
      "dashboard.trendsDescription": "Basado en sus últimos {{count}} registros",
      "dashboard.avg": "prom",
      "dashboard.mostReportedSymptoms": "Síntomas Más Reportados",
      "dashboard.scheduledVisits": "Visitas Programadas",
      "dashboard.tomorrow": "Mañana",
      "dashboard.urgent": "Urgente",
      "dashboard.moreVisits": "más visitas programadas",
      "dashboard.noVisits": "No hay visitas próximas programadas",
      "dashboard.teamWillSchedule": "Su equipo de cuidado programará visitas según sea necesario",
      "dashboard.needToTalk": "¿Necesita Hablar?",
      "dashboard.requestCallbackDesc": "Solicite que nuestro equipo de cuidado lo llame",
      "dashboard.inQueue": "¡Está en la cola!",
      "dashboard.queuePosition": "Posición",
      "dashboard.estWait": "Espera est.",
      "dashboard.soon": "Pronto",
      "dashboard.requestCallback": "Solicitar Llamada",
      "dashboard.emergency": "Emergencia",
      "dashboard.manageContacts": "Administrar Contactos",
      "dashboard.viewAll": "Ver Todo",
      "dashboard.noUpcomingEvents": "No hay eventos próximos",
      
      // Callback Dialog
      "callback.title": "Solicitar una Llamada",
      "callback.description": "Nuestro equipo de cuidado lo llamará lo antes posible",
      "callback.whatHelp": "¿En qué necesita ayuda?",
      "callback.describeConcern": "Describa su inquietud o pregunta...",
      "callback.howUrgent": "¿Qué tan urgente es esto?",
      "callback.normal": "Normal - En unas horas",
      "callback.urgent": "Urgente - En 30 minutos",
      "callback.emergencyOption": "Emergencia - Llámeme inmediatamente",
      "callback.phoneNumber": "Número de teléfono",
      "callback.useRegistered": "Usar mi número registrado",
      "callback.leaveBlank": "Deje en blanco para usar su número de teléfono registrado",
      "callback.bestTime": "Mejor hora para llamar",
      "callback.asap": "Lo antes posible",
      "callback.morning": "Mañana (8am - 12pm)",
      "callback.afternoon": "Tarde (12pm - 5pm)",
      "callback.evening": "Noche (5pm - 8pm)",
      "callback.lifeThreatening": "Para emergencias que amenazan la vida:",
      "callback.call911": "Por favor llame al 911 inmediatamente",
      
      // Check-in
      "checkin.title": "Registro Diario",
      "checkin.overall": "¿Cómo se siente en general?",
      "checkin.pain": "Nivel de Dolor",
      "checkin.sleep": "Calidad del Sueño",
      "checkin.appetite": "Apetito",
      "checkin.mobility": "Movilidad",
      "checkin.mood": "Estado de Ánimo",
      "checkin.symptoms": "¿Algún síntoma hoy?",
      "checkin.notes": "¿Alguna nota?",
      "checkin.complete": "Completar Registro",
      "checkin.voiceTitle": "Registro por Voz",
      "checkin.veryPoor": "Muy Mal",
      "checkin.excellent": "Excelente",
      "checkin.noPain": "Sin Dolor",
      "checkin.severe": "Severo",
      "checkin.notesPlaceholder": "¿Cómo se siente realmente hoy?",
      
      // Moods
      "mood.peaceful": "Tranquilo",
      "mood.happy": "Feliz",
      "mood.okay": "Bien",
      "mood.tired": "Cansado",
      "mood.anxious": "Ansioso",
      "mood.sad": "Triste",
      
      // Symptoms
      "symptom.pain": "Dolor",
      "symptom.nausea": "Náuseas",
      "symptom.fatigue": "Fatiga",
      "symptom.breathlessness": "Falta de Aire",
      "symptom.constipation": "Estreñimiento",
      "symptom.anxiety": "Ansiedad",
      "symptom.confusion": "Confusión",
      "symptom.appetiteLoss": "Pérdida de Apetito",
      "symptom.insomnia": "Insomnio",
      "symptom.weakness": "Debilidad",
      "symptom.restlessness": "Inquietud",
      "symptom.skinChanges": "Cambios en la Piel",
      
      // Medications
      "medications.title": "Medicamentos",
      "medications.add": "Agregar Medicamento",
      "medications.todaySchedule": "Horario de Hoy",
      "medications.allMedications": "Todos los Medicamentos",
      "medications.markTaken": "Marcar Tomado",
      "medications.skip": "Omitir",
      "medications.taken": "Tomado",
      "medications.name": "Nombre del Medicamento",
      "medications.dosage": "Dosis",
      "medications.frequency": "Frecuencia",
      "medications.times": "Horarios",
      "medications.instructions": "Instrucciones Especiales",
      "medications.trackSchedule": "Siga su horario de medicamentos",
      "medications.noScheduled": "No hay medicamentos programados",
      "medications.signInToManage": "Inicie sesión para administrar sus medicamentos",
      "medications.addNew": "Agregar Nuevo Medicamento",
      "medications.namePlaceholder": "ej., Morfina",
      "medications.dosagePlaceholder": "ej., 10mg",
      "medications.instructionsPlaceholder": "ej., Tomar con comida",
      "medications.startDate": "Fecha de Inicio",
      "medications.of": "de",
      "medications.completed": "completado",
      "medications.scheduled": "Programado",
      "medications.activeMedications": "medicamentos activos",
      "medications.noAdded": "No se han agregado medicamentos",
      "medications.addFirst": "Agregue su Primer Medicamento",
      "medications.onceDaily": "Una Vez al Día",
      "medications.twiceDaily": "Dos Veces al Día",
      "medications.threeTimes": "Tres Veces al Día",
      "medications.fourTimes": "Cuatro Veces al Día",
      "medications.asNeeded": "Según Necesidad",
      "medications.weekly": "Semanal",
      
      // Symptom Guide
      "symptomGuide.title": "Guía de Manejo de Síntomas",
      "symptomGuide.caregiverTraining": "Capacitación para Cuidadores",
      "symptomGuide.description": "Guía completa sobre el manejo de síntomas comunes en hospicio. Aprenda medidas de confort y sepa exactamente cuándo llamar a su equipo de hospicio.",
      "symptomGuide.alertMessage": "En caso de duda, siempre llame a su equipo de hospicio. Están disponibles 24/7 y quieren escuchar de usted. Nunca dude en comunicarse.",
      "symptomGuide.downloadComplete": "Descargar Guía Completa (PDF)",
      "symptomGuide.loading": "Cargando guías de síntomas...",
      "symptomGuide.symptoms": "Síntomas",
      "symptomGuide.comfortMeasures": "Medidas de Confort",
      "symptomGuide.whenToCall": "Cuándo Llamar al Equipo de Hospicio",
      "symptomGuide.callImmediately": "LLAME INMEDIATAMENTE Si...",
      "symptomGuide.emergencyNote": "Estas situaciones requieren atención inmediata. Llame a su equipo de hospicio o al 911 de inmediato.",
      "symptomGuide.needMoreGuidance": "¿Necesita Más Orientación?",
      "symptomGuide.ctaDescription": "Chatee con nuestro asistente de IA para orientación personalizada sobre el manejo de síntomas y qué esperar.",
      "symptomGuide.askAboutSymptoms": "Preguntar Sobre Síntomas",
      
      // Messages
      "messages.title": "Mensajes",
      "messages.subtitle": "Comuníquese directamente con su equipo de cuidado",
      "messages.messageYourTeam": "Enviar Mensaje a Su Equipo de Cuidado",
      "messages.signInToMessage": "Inicie sesión para enviar mensajes directamente al personal de la oficina de hospicio y recibir respuestas en tiempo real.",
      "messages.newMessage": "Nuevo Mensaje",
      "messages.newToStaff": "Nuevo Mensaje al Personal",
      "messages.subject": "Asunto",
      "messages.subjectPlaceholder": "¿De qué se trata?",
      "messages.message": "Mensaje",
      "messages.messagePlaceholder": "Escriba su mensaje...",
      "messages.sendMessage": "Enviar Mensaje",
      "messages.conversations": "Conversaciones",
      "messages.noConversations": "Aún no hay conversaciones",
      "messages.startNew": "Inicie un nuevo mensaje a su equipo de cuidado",
      "messages.open": "Abierto",
      "messages.assignedTo": "Asignado a {{name}}",
      "messages.waitingForStaff": "Esperando respuesta del personal",
      "messages.selectConversation": "Seleccione una conversación o inicie una nueva",
      
      // Chat/AI
      "chat.topics": "Temas de Chat",
      "chat.goalsOfCare": "Metas de Cuidado",
      "chat.goalsDescription": "Discutir deseos de fin de vida y planificación de cuidado avanzado",
      "chat.goalsWelcome": "Hola, estoy aquí para ayudarle a navegar las conversaciones sobre metas de cuidado y planificación de cuidado avanzado. Estas discusiones pueden ser difíciles, pero son muy importantes. ¿De qué le gustaría hablar hoy?",
      "chat.hospiceInfo": "Info de Hospicio",
      "chat.hospiceDescription": "Aprenda sobre el cuidado de hospicio y sus beneficios",
      "chat.hospiceWelcome": "¡Bienvenido! Estoy aquí para ayudarle a entender el cuidado de hospicio - qué es, cómo ayuda y qué esperar. ¿Qué preguntas tiene sobre hospicio?",
      "chat.caregiverSupport": "Apoyo al Cuidador",
      "chat.caregiverDescription": "Obtenga apoyo y recursos para cuidadores",
      "chat.caregiverWelcome": "Hola, cuidador. Quiero que sepa que lo que hace importa profundamente. Estoy aquí para apoyarle con recursos, consejos y un oído compasivo. ¿Cómo puedo ayudarle hoy?",
      "chat.symptomHelp": "Ayuda con Síntomas",
      "chat.symptomDescription": "Obtenga orientación sobre el manejo de síntomas y cuándo llamar",
      "chat.symptomWelcome": "Estoy aquí para ayudarle a entender y manejar los síntomas relacionados con el hospicio. Le proporcionaré orientación sobre medidas de confort y le diré claramente cuándo llamar a su equipo de hospicio. ¿Qué síntoma o preocupación le gustaría discutir?",
      "chat.signInSave": "Inicie sesión para guardar conversaciones",
      "chat.historyPersist": "Su historial de chat persistirá entre sesiones",
      "chat.previousConversations": "Conversaciones Anteriores",
      "chat.clear": "Limpiar",
      "chat.new": "Nuevo",
      "chat.thinking": "Pensando...",
      "chat.placeholder": "Escriba su mensaje o use voz...",
      "chat.disclaimer": "Esto es solo para fines informativos. Siempre consulte a su equipo de hospicio para consejo médico.",
      "chat.recordingStarted": "Grabación iniciada - hable ahora",
      "chat.microphoneDenied": "Acceso al micrófono denegado. Por favor habilite los permisos del micrófono.",
      "chat.transcriptionComplete": "Transcripción completa",
      "chat.noSpeechDetected": "No se detectó voz. Por favor intente de nuevo.",
      "chat.transcriptionFailed": "Error al transcribir audio. Por favor intente de nuevo.",
      "chat.recording": "Grabando",
      "chat.transcribing": "Transcribiendo...",
      "chat.speaking": "Hablando...",
      "chat.ttsDisabled": "Respuestas de voz deshabilitadas",
      "chat.ttsEnabled": "Respuestas de voz habilitadas",
      "chat.ttsOn": "Voz Sí",
      "chat.ttsOff": "Voz No",
      "chat.speakNow": "Hable ahora...",
      "chat.voiceCommand.cleared": "Chat borrado por comando de voz",
      "chat.voiceCommand.newStarted": "Nueva conversación iniciada por comando de voz",
      "chat.voiceCommand.stopped": "Voz detenida por comando",
      "chat.voiceCommand.hint": "Intente decir: \"borrar chat\", \"nueva conversación\", o \"deja de hablar\"",
      
      // Profile
      "profile.title": "Mi Perfil",
      "profile.subtitle": "Administre la configuración y preferencias de su cuenta",
      "profile.verified": "Verificado",
      "profile.pendingApproval": "Aprobación Pendiente",
      "profile.profile": "Perfil",
      "profile.security": "Seguridad",
      "profile.notifications": "Notificaciones",
      "profile.profileInfo": "Información del Perfil",
      "profile.updatePersonal": "Actualice su información personal",
      "profile.fullName": "Nombre Completo",
      "profile.email": "Correo Electrónico",
      "profile.phone": "Teléfono",
      "profile.organization": "Organización",
      "profile.orgPlaceholder": "Nombre de la organización",
      "profile.specialty": "Especialidad",
      "profile.specialtyPlaceholder": "ej., Medicina Interna",
      "profile.npiNumber": "Número NPI",
      "profile.npiPlaceholder": "NPI de 10 dígitos",
      "profile.saveChanges": "Guardar Cambios",
      "profile.changePassword": "Cambiar Contraseña",
      "profile.passwordDescription": "Actualice su contraseña para mantener su cuenta segura",
      "profile.currentPassword": "Contraseña Actual",
      "profile.newPassword": "Nueva Contraseña",
      "profile.confirmPassword": "Confirmar Nueva Contraseña",
      "profile.updatePassword": "Actualizar Contraseña",
      "profile.notificationPrefs": "Preferencias de Notificación",
      "profile.notificationDescription": "Administre cómo recibe notificaciones",
      "profile.emailNotifications": "Notificaciones por Correo",
      "profile.emailNotificationsDesc": "Reciba actualizaciones por correo electrónico",
      "profile.smsNotifications": "Notificaciones por SMS",
      "profile.smsNotificationsDesc": "Reciba mensajes de texto para actualizaciones urgentes",
      "profile.pushNotifications": "Notificaciones Push",
      "profile.pushNotificationsDesc": "Reciba notificaciones en la aplicación",
      "profile.savePreferences": "Guardar Preferencias",
      
      // Calendar
      "calendar.title": "Calendario de Cuidado",
      "calendar.addEvent": "Agregar Evento",
      "calendar.upcoming": "Próximos Eventos",
      "calendar.appointment": "Cita",
      "calendar.visit": "Visita a Domicilio",
      "calendar.reminder": "Recordatorio",
      "calendar.milestone": "Hito",
      "calendar.medication": "Medicamento",
      "calendar.signInToView": "Inicie sesión para ver su calendario",
      "calendar.manageEvents": "Administre citas y eventos",
      "calendar.addNewEvent": "Agregar Nuevo Evento",
      "calendar.eventTitle": "Título del Evento",
      "calendar.eventTitlePlaceholder": "ej., Visita al Doctor",
      "calendar.eventType": "Tipo de Evento",
      "calendar.startTime": "Hora de Inicio",
      "calendar.endTime": "Hora de Fin",
      "calendar.location": "Ubicación",
      "calendar.locationPlaceholder": "ej., Oficina de Goodwin Hospice",
      "calendar.description": "Descripción",
      "calendar.descriptionPlaceholder": "Cualquier detalle adicional...",
      "calendar.reminderLabel": "Recordatorio",
      "calendar.15min": "15 minutos antes",
      "calendar.30min": "30 minutos antes",
      "calendar.1hour": "1 hora antes",
      "calendar.1day": "1 día antes",
      "calendar.next7days": "Próximos 7 días",
      "calendar.at": "a las",
      "calendar.noUpcoming": "No hay eventos próximos",
      
      // Emergency
      "emergency.title": "Contactos de Emergencia",
      "emergency.hospice": "Goodwin Hospicio",
      "emergency.nurseLine": "Línea de Enfermería 24/7",
      "emergency.call911": "Llamar 911",
      
      // Voice Commands
      "voice.listening": "Escuchando...",
      "voice.speak": "Hablar",
      "voice.stop": "Detener",
      "voice.repeat": "Repetir",
      "voice.notSupported": "Reconocimiento de voz no soportado",
      "voice.micDenied": "Acceso al micrófono denegado",
      
      // Patient Dashboard
      "patientDashboard.welcome": "Bienvenido",
      "patientDashboard.patient": "Paciente",
      "patientDashboard.subtitle": "Su panel de atención de hospicio",
      "patientDashboard.familyPortal": "Portal Familiar",
      "patientDashboard.messageTeam": "Mensajear Equipo",
      "patientDashboard.telehealth": "Telesalud",
      "patientDashboard.viewSchedule": "Ver Horario",
      "patientDashboard.notifications": "Notificaciones",
      "patientDashboard.overview": "Resumen",
      "patientDashboard.careTeam": "Equipo de Cuidado",
      "patientDashboard.medications": "Medicamentos",
      "patientDashboard.schedule": "Horario",
      "patientDashboard.upcomingVisits": "Próximas Visitas",
      "patientDashboard.noUpcomingVisits": "No hay visitas próximas programadas",
      "patientDashboard.recentActivity": "Actividad Reciente",
      "patientDashboard.currentMedications": "Medicamentos Actuales",
      "patientDashboard.yourMedications": "Sus Medicamentos",
      "patientDashboard.medicationsDescription": "Lista de medicamentos actuales administrada por su equipo de cuidado",
      "patientDashboard.visitSchedule": "Horario de Visitas",
      "patientDashboard.visitScheduleDescription": "Próximas visitas de su equipo de cuidado",
      "patientDashboard.noVisitsScheduled": "No hay visitas programadas",
      
      // Auth
      "auth.signIn": "Iniciar Sesión",
      "auth.signUp": "Registrarse",
      "auth.email": "Correo Electrónico",
      "auth.password": "Contraseña",
      "auth.name": "Nombre",
      "auth.forgotPassword": "¿Olvidó su contraseña?",
      "auth.welcomeBack": "Bienvenido",
      "auth.reset": "Restablecer",
      "auth.signInDescription": "Inicie sesión para acceder a su portal de atención, mensajes y recursos de salud",
      "auth.emailPlaceholder": "su@email.com",
      "auth.signInToPortal": "Iniciar Sesión al Portal",
      "auth.createAccount": "Crear Cuenta",
      "auth.fullName": "Nombre Completo",
      "auth.iAmA": "Soy un...",
      "auth.selectRole": "Seleccione su rol",
      "auth.newPassword": "Nueva Contraseña",
      "auth.confirmPassword": "Confirmar Contraseña",
      "auth.resetPassword": "Restablecer Contraseña",
      "auth.passwordResetSuccess": "¡Contraseña Restablecida Exitosamente!",
      "auth.signInNow": "Iniciar Sesión Ahora",
      "auth.needHelp": "¿Necesita ayuda para acceder a su cuenta?",
      "auth.resetDescription": "Ingrese su correo electrónico y le ayudaremos a restablecer su contraseña.",
      
      // Landing Page (Spanish)
      "landing.hero.badge": "Sirviendo al Norte de Virginia",
      "landing.hero.subtitle": "Cuidado de Hospicio Compasivo para Usted y Su Familia",
      "landing.hero.description": "Atención experta cuando más importa. Nuestro equipo dedicado brinda comodidad, dignidad y apoyo — con acceso las 24 horas a enfermeras, orientación impulsada por IA y un portal familiar para mantener a todos conectados.",
      "landing.hero.cta.chat": "Hablar con Nuestra Guía de IA",
      "landing.hero.cta.demo": "Ver Nuestra Historia",
      "landing.hero.callUs": "Llámenos en cualquier momento:",
      "landing.hero.alwaysHere": "Siempre Aquí",
      "landing.hero.support247": "Soporte 24/7 Disponible",
      "landing.hero.imageAlt": "Apoyo compasivo para cuidadores",
      "landing.stats.support247": "Línea de Enfermeras 24/7",
      "landing.stats.team": "Equipo Interdisciplinario",
      "landing.stats.rating": "",
      "landing.features.aiSupport.title": "Soporte Impulsado por IA",
      "landing.features.aiSupport.description": "Obtenga respuestas a sus preguntas de hospicio con nuestro asistente de IA compasivo. Guía específica por enfermedad disponible.",
      "landing.features.videos.title": "Videos de Capacitación",
      "landing.features.videos.description": "Vea videos dirigidos por expertos sobre cuidado de heridas, posicionamiento, administración de medicamentos y más.",
      "landing.features.symptoms.title": "Manejo de Síntomas",
      "landing.features.symptoms.description": "Guías completas sobre manejo de síntomas con alertas claras de 'cuándo llamar'. Exportar como PDF.",
      "landing.features.programs.title": "Programas Complementarios",
      "landing.features.programs.description": "Terapia de masaje, servicios de doula de fin de vida y apoyo posterior — más allá del cuidado tradicional de hospicio.",
      "landing.features.journal.title": "Diario del Cuidador",
      "landing.features.journal.description": "Rastree observaciones diarias, estado de ánimo, síntomas y medicamentos. Comparta información con su equipo de atención.",
      "landing.features.resources.title": "Recursos Comunitarios",
      "landing.features.resources.description": "Encuentre servicios de apoyo local en el Norte de Virginia — transporte, comidas, consejería y más con llamada directa.",
      "landing.stats.support": "Soporte IA",
      "landing.stats.resources": "Recursos Locales",
      "landing.stats.compassionate": "Compasivo",
      "landing.sections.features": "Cómo Ayudamos",
      "landing.sections.featuresSubtitle": "Soporte Integral para su Viaje",
      "landing.sections.learn": "Más Información",
      
      // Journal Page (Spanish)
      "journal.title": "Diario del Cuidador",
      "journal.subtitle": "Rastree observaciones diarias, estado de ánimo, síntomas y medicamentos para compartir con su equipo de hospicio.",
      "journal.personalTracking": "Seguimiento Personal",
      "journal.signInRequired": "Inicie sesión para acceder a su diario personal del cuidador y rastrear observaciones diarias, estado de ánimo y síntomas.",
      "journal.selectDate": "Seleccionar Fecha",
      "journal.dailyEntry": "Entrada Diaria",
      "journal.history": "Historial",
      "journal.weeklySummary": "Resumen Semanal",
      "journal.patientMood": "Estado de Ánimo del Paciente Hoy",
      "journal.dailyObservations": "Observaciones Diarias",
      "journal.observationsPlaceholder": "¿Cómo estuvo su ser querido hoy? ¿Notó algún cambio? Toque el micrófono para hablar.",
      "journal.symptomsObserved": "Síntomas Observados",
      "journal.rateSeverity": "Calificar severidad (1-5):",
      "journal.medicationLog": "Registro de Medicamentos",
      "journal.medication": "Medicamento",
      "journal.dose": "Dosis",
      "journal.time": "Hora",
      "journal.additionalNotes": "Notas Adicionales",
      "journal.notesPlaceholder": "Otras notas para su equipo de hospicio... Toque el micrófono para hablar.",
      "journal.saveEntry": "Guardar Entrada para",
      "journal.journalHistory": "Historial del Diario",
      "journal.historyDescription": "Ver y administrar sus entradas anteriores",
      "journal.noEntries": "Aún no hay entradas en el diario",
      "journal.moodTrend": "Tendencia del Estado de Ánimo",
      "journal.averageMood": "Estado de ánimo promedio (1-5)",
      "journal.symptomFrequency": "Frecuencia de Síntomas",
      "journal.noData": "Sin datos disponibles",
      "journal.noSymptomsLogged": "No se registraron síntomas esta semana",
      "journal.mood.veryLow": "Muy Bajo",
      "journal.mood.low": "Bajo",
      "journal.mood.neutral": "Neutral",
      "journal.mood.good": "Bueno",
      "journal.mood.veryGood": "Muy Bueno",
      
      // Session
      "session.expiring": "Sesión Por Expirar",
      "session.extend": "Extender Sesión",
      "session.remaining": "Tiempo restante",
      
      // Family Portal
      "familyPortal.title": "Portal Familiar",
      "familyPortal.subtitle": "Manténgase conectado con el equipo de cuidado de su ser querido",
      "familyPortal.newNotifications": "notificaciones nuevas",
      "familyPortal.enhancedCare": "Cuidado de Confort Mejorado",
      "familyPortal.enhancedCareDesc": "Nuestro equipo de atención brinda cuidado enfocado en el confort. Estamos aquí para apoyar tanto a su ser querido como a su familia durante este tiempo.",
      "familyPortal.nurseLineLabel": "Línea de Enfermería 24/7",
      "familyPortal.messageTeam": "Mensajear Equipo",
      "familyPortal.sendMessage": "Enviar Mensaje al Equipo de Cuidado",
      "familyPortal.messageDesc": "Su mensaje será enviado a todo el equipo de cuidado",
      "familyPortal.yourName": "Su Nombre",
      "familyPortal.yourNamePlaceholder": "Su nombre",
      "familyPortal.relationship": "Parentesco",
      "familyPortal.relationshipPlaceholder": "ej., Hija, Hijo, Cónyuge",
      "familyPortal.markUrgent": "Marcar como urgente",
      "familyPortal.callNurse": "Llamar Enfermera",
      "familyPortal.requestChaplain": "Solicitar Capellán",
      "familyPortal.scheduleVideoCall": "Programar Videollamada",
      "familyPortal.overview": "Resumen",
      "familyPortal.updates": "Actualizaciones",
      "familyPortal.resources": "Recursos",
      "familyPortal.careTimeline": "Cronología de Cuidado",
      "familyPortal.yourCareTeam": "Su Equipo de Cuidado",
      "familyPortal.primaryNurse": "Enfermera Principal",
      "familyPortal.attendingPhysician": "Médico Tratante",
      "familyPortal.socialWorker": "Trabajador Social",
      "familyPortal.supportServices": "Servicios de Apoyo",
      "familyPortal.contact": "Contactar",
      "familyPortal.message": "Mensaje",
      "familyPortal.request": "Solicitar",
      "familyPortal.upcomingVisits": "Próximas Visitas",
      "familyPortal.scheduled": "Programado",
      "familyPortal.visitScheduleAppear": "El horario de visitas aparecerá aquí",
      "familyPortal.currentFocusAreas": "Áreas de Enfoque Actuales",
      "familyPortal.recentNotifications": "Notificaciones Recientes",
      "familyPortal.noNotifications": "Aún no hay notificaciones",
      "familyPortal.careUpdates": "Actualizaciones de Cuidado",
      "familyPortal.recentUpdates": "Actualizaciones recientes de su equipo de cuidado",
      "familyPortal.noUpdates": "Aún no hay actualizaciones",
      "familyPortal.from": "De",
      "familyPortal.download": "Descargar",
      "familyPortal.careTimelineActivities": "Actividades recientes de cuidado",
      "familyPortal.timelineAppear": "La cronología de cuidado aparecerá aquí",
      "familyPortal.emergencyLine": "Línea de Emergencia 24/7",
      "familyPortal.callNow": "Llamar Ahora",
      
      // Resources Page
      "resources.location": "Norte de Virginia",
      "resources.title": "Recursos",
      "resources.titleHighlight": "Comunitarios",
      "resources.description": "Servicios de apoyo local para familias de hospicio en el área del Norte de Virginia. Encuentre transporte, comidas, consejería y más.",
      "resources.searchPlaceholder": "Buscar recursos...",
      "resources.allResources": "Todos los Recursos",
      "resources.noResults": "No se encontraron recursos que coincidan con sus criterios.",
      "resources.needHelp": "¿Necesita Ayuda para Encontrar Recursos?",
      "resources.helpDescription": "Nuestro asistente de IA puede ayudarle a encontrar los recursos comunitarios adecuados según sus necesidades específicas. Solo describa lo que está buscando.",
      "resources.chatWithAI": "Chatear con Asistente IA",
      
      // Staff Roster
      "staffRoster.title": "Lista del Personal",
      "staffRoster.subtitle": "Todos los miembros del personal registrados en el sistema",
      "staffRoster.loading": "Cargando Lista del Personal...",
      "staffRoster.totalStaff": "Personal Total",
      "staffRoster.searchPlaceholder": "Buscar por nombre, correo o disciplina...",
      "staffRoster.allRoles": "Todos los Roles",
      "staffRoster.allStatus": "Todos los Estados",
      "staffRoster.clearFilters": "Limpiar Filtros",
      "staffRoster.staffMembers": "Miembros del Personal",
      "staffRoster.noResults": "No se encontraron miembros del personal que coincidan con sus filtros",
      "staffRoster.lastLogin": "Último acceso",
      "staffRoster.completed": "completado",
      "staffRoster.pending": "pendiente",
      "staffRoster.sendEmail": "Enviar Correo",
      "staffRoster.viewTraining": "Ver Capacitación",
      "staffRoster.viewProfile": "Ver perfil de",
      "staffRoster.roles.admin": "Administrador",
      "staffRoster.roles.nurse": "Enfermera",
      "staffRoster.roles.rn": "Enfermera Registrada",
      "staffRoster.roles.lpn": "Enfermera Práctica",
      "staffRoster.roles.cna": "Asistente de Enfermería",
      "staffRoster.roles.socialWorker": "Trabajador Social",
      "staffRoster.roles.chaplain": "Capellán",
      "staffRoster.roles.physician": "Médico",
      "staffRoster.roles.staff": "Personal",
      "staffRoster.roles.volunteer": "Voluntario",
      "staffRoster.status.active": "Activo",
      "staffRoster.status.inactive": "Inactivo",
      "staffRoster.status.onLeave": "De Permiso",
      
      // Family Portal Enhanced
      "familyPortalEnhanced.title": "Portal de Cuidado Familiar",
      "familyPortalEnhanced.subtitle": "Manténgase conectado con el viaje de cuidado de su ser querido",
      "familyPortalEnhanced.linkedPatients": "Pacientes Vinculados",
      "familyPortalEnhanced.visitsThisWeek": "Visitas Esta Semana",
      "familyPortalEnhanced.upcomingVisits": "Próximas Visitas",
      "familyPortalEnhanced.unreadMessages": "Mensajes Sin Leer",
      "familyPortalEnhanced.selectLovedOne": "Seleccionar Ser Querido",
      "familyPortalEnhanced.careStatus": "Estado de Cuidado",
      "familyPortalEnhanced.aiCareTips": "Consejos de IA",
      "familyPortalEnhanced.updates": "Actualizaciones",
      "familyPortalEnhanced.careTeam": "Equipo de Cuidado",
      "familyPortalEnhanced.messages": "Mensajes",
      "familyPortalEnhanced.comfortLevel": "Nivel de Confort",
      "familyPortalEnhanced.painStatus": "Estado de Dolor",
      "familyPortalEnhanced.alertness": "Estado de Alerta",
      "familyPortalEnhanced.appetite": "Apetito",
      "familyPortalEnhanced.mobility": "Movilidad",
      "familyPortalEnhanced.recentVisits": "Visitas Recientes",
      "familyPortalEnhanced.currentMedications": "Medicamentos Actuales",
      "familyPortalEnhanced.sendMessage": "Enviar Mensaje",
      "familyPortalEnhanced.sendToCareTeam": "Enviar al Equipo de Cuidado",
      "familyPortalEnhanced.typeMessage": "Escriba su mensaje al equipo de cuidado...",
      "familyPortalEnhanced.hotline": "Línea de Hospicio 24/7",
      "familyPortalEnhanced.urgentCall": "Para inquietudes urgentes, llame:",
      "familyPortalEnhanced.refreshRecommendations": "Actualizar Recomendaciones",
      "familyPortalEnhanced.noRecommendations": "Aún no hay recomendaciones de IA disponibles",
      "familyPortalEnhanced.linkPatient": "Vincule un paciente para recibir consejos personalizados",
      "familyPortalEnhanced.noCareStatus": "No hay información de estado de cuidado disponible",
      "familyPortalEnhanced.contactCareTeam": "Por favor contacte al equipo de cuidado para vincular su cuenta",
      "familyPortalEnhanced.questionsToAsk": "Preguntas para Hacer Durante las Visitas",
      "familyPortalEnhanced.careTips": "Consejos de Cuidado",
      "familyPortalEnhanced.communicationTips": "Consejos de Comunicación",
      "familyPortalEnhanced.helpfulResources": "Recursos Útiles",
      
      // Language Tour
      "languageTour.title": "¡Idioma Cambiado!",
      "languageTour.welcome": "Bienvenido",
      "languageTour.keyPhrases": "Frases clave en su idioma:",
      "languageTour.home": "Inicio",
      "languageTour.help": "Obtener Ayuda",
      "languageTour.messages": "Mensajes",
      "languageTour.profile": "Perfil",
      "languageTour.gotIt": "¡Entendido!",
      
      // Staff Portal
      "staffPortal.title": "Portal del Personal",
      "staffPortal.subtitle": "Administre su carga de pacientes y comunicaciones",
      "staffPortal.loading": "Cargando portal del personal...",
      "staffPortal.accessDenied": "Acceso Denegado",
      "staffPortal.accessDeniedDesc": "Necesita credenciales de personal para acceder a este portal.",
      "staffPortal.signIn": "Iniciar Sesión",
      "staffPortal.tabs.caseload": "Mi Carga de Pacientes",
      "staffPortal.tabs.messages": "Mensajes",
      "staffPortal.tabs.alerts": "Alertas Clínicas",
      "staffPortal.tabs.appreciations": "Agradecimientos",
      "staffPortal.tabs.settings": "Configuración",
      "staffPortal.tabs.admin": "Herramientas Admin",
      "staffPortal.caseload.title": "Carga de Pacientes",
      "staffPortal.caseload.search": "Buscar pacientes...",
      "staffPortal.caseload.assignPatient": "Asignar Paciente",
      "staffPortal.caseload.importCaseload": "Importar Carga",
      "staffPortal.caseload.downloadTemplate": "Descargar Plantilla",
      "staffPortal.caseload.total": "Total",
      "staffPortal.caseload.primary": "Primario",
      "staffPortal.caseload.backup": "Respaldo",
      "staffPortal.caseload.noPatients": "No hay pacientes en su carga",
      "staffPortal.caseload.getStarted": "Comience importando pacientes o asignándolos manualmente",
      "staffPortal.patient.details": "Detalles del Paciente",
      "staffPortal.patient.status": "Estado",
      "staffPortal.patient.diagnosis": "Diagnóstico",
      "staffPortal.patient.admissionDate": "Fecha de Admisión",
      "staffPortal.patient.address": "Dirección",
      "staffPortal.patient.phone": "Teléfono",
      "staffPortal.patient.assignedStaff": "Personal Asignado",
      "staffPortal.patient.recentJournal": "Entrada de Diario Reciente",
      "staffPortal.patient.noJournalEntries": "Aún no hay entradas de diario",
      "staffPortal.patient.viewFullProfile": "Ver Perfil Completo",
      "staffPortal.patient.startConversation": "Iniciar Conversación",
      "staffPortal.messages.title": "Conversaciones del Equipo",
      "staffPortal.messages.newConversation": "Nueva Conversación",
      "staffPortal.messages.noConversations": "Aún no hay conversaciones",
      "staffPortal.messages.startNew": "Inicie una nueva conversación con la familia del paciente o el equipo de cuidado",
      "staffPortal.messages.typeMessage": "Escriba un mensaje...",
      "staffPortal.messages.send": "Enviar",
      "staffPortal.alerts.title": "Alertas Clínicas",
      "staffPortal.alerts.noAlerts": "No hay alertas activas",
      "staffPortal.alerts.allClear": "¡Todos sus pacientes están bien!",
      "staffPortal.alerts.dismiss": "Descartar",
      "staffPortal.alerts.viewPatient": "Ver Paciente",
      "staffPortal.appreciations.title": "Agradecimientos del Personal",
      "staffPortal.appreciations.noAppreciations": "Aún no hay agradecimientos",
      "staffPortal.appreciations.keepUp": "¡Siga con el excelente trabajo!",
      "staffPortal.settings.title": "Notificaciones SMS",
      "staffPortal.settings.enableSms": "Habilitar Notificaciones SMS",
      "staffPortal.settings.phoneNumber": "Número de Teléfono",
      "staffPortal.settings.saveSettings": "Guardar Configuración",
      "staffPortal.admin.title": "Herramientas de Administración",
      "staffPortal.admin.quickStart": "Asistente de Inicio Rápido",
      "staffPortal.admin.ehrFeatures": "Funciones EHR",
      "staffPortal.admin.createPatient": "Crear Paciente",
      "staffPortal.import.title": "Importar Carga de Pacientes",
      "staffPortal.import.selectFile": "Seleccionar archivo CSV",
      "staffPortal.import.import": "Importar",
      "staffPortal.import.success": "Importación exitosa",
      "staffPortal.import.failed": "Importación fallida",
      "staffPortal.assign.title": "Asignar Paciente",
      "staffPortal.assign.selectStaff": "Seleccionar Miembro del Personal",
      "staffPortal.assign.selectPatient": "Seleccionar Paciente",
      "staffPortal.assign.assignmentType": "Tipo de Asignación",
      "staffPortal.assign.notes": "Notas (opcional)",
      "staffPortal.assign.assign": "Asignar",
      "staffPortal.orderSupplies": "Pedir Suministros",
      "staffPortal.admin.addToEHR": "Agregar a EHR",
      "staffPortal.stats.myPatients": "Mis Pacientes",
      "staffPortal.stats.viewCaseload": "Ver pacientes",
      "staffPortal.stats.activeThisWeek": "Activos Esta Semana",
      "staffPortal.stats.viewScheduled": "Ver programados",
      "staffPortal.stats.conversations": "Conversaciones",
      "staffPortal.stats.viewAllMessages": "Ver todos los mensajes",
      "staffPortal.stats.unreadMessages": "Mensajes Sin Leer",
      "staffPortal.stats.viewUnread": "Ver sin leer",
      "staffPortal.tabs.moodTrends": "Tendencias de Ánimo",
      "staffPortal.tabs.system": "Sistema",
      "staffPortal.caseload.assignedPatients": "Pacientes Asignados",
      "staffPortal.caseload.description": "Ver y administrar su carga de pacientes asignados",
      "staffPortal.caseload.addPatient": "Agregar Paciente",
      "staffPortal.caseload.noPatientsAssigned": "No hay pacientes asignados",
      "staffPortal.caseload.importOrAssign": "Importe una carga de trabajo o asigne pacientes manualmente.",
      "staffPortal.caseload.contactAdmin": "Contacte a su administrador para que le asignen pacientes.",
      "staffPortal.caseload.noCheckins": "Sin registros",
      "staffPortal.caseload.lastActivity": "Última actividad",
      "staffPortal.messages.conversations": "Conversaciones",
      "staffPortal.academy.title": "Academia de Hospicio",
      "staffPortal.academy.description": "Acceder a cursos de capacitación y seguimiento de CEU",
      "staffPortal.academy.open": "Abrir Academia",
      "staffPortal.clinicalAlerts.title": "Alertas de Documentación Clínica",
      "staffPortal.clinicalAlerts.description": "Información clínica importante que debe documentarse en el expediente médico",
      "staffPortal.clinicalAlerts.viewPatient": "Ver Paciente",
      "staffPortal.clinicalAlerts.dismiss": "Descartar",
      "staffPortal.clinicalAlerts.allClear": "¡Todo Claro!",
      "staffPortal.clinicalAlerts.noAlerts": "No hay alertas de documentación clínica en este momento",
      "staffPortal.documentation.bestPractices": "Mejores Prácticas de Documentación",
      "staffPortal.documentation.patientCheckins": "Registro de Pacientes",
      "staffPortal.documentation.painLevels": "Documentar niveles de dolor superiores a 5/10",
      "staffPortal.documentation.moodChanges": "Anotar cambios de ánimo e intervenciones",
      "staffPortal.documentation.symptomProgression": "Registrar progresión de síntomas",
      "staffPortal.documentation.medicationCompliance": "Cumplimiento de Medicamentos",
      "staffPortal.documentation.missedMedications": "Documentar medicamentos omitidos",
      "staffPortal.documentation.nonCompliance": "Anotar razones de incumplimiento",
      "staffPortal.documentation.prnEffectiveness": "Registrar efectividad de medicamentos PRN",
      "staffPortal.documentation.vitalSigns": "Signos Vitales",
      "staffPortal.documentation.vitalChanges": "Registrar cambios vitales significativos",
      "staffPortal.documentation.weightFluctuations": "Documentar fluctuaciones de peso",
      "staffPortal.documentation.respiratoryStatus": "Anotar cambios en estado respiratorio",
      "staffPortal.documentation.familyCommunication": "Comunicación Familiar",
      "staffPortal.documentation.familyConcerns": "Documentar preocupaciones familiares",
      "staffPortal.documentation.educationProvided": "Registrar educación proporcionada",
      "staffPortal.documentation.carePlanDiscussions": "Anotar discusiones del plan de cuidados",
      "staffPortal.moodTrends.attentionNeeded": "Atención Necesaria",
      "staffPortal.moodTrends.distribution": "Distribución de Ánimo (Últimos 30 Días)",
      "staffPortal.moodTrends.checkinsFrom": "{{count}} registros de {{patients}} pacientes",
      "staffPortal.moodTrends.noData": "No hay datos de ánimo disponibles",
      "staffPortal.moodTrends.latestPatientMoods": "Últimos Estados de Ánimo",
      "staffPortal.moodTrends.pain": "Dolor",
      "staffPortal.moodTrends.noRecentData": "No hay datos recientes de ánimo",
      "staffPortal.appreciation.yourStats": "Sus Estadísticas de Reconocimiento",
      "staffPortal.appreciation.fromCount": "de {{count}} reconocimiento(s)",
      "staffPortal.appreciation.byCategory": "Por Categoría",
      "staffPortal.appreciation.recentAppreciations": "Reconocimientos Recientes",
      "staffPortal.appreciation.from": "De",
      "staffPortal.appreciation.noAppreciations": "Sin reconocimientos aún",
      "staffPortal.appreciation.keepUp": "¡Sigan con el excelente trabajo!",
      "staffPortal.smsSettings.title": "Configuración de Notificaciones SMS",
      "staffPortal.smsSettings.description": "Reciba alertas de mensaje de texto cuando los pacientes le envíen mensajes",
      "staffPortal.smsSettings.phoneNumber": "Número de Teléfono",
      "staffPortal.smsSettings.usPhoneNumber": "Número de teléfono de EE.UU. para recibir notificaciones SMS",
      "staffPortal.smsSettings.textMessageNotifications": "Notificaciones por Mensaje de Texto",
      "staffPortal.smsSettings.receiveAlerts": "Recibir alertas SMS cuando los pacientes le envíen mensajes",
      "staffPortal.smsSettings.howItWorks": "Cómo funciona",
      "staffPortal.smsSettings.howItWorksDescription": "Cuando un paciente le envía un mensaje a través del portal, recibirá un mensaje de texto con una vista previa de su mensaje. Esto le ayuda a responder rápidamente a las necesidades del paciente.",
      "staffPortal.smsSettings.saveSettings": "Guardar Configuración",
      "staffPortal.patientDetails.title": "Detalles del Paciente",
      "staffPortal.patientDetails.description": "Ver información completa del paciente y actividad",
      "staffPortal.patientDetails.email": "Correo",
      "staffPortal.patientDetails.condition": "Condición",
      "staffPortal.patientDetails.notSpecified": "No especificado",
      "staffPortal.patientDetails.phone": "Teléfono",
      "staffPortal.patientDetails.recentCheckins": "Registros Recientes",
      "staffPortal.patientDetails.painLevel": "Dolor",
      "staffPortal.patientDetails.mood": "Ánimo",
      "staffPortal.patientDetails.symptoms": "Síntomas",
      "staffPortal.patientDetails.noCheckins": "No hay registros",
      "staffPortal.patientDetails.currentMedications": "Medicamentos Actuales",
      "staffPortal.patientDetails.noMedications": "Sin medicamentos listados",
      "staffPortal.patientDetails.recentJournals": "Diarios Recientes",
      "staffPortal.patientDetails.noJournals": "Sin entradas de diario",
      "staffPortal.patientDetails.aiCareInsights": "Análisis de Cuidado con IA",
      "staffPortal.import.title": "Importar Carga de Trabajo",
      "staffPortal.import.description": "Suba un archivo CSV o Excel con asignaciones de personal-paciente",
      "staffPortal.import.selectFile": "Seleccionar Archivo",
      "staffPortal.import.downloadTemplate": "Descargar Plantilla",
      "staffPortal.import.results": "Resultados de Importación",
      "staffPortal.import.successful": "exitoso",
      "staffPortal.import.failed": "fallido",
      "staffPortal.import.assignmentsCreated": "Asignaciones Creadas",
      "staffPortal.import.errors": "Errores",
      "staffPortal.import.row": "Fila",
      "staffPortal.import.import": "Importar",
      "staffPortal.assign.title": "Asignar Paciente al Personal",
      "staffPortal.assign.description": "Crear una nueva asignación de personal-paciente",
      "staffPortal.assign.staffMember": "Miembro del Personal",
      "staffPortal.assign.selectStaff": "Seleccionar personal...",
      "staffPortal.assign.patients": "pacientes",
      "staffPortal.assign.patient": "Paciente",
      "staffPortal.assign.selectPatient": "Seleccionar paciente...",
      "staffPortal.assign.assignmentType": "Tipo de Asignación",
      "staffPortal.assign.primary": "Primario",
      "staffPortal.assign.secondary": "Secundario",
      "staffPortal.assign.backup": "Respaldo",
      "staffPortal.assign.notes": "Notas (opcional)",
      "staffPortal.assign.notesPlaceholder": "Agregar notas sobre esta asignación...",
      "staffPortal.assign.assign": "Asignar",
      "staffPortal.newConversation.title": "Nueva Conversación",
      "staffPortal.newConversation.description": "Iniciar una conversación con un paciente asignado",
      "staffPortal.newConversation.patient": "Paciente",
      "staffPortal.newConversation.selectPatient": "Seleccionar paciente...",
      "staffPortal.newConversation.subject": "Asunto",
      "staffPortal.newConversation.subjectPlaceholder": "Asunto de la conversación...",
      "staffPortal.newConversation.message": "Mensaje",
      "staffPortal.newConversation.messagePlaceholder": "Su mensaje...",
      "staffPortal.newConversation.start": "Iniciar Conversación",
      "staffPortal.addPatient.title": "Agregar Nuevo Paciente",
      "staffPortal.addPatient.description": "Crear manualmente un nuevo registro de paciente. Se generará una contraseña temporal.",
      "staffPortal.addPatient.success": "¡Paciente Creado Exitosamente!",
      "staffPortal.addPatient.name": "Nombre",
      "staffPortal.addPatient.emailLabel": "Correo",
      "staffPortal.addPatient.tempPassword": "Contraseña Temporal",
      "staffPortal.addPatient.smsNotification": "Notificación SMS",
      "staffPortal.addPatient.sentToPatient": "✓ Enviado al paciente",
      "staffPortal.addPatient.notSent": "No enviado",
      "staffPortal.addPatient.shareCredentials": "Por favor comparta estas credenciales de acceso con el paciente. Se le pedirá que cambie su contraseña en el primer inicio de sesión.",
      "staffPortal.addPatient.credentialsSent": "Las credenciales han sido enviadas al teléfono del paciente por SMS.",
      "staffPortal.addPatient.done": "Listo",
      "staffPortal.addPatient.fullName": "Nombre Completo",
      "staffPortal.addPatient.fullNamePlaceholder": "Juan Pérez",
      "staffPortal.addPatient.email": "Correo Electrónico",
      "staffPortal.addPatient.emailPlaceholder": "paciente@ejemplo.com",
      "staffPortal.addPatient.phone": "Teléfono",
      "staffPortal.addPatient.dob": "Fecha de Nacimiento",
      "staffPortal.addPatient.address": "Dirección",
      "staffPortal.addPatient.addressPlaceholder": "123 Calle Principal, Ciudad, Estado",
      "staffPortal.addPatient.diagnosis": "Diagnóstico Principal",
      "staffPortal.addPatient.diagnosisPlaceholder": "Ingrese diagnóstico principal...",
      "staffPortal.addPatient.emergencyContactName": "Nombre de Contacto de Emergencia",
      "staffPortal.addPatient.emergencyNamePlaceholder": "María López",
      "staffPortal.addPatient.emergencyContactPhone": "Teléfono de Contacto de Emergencia",
      "staffPortal.addPatient.notes": "Notas",
      "staffPortal.addPatient.notesPlaceholder": "Notas adicionales sobre el paciente...",
      "staffPortal.addPatient.createPatient": "Crear Paciente",
      
      // Moods (shared across pages)
      "moods.happy": "Feliz",
      "moods.calm": "Tranquilo",
      "moods.grateful": "Agradecido",
      "moods.hopeful": "Esperanzado",
      "moods.content": "Contento",
      "moods.anxious": "Ansioso",
      "moods.sad": "Triste",
      "moods.frustrated": "Frustrado",
      "moods.tired": "Cansado",
      "moods.scared": "Asustado",
      "moods.angry": "Enojado",
      
      // Hospice Academy Page (Spanish)
      "academy.accessRestricted": "Acceso restringido a personal y administradores.",
      "academy.tabs.courses": "Cursos",
      "academy.tabs.certificates": "Mis Certificados",
      "academy.tabs.achievements": "Logros",
      "academy.tabs.assignments": "Asignaciones",
      "academy.tabs.admin": "Admin",
      "academy.elnec.title": "Currículo ELNEC Core",
      "academy.elnec.description": "Consorcio de Educación de Enfermería al Final de la Vida - 8.0 Créditos CEU",
      "academy.modules": "Módulos",
      "academy.completed": "✓ Completado",
      "academy.startElnec": "Iniciar ELNEC",
      "academy.searchCourses": "Buscar cursos...",
      "academy.required": "Requerido",
      "academy.review": "Revisar",
      "academy.takeQuiz": "Tomar Examen",
      "academy.watchVideo": "Ver Video",
      "academy.certificates.title": "Mis Certificados y Transcripción CEU",
      "academy.certificates.subtitle": "Descargue o imprima sus certificados de finalización",
      "academy.ceuCreditsEarned": "Créditos CEU Obtenidos",
      "academy.ofTotalAvailable": "de {{total}} disponibles en total",
      "academy.elnecCertificate": "Certificado de Finalización ELNEC Core",
      "academy.elnecCertDesc": "Consorcio de Educación de Enfermería al Final de la Vida • 8.0 Créditos CEU",
      "academy.downloadCertificate": "Descargar Certificado",
      "academy.individualCertificates": "Certificados de Cursos Individuales",
      "academy.completeToCertify": "Complete cursos para obtener certificados",
      "academy.browseCourses": "Explorar Cursos",
      "academy.completedOn": "Completado",
      "academy.achievements.title": "Logros y Distintivos",
      "academy.achievements.subtitle": "{{earned}} de {{total}} distintivos obtenidos",
      "academy.earned": "Obtenido",
      "academy.assignments.title": "Asignaciones de Capacitación",
      "academy.assignments.subtitle": "Monitorear y gestionar el progreso de capacitación del personal",
      "academy.assignments.filter.all": "Todas las Asignaciones",
      "academy.assignments.filter.active": "Activas (Pendientes)",
      "academy.assignments.filter.due": "Próximas a Vencer",
      "academy.assignments.filter.overdue": "Vencidas",
      "academy.assignments.filter.completed": "Completadas",
      "academy.processing": "Procesando...",
      "academy.sendReminders": "Enviar Recordatorios",
      "academy.assignTraining": "Asignar Capacitación",
      "academy.stats.active": "Activas/Pendientes",
      "academy.stats.dueSoon": "Próximas a Vencer",
      "academy.stats.overdue": "Vencidas",
      "academy.stats.completed": "Completadas",
      "academy.assignments.label": "Asignaciones",
      "academy.assignments.none": "No se encontraron asignaciones {{filter}}",
      "academy.unknownStaff": "Personal Desconocido",
      "academy.dueDate": "Fecha Límite",
      "academy.status.overdue": "Vencida",
      "academy.status.dueSoon": "Próxima a Vencer",
      "academy.analytics.title": "Análisis de Capacitación del Personal",
      "academy.analytics.subtitle": "Monitorear tasas de finalización y seguir el progreso del equipo",
      "academy.analytics.totalCourses": "Total de Cursos",
      "academy.analytics.totalCeus": "CEUs Disponibles Totales",
      "academy.analytics.requiredCourses": "Cursos Requeridos",
      "academy.analytics.trainingTracks": "Rutas de Capacitación",
      "academy.analytics.tracksOverview": "Resumen de Rutas de Capacitación",
      "academy.analytics.tracksDesc": "Cursos y créditos CEU por disciplina",
      "academy.courses": "cursos",
      "academy.certificate.title": "Certificado de Finalización",
      "academy.certificate.subtitle": "Imprima o descargue su certificado",
      "academy.printCertificate": "Imprimir Certificado",
      
      // Programs Page
      "programs.hero.badge": "Más Allá del Cuidado Tradicional",
      "programs.hero.title": "Programas",
      "programs.hero.titleHighlight": "Complementarios",
      "programs.hero.description": "En Goodwin Hospice, nuestro cuidado se basa en la creencia de que toda persona tiene derecho a morir sin dolor y con dignidad. Ofrecemos terapias y servicios que van más allá del cuidado tradicional de hospicio.",
      "programs.benefits": "Beneficios",
      "programs.servicesWeHelp": "Servicios con los que Ayudamos",
      "programs.call": "Llamar",
      "programs.visitWebsite": "Visitar Sitio Web",
      "programs.contactToLearnMore": "Contáctenos para más información sobre este programa",
      "programs.cta.title": "¿Tiene Preguntas Sobre Nuestros Programas?",
      "programs.cta.description": "Nuestro equipo está aquí para ayudarle a entender cómo estos programas complementarios pueden apoyarle a usted y a sus seres queridos durante este camino.",
      "programs.cta.callButton": "Llamar 703.578.7108",
      "programs.cta.contactOnline": "Contáctenos en Línea",
      "programs.massage.title": "Terapia de Masaje al Final de la Vida",
      "programs.massage.subtitle": "Confort a Través del Toque Sanador",
      "programs.massage.description": "El contacto físico puede tener un impacto profundo en el bienestar general de alguien que enfrenta problemas médicos graves. Las investigaciones indican que la terapia de masaje puede mejorar la calidad de vida de quienes reciben cuidado de hospicio.",
      "programs.massage.quote": "Uno de los comentarios que más escuchamos de los pacientes es cuánto disfrutaron la compañía de nuestros terapeutas. Pueden beneficiarse de la interacción social así como del masaje terapéutico.",
      "programs.massage.quoteAuthor": "Laura Bryant-Earner, Directora de Servicios en Healwell",
      "programs.massage.benefits.1": "Mejora la calidad de vida de los pacientes de hospicio",
      "programs.massage.benefits.2": "Reduce los niveles de ansiedad y estrés",
      "programs.massage.benefits.3": "Alivia el dolor, la falta de sueño, las náuseas y la fatiga",
      "programs.massage.benefits.4": "Ayuda a los músculos a relajarse y liberar tensión",
      "programs.massage.benefits.5": "Proporciona interacción social reconfortante",
      "programs.massage.benefits.6": "Promueve sentimientos de paz y dignidad",
      "programs.massage.details": "Cualquier persona que reciba atención de Goodwin Hospice puede solicitar terapia de masaje, y recibirá hasta tres sesiones, que pueden compartirse entre pacientes y familiares.",
      "programs.massage.highlight": "Ofrecido sin costo adicional para los pacientes",
      "programs.doula.title": "Servicios de Doula al Final de la Vida",
      "programs.doula.subtitle": "Guía Compasiva Durante la Transición",
      "programs.doula.description": "Una doula de final de vida guía a la persona que está transitando hacia la muerte y a sus seres queridos a través del proceso de morir. Las doulas ayudan a las familias a enfrentar la muerte reconociéndola como una parte natural e importante de la vida.",
      "programs.doula.quote": "Una doula de final de vida guía a la persona que está transitando hacia la muerte y a sus seres queridos a través del proceso de morir.",
      "programs.doula.quoteAuthor": "La Asociación Internacional de Doulas de Final de Vida (INELDA)",
      "programs.doula.benefits.1": "Apoyo emocional, espiritual y físico en un momento intensamente personal",
      "programs.doula.benefits.2": "Ayuda a planificar cómo se desarrollarán los últimos días",
      "programs.doula.benefits.3": "Apoya a los seres queridos durante el proceso de morir",
      "programs.doula.benefits.4": "Alivia el sufrimiento del duelo",
      "programs.doula.benefits.5": "Hace la transición menos caótica y más enfocada en la compasión",
      "programs.doula.benefits.6": "Acompaña a los seres queridos moribundos o guía a través de la meditación",
      "programs.doula.details": "Las doulas se consideran parte del equipo interdisciplinario en Goodwin Hospice. Intervienen según sea necesario a solicitud de la familia, brindando presencia, guía y apoyo.",
      "programs.doula.highlight": "Proporcionado a través del generoso apoyo de donantes",
      "programs.aftercare.title": "Cuidado Posterior Círculo Completo",
      "programs.aftercare.subtitle": "Podemos Ser Su Guía",
      "programs.aftercare.description": "La pérdida de un ser querido es difícil. Puede que no sepa qué hacer a continuación. Proporcionamos recursos para ayudarle a navegar las complejidades de cerrar el patrimonio de su ser querido.",
      "programs.aftercare.details": "Asistencia adicional de un especialista en patrimonio capacitado está disponible para ayudarle a navegar este momento difícil.",
      "programs.aftercare.highlight": "Orientación experta cuando más la necesita",
      "programs.aftercare.services.government": "Gobierno",
      "programs.aftercare.services.socialSecurity": "Seguro Social",
      "programs.aftercare.services.va": "Administración de Veteranos",
      "programs.aftercare.services.opm": "OPM",
      "programs.aftercare.services.finances": "Finanzas",
      "programs.aftercare.services.pension": "Planes de Pensión",
      "programs.aftercare.services.401k": "401K/IRA",
      "programs.aftercare.services.creditCards": "Tarjetas de Crédito",
      "programs.aftercare.services.bankAccounts": "Cuentas Bancarias",
      "programs.aftercare.services.insurance": "Seguros",
      "programs.aftercare.services.lifeInsurance": "Seguro de Vida",
      "programs.aftercare.services.medical": "Médico/Salud",
      "programs.aftercare.services.carHome": "Auto/Hogar",
      "programs.aftercare.services.beneficiaries": "Cambiar Beneficiarios",
      "programs.aftercare.services.estate": "Patrimonio",
      "programs.aftercare.services.wills": "Testamentos/Fideicomisos/Sucesiones",
      "programs.aftercare.services.realEstate": "Título de Propiedad Inmobiliaria",
      "programs.aftercare.services.vehicle": "Título de Vehículo",
      "programs.aftercare.services.protection": "Protección",
      "programs.aftercare.services.creditBureau": "Buró de Crédito/Alerta de Fraude",
      "programs.aftercare.services.deceased": "Registro de Fallecidos",
      "programs.aftercare.services.junkMail": "Detener Correo Basura",
      "programs.aftercare.services.subscriptions": "Cancelar Suscripciones",
      
      // Translation Dashboard
      "translationDashboard.title": "Panel de Progreso de Traducción",
      "translationDashboard.subtitle": "Seguimiento de la cobertura de internacionalización en todas las páginas e idiomas",
      "translationDashboard.coverage": "Cobertura",
      "translationDashboard.keysTranslated": "claves traducidas",
      "translationDashboard.pages": "páginas",
      "translationDashboard.totalPages": "Total de Páginas/Componentes",
      "translationDashboard.languagesComplete": "Idiomas 90%+ Completos",
      "translationDashboard.missingKeys": "Claves Faltantes",
      
      // Video Library (Spanish)
      "videoLibrary.backToHome": "Volver al Inicio",
      "videoLibrary.title": "Biblioteca de Videos",
      "videoLibrary.description": "Explore nuestra colección de demostraciones completas. Cada video proporciona una experiencia completa y fluida que muestra las capacidades de Goodwin Cares.",
      "videoLibrary.fullVideos": "{{count}} Videos Completos",
      "videoLibrary.takeInteractiveTour": "Tomar el Tour Interactivo",
      "videoLibrary.searchPlaceholder": "Buscar videos...",
      "videoLibrary.fullLengthTitle": "Videos Completos de Características",
      "videoLibrary.fullLengthDescription": "Cada video se reproduce sin interrupciones - introducción de marca, muestra completa de características y cierre profesional. Experimente la historia completa de cada característica en ~36 segundos.",
      "videoLibrary.searchResults": "Resultados de búsqueda para \"{{query}}\"",
      "videoLibrary.allFeatures": "Todas las Características",
      "videoLibrary.videoCount": "{{count}} videos",
      "videoLibrary.noVideosFound": "No se encontraron videos",
      "videoLibrary.tryAdjusting": "Intente ajustar sus criterios de búsqueda o filtro",
      "videoLibrary.cta.title": "¿Listo para Experimentar Cuidado Compasivo?",
      "videoLibrary.cta.description": "Únase a miles de familias y profesionales de la salud que confían en Goodwin Cares para su viaje de cuidado de hospicio.",
      "videoLibrary.cta.getStarted": "Comenzar",
      "videoLibrary.cta.takeTour": "Tomar el Tour",
      "videoLibrary.category.all": "Todos",
      "videoLibrary.category.overview": "Resumen",
      "videoLibrary.category.communication": "Comunicación",
      "videoLibrary.category.scheduling": "Programación",
      "videoLibrary.category.clinical": "Clínico",
      "videoLibrary.category.training": "Capacitación",
      "videoLibrary.category.analytics": "Análisis",
      "videoLibrary.skipIntro": "Saltar Intro",
      
      // Admin Dashboard
      "adminDashboard.title": "Panel de Administración",
      "adminDashboard.subtitle": "Monitorear análisis del sitio, uso de chat y mensajes de contacto",
      "adminDashboard.refreshData": "Actualizar Datos",
      "adminDashboard.accessRequired": "Acceso de Administrador Requerido",
      "adminDashboard.noPrivileges": "Su cuenta no tiene privilegios de administrador. Por favor contacte a un administrador.",
      "adminDashboard.pleaseSignIn": "Por favor inicie sesión con una cuenta de administrador para acceder al panel.",
      "adminDashboard.signInAsAdmin": "Iniciar Sesión como Admin",
      "adminDashboard.status.new": "Nuevo",
      "adminDashboard.status.inProgress": "En Progreso",
      "adminDashboard.status.resolved": "Resuelto",
      "adminDashboard.status.archived": "Archivado",
      "adminDashboard.stats.totalVisits": "Visitas Totales",
      "adminDashboard.stats.chatSessions": "Sesiones de Chat",
      "adminDashboard.stats.totalMessages": "Mensajes Totales",
      "adminDashboard.stats.uniqueUsers": "Usuarios Únicos",
      "adminDashboard.stats.contactMessages": "Mensajes de Contacto",
      "adminDashboard.tabs.overview": "Resumen",
      "adminDashboard.tabs.contacts": "Mensajes de Contacto",
      "adminDashboard.tabs.system": "Funciones del Sistema",
      
      // Demo Page - Interactive Tour (Spanish)
      "demo.welcome.title": "Bienvenido a Goodwin Cares",
      "demo.welcome.subtitle": "Cuidado Compasivo, Impulsado por Tecnología",
      "demo.welcome.description": "Una plataforma integral de soporte para hospicio y cuidadores que combina inteligencia impulsada por IA con compasión humana para brindar la mejor experiencia de cuidado posible.",
      "demo.welcome.patientCare": "Cuidado Centrado en el Paciente",
      "demo.welcome.hipaa": "Cumple con HIPAA",
      "demo.welcome.aiInsights": "Análisis Impulsados por IA",
      "demo.welcome.familyEngagement": "Participación Familiar",
      "demo.patientCare.title": "Cuidado Integral del Paciente",
      "demo.patientCare.subtitle": "Todo lo que las familias necesitan en un solo lugar",
      "demo.patientCare.description": "Los pacientes y cuidadores pueden rastrear síntomas, medicamentos y comunicarse directamente con su equipo de cuidado a través de nuestro panel intuitivo.",
      "demo.patientCare.checkins": "Registros Diarios",
      "demo.patientCare.checkinsDesc": "Rastree dolor, estado de ánimo y síntomas",
      "demo.patientCare.medications": "Recordatorios de Medicamentos",
      "demo.patientCare.medicationsDesc": "Nunca olvide una dosis",
      "demo.patientCare.messaging": "Mensajería Directa",
      "demo.patientCare.messagingDesc": "Chatee con su equipo de cuidado",
      "demo.patientCare.symptoms": "Seguimiento de Síntomas",
      "demo.patientCare.symptomsDesc": "Tendencias visuales de salud",
      "demo.communication.title": "Comunicación en Tiempo Real",
      "demo.communication.subtitle": "Manténgase conectado con su equipo de cuidado",
      "demo.communication.description": "Mensajería segura, videollamadas y notificaciones instantáneas mantienen a todos informados y conectados, reduciendo la ansiedad y mejorando la coordinación del cuidado.",
      "demo.communication.telehealth": "Visitas de Telesalud",
      "demo.communication.telehealthDesc": "Cara a cara desde casa",
      "demo.communication.secureMsg": "Mensajería Segura",
      "demo.communication.secureMsgDesc": "Chat compatible con HIPAA",
      "demo.communication.notifications": "Notificaciones Push",
      "demo.communication.notificationsDesc": "Alertas en tiempo real",
      "demo.communication.triage": "Triaje de Llamadas",
      "demo.communication.triageDesc": "Soporte urgente 24/7",
      "demo.ai.title": "Inteligencia Impulsada por IA",
      "demo.ai.subtitle": "Funciones inteligentes que marcan la diferencia",
      "demo.ai.description": "Nuestra IA analiza los datos del paciente para proporcionar información procesable, predecir necesidades y ayudar al personal a brindar cuidado proactivo y personalizado.",
      "demo.ai.chatAssistant": "Asistente de Chat IA",
      "demo.ai.chatAssistantDesc": "Obtenga respuestas instantáneas",
      "demo.ai.suggestions": "Sugerencias Inteligentes",
      "demo.ai.suggestionsDesc": "Respuestas de mensajes con IA",
      "demo.ai.predictive": "Alertas Predictivas",
      "demo.ai.predictiveDesc": "Sistema de alerta temprana",
      "demo.ai.autoDocs": "Documentación Automática",
      "demo.ai.autoDocsDesc": "Notas simplificadas",
      "demo.scheduling.title": "Programación Inteligente",
      "demo.scheduling.subtitle": "Optimice la entrega de cuidado",
      "demo.scheduling.description": "La programación de visitas optimizada por IA asegura que el personal adecuado llegue al paciente adecuado en el momento adecuado, con agrupación geográfica y enrutamiento basado en prioridades.",
      "demo.scheduling.calendar": "Vista de Calendario",
      "demo.scheduling.calendarDesc": "Gestión visual de horarios",
      "demo.scheduling.geo": "Optimización Geográfica",
      "demo.scheduling.geoDesc": "Enrutamiento eficiente",
      "demo.scheduling.aiSuggestions": "Sugerencias de IA",
      "demo.scheduling.aiSuggestionsDesc": "Horarios óptimos",
      "demo.scheduling.staff": "Gestión de Personal",
      "demo.scheduling.staffDesc": "Disponibilidad y tiempo libre",
      "demo.training.title": "Academia de Hospicio",
      "demo.training.subtitle": "Aprendizaje continuo para la excelencia",
      "demo.training.description": "Plataforma de capacitación integral con currículo alineado con ELNEC, cursos interactivos, cuestionarios y generación de certificados CEU para todas las disciplinas.",
      "demo.training.courses": "56+ Cursos",
      "demo.training.coursesDesc": "11 pistas de capacitación",
      "demo.training.ceu": "Certificados CEU",
      "demo.training.ceuDesc": "Credenciales descargables",
      "demo.training.badges": "Insignias de Logros",
      "demo.training.badgesDesc": "Aprendizaje gamificado",
      "demo.training.progress": "Seguimiento de Progreso",
      "demo.training.progressDesc": "Individual y equipo",
      "demo.analytics.title": "Análisis e Información",
      "demo.analytics.subtitle": "Mejora del cuidado basada en datos",
      "demo.analytics.description": "Paneles completos para rendimiento del personal, participación familiar, predicción de puntuación CAHPS y métricas operativas para mejorar continuamente la calidad del cuidado.",
      "demo.analytics.performance": "Rendimiento del Personal",
      "demo.analytics.performanceDesc": "Tiempos de respuesta y resultados",
      "demo.analytics.engagement": "Participación Familiar",
      "demo.analytics.engagementDesc": "Métricas de interacción",
      "demo.analytics.cahps": "Predictor CAHPS",
      "demo.analytics.cahpsDesc": "Puntuaciones de calidad",
      "demo.analytics.clinical": "Información Clínica",
      "demo.analytics.clinicalDesc": "Patrones de cuidado",
      "demo.getStarted.title": "¿Listo para Transformar el Cuidado?",
      "demo.getStarted.subtitle": "Únase a la familia Goodwin Cares",
      "demo.getStarted.description": "Ya sea paciente, cuidador o profesional de la salud, Goodwin Cares proporciona las herramientas que necesita para brindar y recibir atención de hospicio excepcional.",
      "demo.getStarted.forPatients": "Para Pacientes",
      "demo.getStarted.forPatientsDesc": "Regístrese para soporte de cuidado",
      "demo.getStarted.forProviders": "Para Proveedores",
      "demo.getStarted.forProvidersDesc": "Únase a nuestra red de cuidado",
      "demo.getStarted.forPartners": "Para Socios",
      "demo.getStarted.forPartnersDesc": "Integración de atención médica",
      "demo.getStarted.forFamilies": "Para Familias",
      "demo.getStarted.forFamiliesDesc": "Recursos para cuidadores",
      
      // Contact Page
      "contact.caption": "Contáctenos",
      "contact.title": "Estamos Aquí para Ayudar",
      "contact.description": "¿Tiene preguntas sobre el cuidado de hospicio, necesita apoyo o desea obtener más información sobre nuestros servicios? Comuníquese con nosotros en cualquier momento. Estamos aquí para usted.",
      "contact.phone": "Teléfono",
      "contact.available247": "Disponible 24/7",
      "contact.email": "Correo Electrónico",
      "contact.responseTime": "Respondemos dentro de 24 horas",
      "contact.hours": "Horario",
      "contact.24hours": "24 horas al día",
      "contact.7days": "7 días a la semana",
      "contact.compassionateNote": "Entendemos que comunicarse durante este tiempo puede ser difícil. Sepa que nuestro equipo está aquí con compasión y sin juicio. Sus preguntas e inquietudes nos importan.",
      "contact.sendMessage": "Envíenos un Mensaje",
      "contact.thankYou": "Gracias",
      "contact.messageReceived": "Su mensaje ha sido recibido. Nos pondremos en contacto con usted pronto.",
      "contact.sendAnother": "Enviar Otro Mensaje",
      "contact.fullName": "Nombre Completo",
      "contact.namePlaceholder": "Su nombre",
      "contact.emailAddress": "Correo Electrónico",
      "contact.emailPlaceholder": "su@correo.com",
      "contact.phoneNumber": "Número de Teléfono",
      "contact.subject": "Asunto",
      "contact.subjectPlaceholder": "¿Cómo podemos ayudarle?",
      "contact.message": "Mensaje",
      "contact.messagePlaceholder": "Cuéntenos cómo podemos ayudarle...",
      "contact.sending": "Enviando...",
      "contact.sendMessageBtn": "Enviar Mensaje",
      
      // Reset Password Page (Spanish)
      "resetPassword.title": "Crear Nueva Contraseña",
      "resetPassword.description": "Ingrese su nueva contraseña a continuación.",
      "resetPassword.completeTitle": "Restablecimiento Completo",
      "resetPassword.completeDescription": "Su contraseña ha sido restablecida exitosamente.",
      "resetPassword.invalidLink": "Enlace inválido. Por favor solicite un nuevo restablecimiento de contraseña.",
      "resetPassword.passwordsMismatch": "Las contraseñas no coinciden",
      "resetPassword.passwordTooShort": "La contraseña debe tener al menos 8 caracteres",
      "resetPassword.successMessage": "¡Contraseña restablecida exitosamente!",
      "resetPassword.failedMessage": "Error al restablecer contraseña. El enlace puede haber expirado.",
      "resetPassword.canSignIn": "Ahora puede iniciar sesión con su nueva contraseña.",
      "resetPassword.goToSignIn": "Ir a Iniciar Sesión",
      "resetPassword.newPassword": "Nueva Contraseña",
      "resetPassword.enterNewPassword": "Ingrese nueva contraseña",
      "resetPassword.minChars": "Mínimo 8 caracteres",
      "resetPassword.confirmPassword": "Confirmar Contraseña",
      "resetPassword.confirmNewPassword": "Confirme nueva contraseña",
      "resetPassword.resetButton": "Restablecer Contraseña",
      "resetPassword.resetting": "Restableciendo...",
      "resetPassword.rememberPassword": "¿Recuerda su contraseña?",
      
      // Joyful Memories Page (Spanish)
      "memories.title": "Recuerdos",
      "memories.titleHighlight": "Alegres",
      "memories.celebrateLife": "Celebrar la Vida",
      "memories.description": "Capture y comparta los hermosos momentos, historias e hitos que hacen especial la vida de su ser querido. Cada recuerdo es un tesoro que vale la pena preservar.",
      "memories.shareTitle": "Compartir Recuerdos Alegres",
      "memories.signInPrompt": "Inicie sesión para crear y compartir hermosos recuerdos de sus seres queridos.",
      "memories.createNew": "Crear Nuevo Recuerdo",
      "memories.dialogTitle": "Crear un Recuerdo Alegre",
      "memories.memoryType": "Tipo de Recuerdo",
      "memories.forPatientLabel": "Para (Nombre del Paciente)",
      "memories.patientPlaceholder": "¿Para quién es este recuerdo?",
      "memories.titleLabel": "Título",
      "memories.titlePlaceholder": "Dale un título a este recuerdo",
      "memories.contentLabel": "Su Recuerdo",
      "memories.contentPlaceholder": "Comparta su hermoso recuerdo...",
      "memories.enhanceAI": "Mejorar con IA",
      "memories.dateLabel": "¿Cuándo sucedió? (Opcional)",
      "memories.shareWithCommunity": "Compartir con la Comunidad",
      "memories.shareDescription": "Permitir que otros vean y aprecien este recuerdo",
      "memories.saveMemory": "Guardar Recuerdo",
      "memories.myMemories": "Mis Recuerdos",
      "memories.community": "Comunidad",
      "memories.noMemoriesYet": "Aún no hay recuerdos. ¡Cree el primero!",
      "memories.noCommunityMemories": "Aún no hay recuerdos compartidos de la comunidad.",
      "memories.forPatient": "Para {{name}}",
      "memories.shared": "Compartido",
      "memories.private": "Privado",
      "memories.defaultLabel": "Recuerdo",
      "memories.fillRequired": "Por favor complete todos los campos requeridos",
      "memories.saveSuccess": "¡Recuerdo guardado hermosamente!",
      "memories.saveFailed": "Error al guardar recuerdo",
      "memories.writeFirst": "Por favor escriba algo primero",
      "memories.enhanceSuccess": "¡Recuerdo mejorado con magia de IA!",
      "memories.enhanceFailed": "Error al mejorar recuerdo",
      "memories.ctaTitle": "Cada Recuerdo Importa",
      "memories.ctaDescription": "Las historias que compartimos y los momentos que recordamos se convierten en el legado que dejamos. Tómese un momento para capturar algo hermoso hoy.",
      "memories.types.story": "Historia",
      "memories.types.storyDesc": "Comparta un recuerdo o historia preciada",
      "memories.types.milestone": "Hito",
      "memories.types.milestoneDesc": "Celebre un momento importante",
      "memories.types.gratitude": "Gratitud",
      "memories.types.gratitudeDesc": "Exprese agradecimiento",
      "memories.types.photo": "Recuerdo Fotográfico",
      "memories.types.photoDesc": "Describa una foto atesorada",
      
      // Telehealth Page (Spanish)
      "telehealth.title": "Videollamadas de Telesalud",
      "telehealth.subtitle": "Consultas de video compatibles con HIPAA con pacientes y familias",
      "telehealth.connected": "Conectado",
      "telehealth.connecting": "Conectando...",
      "telehealth.endCall": "Terminar Llamada",
      "telehealth.addNotes": "Agregue notas antes de terminar la llamada",
      "telehealth.callNotes": "Notas de Llamada (Opcional)",
      "telehealth.notesPlaceholder": "Agregue notas sobre esta llamada...",
      "telehealth.cancel": "Cancelar",
      "telehealth.addParticipant": "Agregar Participante",
      "telehealth.inviteDescription": "Invite a otra persona a unirse a esta llamada",
      "telehealth.selectParticipant": "Seleccionar Participante",
      "telehealth.chooseParticipant": "Elegir un participante...",
      "telehealth.consultation": "Consulta de telesalud",
      "telehealth.tips.title": "Consejos para Videollamadas",
      "telehealth.tips.lighting": "Asegure buena iluminación en su rostro",
      "telehealth.tips.headphones": "Use audífonos para mejor audio",
      "telehealth.tips.testConnection": "Pruebe su conexión antes de la llamada",
      "telehealth.tips.recording": "La grabación se almacena de forma segura",
      
      // Call Triage Page (Spanish)
      "callTriage.title": "Centro de Triaje de Llamadas",
      "callTriage.subtitle": "Conexión siempre activa para manejo instantáneo de llamadas de pacientes",
      "callTriage.alwaysConnected": "Centro de Llamadas Siempre Conectado - Recibirá notificaciones instantáneas para llamadas entrantes",
      "callTriage.enableAlerts": "Habilitar Alertas",
      "callTriage.notificationsOn": "Notificaciones Activadas",
      "callTriage.dial": "Marcar",
      "callTriage.newCall": "Nueva Llamada",
      "callTriage.addToEHR": "Agregar a EHR",
      "callTriage.dialing": "Marcando...",
      "callTriage.ringing": "Sonando...",
      "callTriage.callConnected": "Llamada Conectada",
      "callTriage.callFailed": "Llamada Fallida",
      "callTriage.endCall": "Terminar Llamada",
      "callTriage.filterDate": "Filtrar por Fecha:",
      "callTriage.clear": "Limpiar",
      "callTriage.inQueue": "En Cola",
      "callTriage.viewQueue": "Ver cola",
      "callTriage.activeCalls": "Llamadas Activas",
      "callTriage.viewActive": "Ver activas",
      "callTriage.availableAgents": "Agentes Disponibles",
      "callTriage.viewAgents": "Ver agentes",
      "callTriage.callsToday": "Llamadas Hoy",
      "callTriage.viewHistory": "Ver historial",
      "callTriage.myStatus": "Mi Estado",
      "callTriage.online": "En Línea",
      "callTriage.away": "Ausente",
      "callTriage.clickToggle": "Clic para cambiar",
      "callTriage.activeCall": "Llamada Activa",
      "callTriage.patientName": "Nombre del Paciente",
      "callTriage.phone": "Teléfono",
      "callTriage.callNow": "Llamar Ahora",
      "callTriage.reasonForCall": "Razón de la Llamada",
      "callTriage.notes": "Notas",
      "callTriage.transferHistory": "Historial de Transferencias",
      "callTriage.completeCall": "Completar Llamada",
      "callTriage.transfer": "Transferir",
      "callTriage.callQueue": "Cola de Llamadas",
      "callTriage.live": "EN VIVO",
      "callTriage.noCallsWaiting": "No hay llamadas esperando - Cola monitoreada en tiempo real",
      "callTriage.callsWaiting": "{{count}} llamada(s) esperando",
      "callTriage.noCallsInQueue": "No hay llamadas en cola",
      "callTriage.newCallsAppear": "Las nuevas llamadas aparecerán aquí instantáneamente",
      "callTriage.monitoring": "Monitoreando llamadas entrantes...",
      "callTriage.accept": "Aceptar",
      "callTriage.teamStatus": "Estado del Equipo",
      "callTriage.noAgents": "No hay agentes conectados",
      "callTriage.quickActions": "Acciones Rápidas",
      "callTriage.addCallToQueue": "Agregar Llamada a Cola",
      "callTriage.refreshQueue": "Actualizar Cola",
      "callTriage.reconnect": "Reconectar",
      "callTriage.alwaysOnStatus": "Estado Siempre Activo",
      "callTriage.webSocket": "WebSocket",
      "callTriage.disconnected": "Desconectado",
      "callTriage.notifications": "Notificaciones",
      "callTriage.enabled": "Habilitadas",
      "callTriage.disabled": "Deshabilitadas",
      "callTriage.autoReconnect": "Auto-reconexión",
      "callTriage.active": "Activo",
      "callTriage.lastPing": "Último ping",
      "callTriage.triageTips": "Consejos de Triaje",
      "callTriage.tip1": "Las llamadas de emergencia omiten la cola",
      "callTriage.tip2": "Mantenga esta pestaña abierta para alertas instantáneas",
      "callTriage.tip3": "Habilite las notificaciones del navegador",
      "callTriage.tip4": "Establezca estado Ausente durante descansos",
      
      // Physician Portal (Spanish)
      "physician.portal.title": "Portal del Médico",
      "physician.portal.subtitle": "Únase a Goodwin Hospice como Campeón de Hospicio. Acceda a información de pacientes, envíe referencias fácilmente y marque la diferencia en la atención al final de la vida.",
      "physician.portal.championProgram": "Programa Campeón de Hospicio",
      "physician.signIn": "Iniciar Sesión",
      "physician.signIn.title": "Inicio de Sesión del Médico",
      "physician.signIn.description": "Acceda a su panel de pacientes y herramientas de hospicio",
      "physician.register": "Unirse al Programa Campeón",
      "physician.register.title": "Únase al Programa Campeón de Hospicio",
      "physician.register.description": "Registre su práctica para convertirse en un campeón de hospicio y mejorar la atención al final de la vida",
      "physician.email": "Correo Electrónico",
      "physician.password": "Contraseña",
      "physician.forgotPassword": "¿Olvidó su Contraseña?",
      "physician.signingIn": "Iniciando Sesión...",
      "physician.submitting": "Enviando...",
      "physician.benefits.recognition": "Obtenga Reconocimiento",
      "physician.benefits.recognitionDesc": "Niveles de campeón y créditos CME",
      "physician.benefits.insights": "Información de Pacientes con IA",
      "physician.benefits.insightsDesc": "Análisis predictivo de elegibilidad",
      "physician.benefits.referrals": "Referencias Fáciles",
      "physician.benefits.referralsDesc": "Referencias de pacientes con un clic",
      "physician.benefits.support": "Soporte Prioritario",
      "physician.benefits.supportDesc": "Enlace dedicado de hospicio",
      "physician.practice.title": "Información de la Práctica",
      "physician.practice.name": "Nombre de la Práctica",
      "physician.practice.type": "Tipo de Práctica",
      "physician.practice.specialty": "Especialidad",
      "physician.practice.npi": "NPI de la Práctica",
      "physician.address.title": "Dirección de la Práctica",
      "physician.address.street": "Dirección",
      "physician.address.city": "Ciudad",
      "physician.address.state": "Estado",
      "physician.address.zip": "Código Postal",
      "physician.address.phone": "Teléfono",
      "physician.address.fax": "Fax",
      "physician.info.title": "Información del Médico",
      "physician.info.name": "Nombre del Médico",
      "physician.info.npi": "NPI Individual",
      "physician.info.email": "Correo del Médico",
      "physician.info.phone": "Teléfono del Médico",
      "physician.account.title": "Credenciales de la Cuenta",
      "physician.account.email": "Correo de la Cuenta",
      "physician.account.password": "Contraseña",
      "physician.account.confirm": "Confirmar Contraseña",
      "physician.champion.enroll": "Inscribirse en el Programa Campeón de Hospicio",
      "physician.champion.enrollDesc": "Obtenga reconocimiento, créditos CME y soporte prioritario por referencias tempranas de hospicio. Progrese a través de los niveles Bronce, Plata, Oro y Platino.",
      "physician.pending.title": "Registro Pendiente de Aprobación",
      "physician.pending.message": "¡Gracias por unirse a nuestro programa Campeón de Hospicio! Su solicitud está siendo revisada.",
      "physician.pending.notice": "Recibirá un correo electrónico una vez aprobado. Normalmente toma 1-2 días hábiles.",
      "physician.pending.pendingApproval": "Su registro está pendiente de aprobación.",
      "physician.goToLogin": "Ir a Iniciar Sesión",
      "physician.dashboard.title": "Panel del Médico",
      "physician.dashboard.welcome": "Bienvenido",
      "physician.dashboard.addPatient": "Agregar Paciente",
      "physician.dashboard.logout": "Cerrar Sesión",
      "physician.dashboard.loading": "Cargando panel...",
      "physician.dashboard.championProgress": "Progreso de Campeón de Hospicio",
      "physician.dashboard.referrals": "referencias",
      "physician.dashboard.earlyReferrals": "referencias tempranas",
      "physician.dashboard.progressTo": "Progreso hacia",
      "physician.dashboard.referralsNeeded": "referencias necesarias",
      "physician.dashboard.viewBenefits": "Ver Beneficios",
      "physician.dashboard.myPatients": "Mis Pacientes",
      "physician.dashboard.highRisk": "Pacientes de Alto Riesgo",
      "physician.dashboard.pendingOrders": "Órdenes Pendientes",
      "physician.dashboard.totalReferrals": "Total de Referencias",
      "physician.dashboard.criteriaTool": "Herramienta de Criterios de Hospicio",
      "physician.dashboard.criteriaDesc": "Evaluación de elegibilidad con IA",
      "physician.dashboard.goalsOfCare": "Metas de Atención",
      "physician.dashboard.goalsDesc": "Documentar discusiones",
      "physician.dashboard.callHospice": "Llamar a Hospicio",
      "physician.tabs.patients": "Panel de Pacientes",
      "physician.tabs.referrals": "Referencias",
      "physician.tabs.orders": "Órdenes",
      "physician.patients.title": "Panel de Pacientes",
      "physician.patients.description": "Pacientes con información predictiva de hospicio",
      "physician.patients.search": "Buscar pacientes...",
      "physician.patients.noPatients": "Sin Pacientes Aún",
      "physician.patients.noPatientsDesc": "Agregue pacientes para rastrear su elegibilidad de hospicio",
      "physician.patients.addFirst": "Agregar Primer Paciente",
      "physician.patients.patient": "Paciente",
      "physician.patients.diagnosis": "Diagnóstico",
      "physician.patients.status": "Estado",
      "physician.patients.eligibility": "Elegibilidad",
      "physician.patients.alerts": "Alertas",
      "physician.patients.actions": "Acciones",
      "physician.patients.view": "Ver",
      "physician.patients.refer": "Referir",
      "physician.referrals.title": "Historial de Referencias",
      "physician.referrals.description": "Rastree sus referencias de hospicio",
      "physician.referrals.noReferrals": "Sin Referencias Aún",
      "physician.referrals.noReferralsDesc": "Las referencias aparecerán aquí una vez enviadas",
      "physician.orders.title": "Órdenes del Hospicio",
      "physician.orders.description": "Revise y complete órdenes de Goodwin Hospice",
      "physician.orders.noOrders": "Sin Órdenes",
      "physician.orders.noOrdersDesc": "Las órdenes del hospicio aparecerán aquí",
      "physician.orders.acknowledge": "Confirmar",
      "physician.orders.markComplete": "Marcar Completo",
      "physician.orders.viewDetails": "Ver Detalles",
      
      // Facility Portal (Spanish)
      "facility.portal.title": "Portal de Instalaciones Asociadas",
      "facility.portal.subtitle": "Únase a Goodwin Hospice como socio de referencias. Envíe referencias de pacientes de forma segura, acceda a herramientas de elegibilidad de hospicio y conéctese con nuestro equipo de atención.",
      "facility.portal.partnerPortal": "Portal de Socios de Salud",
      "facility.signIn": "Iniciar Sesión",
      "facility.signIn.title": "Inicio de Sesión de Instalación",
      "facility.signIn.description": "Acceda a su panel de instalaciones para gestionar referencias y usar herramientas de hospicio",
      "facility.register": "Registrar Su Instalación",
      "facility.register.title": "Registrar Su Instalación",
      "facility.register.description": "Complete el formulario a continuación para convertirse en socio de referencias de Goodwin Hospice. Todos los registros requieren aprobación administrativa.",
      "facility.email": "Correo Electrónico",
      "facility.password": "Contraseña",
      "facility.forgotPassword": "¿Olvidó su Contraseña?",
      "facility.signingIn": "Iniciando Sesión...",
      "facility.submitting": "Enviando...",
      "facility.submitRegistration": "Enviar Registro",
      "facility.info.title": "Información de la Instalación",
      "facility.info.name": "Nombre de la Instalación",
      "facility.info.type": "Tipo de Instalación",
      "facility.info.npi": "Número NPI",
      "facility.info.taxId": "ID Fiscal",
      "facility.address.title": "Dirección",
      "facility.address.street": "Dirección",
      "facility.address.city": "Ciudad",
      "facility.address.state": "Estado",
      "facility.address.zip": "Código Postal",
      "facility.address.phone": "Teléfono",
      "facility.address.fax": "Fax",
      "facility.address.website": "Sitio Web",
      "facility.contact.title": "Contacto Principal",
      "facility.contact.name": "Nombre del Contacto",
      "facility.contact.titleField": "Título",
      "facility.contact.email": "Correo del Contacto",
      "facility.contact.phone": "Teléfono del Contacto",
      "facility.account.title": "Credenciales de la Cuenta",
      "facility.account.email": "Correo de la Cuenta",
      "facility.account.password": "Contraseña (mín 8 caracteres)",
      "facility.account.confirm": "Confirmar Contraseña",
      "facility.certifications.title": "Certificaciones",
      "facility.certifications.medicare": "Certificado Medicare",
      "facility.certifications.medicaid": "Certificado Medicaid",
      "facility.certifications.jointCommission": "Acreditado por Joint Commission",
      "facility.pending.title": "Registro Pendiente de Aprobación",
      "facility.pending.message": "Gracias por registrarse como instalación asociada de Goodwin Hospice. Su solicitud está siendo revisada por nuestro equipo administrativo.",
      "facility.pending.notice": "Recibirá una notificación por correo electrónico una vez que su cuenta sea aprobada. Esto normalmente toma 1-2 días hábiles.",
      "facility.pending.pendingApproval": "El registro de su instalación está pendiente de aprobación administrativa. Se le notificará cuando sea aprobado.",
      "facility.goToLogin": "Ir a Iniciar Sesión",
      "facility.dashboard.title": "Panel de la Instalación",
      "facility.dashboard.welcome": "Bienvenido de nuevo",
      "facility.dashboard.logout": "Cerrar Sesión",
      "facility.dashboard.loading": "Cargando panel...",
      "facility.dashboard.totalReferrals": "Total de Referencias",
      "facility.dashboard.pendingReview": "Pendiente de Revisión",
      "facility.dashboard.accepted": "Aceptadas",
      "facility.dashboard.thisMonth": "Este Mes",
      "facility.dashboard.newReferral": "Nueva Referencia de Paciente",
      "facility.dashboard.newReferralDesc": "Envíe una referencia de paciente segura",
      "facility.dashboard.criteriaTool": "Herramienta de Criterios de Hospicio",
      "facility.dashboard.criteriaDesc": "Evaluación de elegibilidad con IA",
      "facility.dashboard.callReferral": "Llamar a Línea de Referencias",
      "facility.dashboard.callReferralDesc": "703-578-7108 (24/7)",
      "facility.dashboard.recentReferrals": "Referencias Recientes",
      "facility.dashboard.recentReferralsDesc": "Rastree sus envíos de referencias de pacientes",
      "facility.dashboard.viewAll": "Ver Todo",
      "facility.dashboard.noReferrals": "Sin Referencias Aún",
      "facility.dashboard.noReferralsDesc": "Envíe su primera referencia de paciente para comenzar",
      "facility.dashboard.newReferralBtn": "Nueva Referencia",
      "facility.referrals.patient": "Paciente",
      "facility.referrals.diagnosis": "Diagnóstico",
      "facility.referrals.urgency": "Urgencia",
      "facility.referrals.status": "Estado",
      "facility.referrals.date": "Fecha",
      "facility.referrals.view": "Ver",
      "facility.status.submitted": "Enviada",
      "facility.status.underReview": "En Revisión",
      "facility.status.accepted": "Aceptada",
      "facility.status.declined": "Rechazada",
      
      // Family Hub (Spanish)
      "familyHub.title": "Centro de Atención Familiar",
      "familyHub.subtitle": "Su centro de atención • Estamos aquí para usted",
      "familyHub.loading": "Cargando su panel...",
      
      // 404 Not Found (Spanish)
      "notFound.title": "Página No Encontrada",
      "notFound.description": "Lo sentimos, la página que busca no existe o ha sido movida.",
      "notFound.goHome": "Ir al Inicio",
      "notFound.contactUs": "Contáctenos",
      "notFound.goBack": "Volver a la página anterior"
    }
  },
  zh: {
    translation: {
      // Common
      "app.name": "Goodwin Cares",
      "app.tagline": "关怀临终护理支持",
      "common.loading": "加载中...",
      "common.save": "保存",
      "common.cancel": "取消",
      "common.delete": "删除",
      "common.edit": "编辑",
      "common.close": "关闭",
      "common.submit": "提交",
      "common.back": "返回",
      "common.next": "下一步",
      "common.yes": "是",
      "common.no": "否",
      "common.search": "搜索",
      "common.refresh": "刷新",
      
      // Navigation
      "nav.home": "首页",
      "nav.dashboard": "仪表板",
      "nav.chat": "询问AI",
      "nav.symptoms": "症状",
      "nav.journal": "日记",
      "nav.medications": "药物",
      "nav.calendar": "日历",
      "nav.resources": "资源",
      "nav.signIn": "登录",
      "nav.signOut": "退出",
      
      // Demo Page - Interactive Tour (Chinese)
      "demo.welcome.title": "欢迎来到Goodwin Cares",
      "demo.welcome.subtitle": "以科技驱动的关怀护理",
      "demo.welcome.description": "一个综合性的临终关怀和护理支持平台，将人工智能与人性化关怀相结合，提供最佳护理体验。",
      "demo.welcome.patientCare": "以患者为中心的护理",
      "demo.welcome.hipaa": "符合HIPAA标准",
      "demo.welcome.aiInsights": "AI驱动的洞察",
      "demo.welcome.familyEngagement": "家庭参与",
      "demo.patientCare.title": "全面的患者护理",
      "demo.patientCare.subtitle": "家庭所需尽在一处",
      "demo.patientCare.description": "患者和护理人员可以通过我们直观的仪表板跟踪症状、药物并直接与护理团队沟通。",
      "demo.patientCare.checkins": "每日签到",
      "demo.patientCare.checkinsDesc": "追踪疼痛、心情和症状",
      "demo.patientCare.medications": "药物提醒",
      "demo.patientCare.medicationsDesc": "不再错过任何剂量",
      "demo.patientCare.messaging": "直接消息",
      "demo.patientCare.messagingDesc": "与护理团队聊天",
      "demo.patientCare.symptoms": "症状追踪",
      "demo.patientCare.symptomsDesc": "可视化健康趋势",
      "demo.communication.title": "实时通讯",
      "demo.communication.subtitle": "与护理团队保持联系",
      "demo.communication.description": "安全消息、视频通话和即时通知使每个人都保持知情和联系，减少焦虑并改善护理协调。",
      "demo.communication.telehealth": "远程医疗访问",
      "demo.communication.telehealthDesc": "在家面对面",
      "demo.communication.secureMsg": "安全消息",
      "demo.communication.secureMsgDesc": "符合HIPAA的聊天",
      "demo.communication.notifications": "推送通知",
      "demo.communication.notificationsDesc": "实时警报",
      "demo.communication.triage": "电话分诊",
      "demo.communication.triageDesc": "24/7紧急支持",
      "demo.ai.title": "AI驱动的智能",
      "demo.ai.subtitle": "智能功能带来改变",
      "demo.ai.description": "我们的AI分析患者数据以提供可操作的洞察、预测需求，并帮助员工提供主动、个性化的护理。",
      "demo.ai.chatAssistant": "AI聊天助手",
      "demo.ai.chatAssistantDesc": "获取即时答案",
      "demo.ai.suggestions": "智能建议",
      "demo.ai.suggestionsDesc": "AI消息回复",
      "demo.ai.predictive": "预测警报",
      "demo.ai.predictiveDesc": "预警系统",
      "demo.ai.autoDocs": "自动文档",
      "demo.ai.autoDocsDesc": "简化的笔记",
      "demo.scheduling.title": "智能排程",
      "demo.scheduling.subtitle": "优化护理交付",
      "demo.scheduling.description": "AI优化的访问排程确保合适的员工在合适的时间到达合适的患者，具有地理聚类和基于优先级的路由。",
      "demo.scheduling.calendar": "日历视图",
      "demo.scheduling.calendarDesc": "可视化排程管理",
      "demo.scheduling.geo": "地理优化",
      "demo.scheduling.geoDesc": "高效路由",
      "demo.scheduling.aiSuggestions": "AI建议",
      "demo.scheduling.aiSuggestionsDesc": "最佳时间段",
      "demo.scheduling.staff": "员工管理",
      "demo.scheduling.staffDesc": "可用性和休假",
      "demo.training.title": "临终关怀学院",
      "demo.training.subtitle": "持续学习追求卓越",
      "demo.training.description": "与ELNEC对齐的综合培训平台，提供交互式课程、测验和所有学科的CEU证书生成。",
      "demo.training.courses": "56+课程",
      "demo.training.coursesDesc": "11个培训轨道",
      "demo.training.ceu": "CEU证书",
      "demo.training.ceuDesc": "可下载证书",
      "demo.training.badges": "成就徽章",
      "demo.training.badgesDesc": "游戏化学习",
      "demo.training.progress": "进度追踪",
      "demo.training.progressDesc": "个人和团队",
      "demo.analytics.title": "分析与洞察",
      "demo.analytics.subtitle": "数据驱动的护理改进",
      "demo.analytics.description": "用于员工绩效、家庭参与、CAHPS评分预测和运营指标的综合仪表板，持续提高护理质量。",
      "demo.analytics.performance": "员工绩效",
      "demo.analytics.performanceDesc": "响应时间和结果",
      "demo.analytics.engagement": "家庭参与",
      "demo.analytics.engagementDesc": "互动指标",
      "demo.analytics.cahps": "CAHPS预测器",
      "demo.analytics.cahpsDesc": "质量评分",
      "demo.analytics.clinical": "临床洞察",
      "demo.analytics.clinicalDesc": "护理模式",
      "demo.getStarted.title": "准备好转变护理了吗？",
      "demo.getStarted.subtitle": "加入Goodwin Cares大家庭",
      "demo.getStarted.description": "无论您是患者、护理人员还是医疗专业人员，Goodwin Cares提供您所需的工具来提供和接受卓越的临终关怀。",
      "demo.getStarted.forPatients": "对于患者",
      "demo.getStarted.forPatientsDesc": "注册护理支持",
      "demo.getStarted.forProviders": "对于提供者",
      "demo.getStarted.forProvidersDesc": "加入我们的护理网络",
      "demo.getStarted.forPartners": "对于合作伙伴",
      "demo.getStarted.forPartnersDesc": "医疗保健整合",
      "demo.getStarted.forFamilies": "对于家庭",
      "demo.getStarted.forFamiliesDesc": "护理人员资源",
      
      // Dashboard
      "dashboard.greeting.morning": "早上好",
      "dashboard.greeting.afternoon": "下午好",
      "dashboard.greeting.evening": "晚上好",
      "dashboard.checkin": "签到",
      "dashboard.checkedIn": "已签到",
      "dashboard.streak": "天连续",
      "dashboard.medications": "药物",
      "dashboard.upcoming": "即将到来",
      "dashboard.careTeam": "护理团队",
      "dashboard.quickActions": "快速操作",
      "dashboard.askAI": "询问AI",
      "dashboard.insights": "护理洞察",
      "dashboard.manual": "手动",
      "dashboard.voice": "语音",
      
      // Check-in
      "checkin.title": "每日签到",
      "checkin.overall": "您今天感觉如何？",
      "checkin.pain": "疼痛程度",
      "checkin.sleep": "睡眠质量",
      "checkin.appetite": "食欲",
      "checkin.mood": "当前心情",
      "checkin.symptoms": "今天有什么症状吗？",
      "checkin.notes": "有什么要记录的吗？",
      "checkin.complete": "完成签到",
      
      // Moods
      "mood.peaceful": "平静",
      "mood.happy": "开心",
      "mood.okay": "还好",
      "mood.tired": "疲倦",
      "mood.anxious": "焦虑",
      "mood.sad": "悲伤",
      
      // Medications
      "medications.title": "药物管理",
      "medications.add": "添加药物",
      "medications.markTaken": "标记已服用",
      "medications.taken": "已服用",
      
      // Emergency
      "emergency.title": "紧急联系人",
      "emergency.hospice": "Goodwin临终关怀",
      "emergency.nurseLine": "24小时护士热线",
      "emergency.call911": "呼叫911",
      
      // Voice
      "voice.listening": "正在听...",
      "voice.speak": "说话",
      "voice.stop": "停止",
      
      // Auth
      "auth.signIn": "登录",
      "auth.signUp": "注册",
      "auth.email": "电子邮件",
      "auth.password": "密码",
      "auth.welcomeBack": "欢迎回来！",
      
      // Family Portal
      "familyPortal.title": "家庭门户",
      "familyPortal.subtitle": "与您亲人的护理团队保持联系",
      "familyPortal.newNotifications": "新通知",
      "familyPortal.enhancedCare": "增强舒适护理",
      "familyPortal.enhancedCareDesc": "我们的护理团队正在提供以舒适为重点的增强护理。在此期间，我们将为您的亲人和家人提供支持。",
      "familyPortal.nurseLineLabel": "24/7护士热线",
      "familyPortal.messageTeam": "联系团队",
      "familyPortal.callNurse": "呼叫护士",
      "familyPortal.requestChaplain": "请求牧师",
      "familyPortal.scheduleVideoCall": "预约视频通话",
      "familyPortal.overview": "概览",
      "familyPortal.updates": "更新",
      "familyPortal.resources": "资源",
      "familyPortal.careTimeline": "护理时间线",
      "familyPortal.yourCareTeam": "您的护理团队",
      "familyPortal.primaryNurse": "主护士",
      "familyPortal.attendingPhysician": "主治医生",
      "familyPortal.socialWorker": "社工",
      "familyPortal.upcomingVisits": "即将到来的访问",
      "familyPortal.emergencyLine": "24/7紧急热线",
      "familyPortal.callNow": "立即呼叫",
      
      // Resources Page
      "resources.title": "社区",
      "resources.titleHighlight": "资源",
      "resources.description": "北弗吉尼亚地区临终关怀家庭的本地支持服务。",
      "resources.searchPlaceholder": "搜索资源...",
      "resources.allResources": "所有资源",
      "resources.noResults": "未找到符合条件的资源。",
      "resources.needHelp": "需要帮助寻找资源？",
      "resources.chatWithAI": "与AI助手聊天",
      
      // Staff Roster
      "staffRoster.title": "员工名册",
      "staffRoster.subtitle": "系统中所有已登录的员工",
      "staffRoster.loading": "正在加载员工名册...",
      "staffRoster.totalStaff": "员工总数",
      "staffRoster.searchPlaceholder": "按姓名、电子邮件或专业搜索...",
      "staffRoster.allRoles": "所有角色",
      "staffRoster.allStatus": "所有状态",
      "staffRoster.clearFilters": "清除筛选",
      "staffRoster.staffMembers": "员工成员",
      "staffRoster.noResults": "未找到符合筛选条件的员工",
      "staffRoster.roles.admin": "管理员",
      "staffRoster.roles.nurse": "护士",
      "staffRoster.roles.physician": "医生",
      "staffRoster.status.active": "活跃",
      "staffRoster.status.inactive": "非活跃",
      "staffRoster.status.onLeave": "休假中",
      
      // Language Tour
      "languageTour.title": "语言已更改！",
      "languageTour.welcome": "欢迎",
      "languageTour.keyPhrases": "您语言中的关键短语：",
      "languageTour.home": "首页",
      "languageTour.help": "获取帮助",
      "languageTour.messages": "消息",
      "languageTour.profile": "个人资料",
      "languageTour.gotIt": "知道了！",
      
      // Voice Chat
      "chat.recordingStarted": "录音已开始 - 请说话",
      "chat.microphoneDenied": "麦克风访问被拒绝。请启用麦克风权限。",
      "chat.transcriptionComplete": "转录完成",
      "chat.noSpeechDetected": "未检测到语音。请重试。",
      "chat.transcriptionFailed": "音频转录失败。请重试。",
      "chat.recording": "录音中",
      "chat.transcribing": "转录中...",
      "chat.speaking": "朗读中...",
      "chat.ttsDisabled": "语音响应已禁用",
      "chat.ttsEnabled": "语音响应已启用",
      "chat.ttsOn": "语音开",
      "chat.ttsOff": "语音关",
      "chat.speakNow": "请说话...",
      "chat.voiceCommand.cleared": "聊天已通过语音命令清除",
      "chat.voiceCommand.newStarted": "已通过语音命令开始新对话",
      "chat.voiceCommand.stopped": "语音已通过命令停止",
      "chat.voiceCommand.hint": "尝试说：\"清除聊天\"、\"新对话\"或\"停止说话\"",
      
      // Staff Portal
      "staffPortal.title": "员工门户",
      "staffPortal.subtitle": "管理您的病例和通讯",
      "staffPortal.tabs.caseload": "我的病例",
      "staffPortal.tabs.messages": "消息",
      "staffPortal.tabs.alerts": "临床警报",
      "staffPortal.caseload.search": "搜索患者...",
      "staffPortal.caseload.assignPatient": "分配患者",
      "staffPortal.caseload.noPatients": "您的病例中没有患者",
      "staffPortal.messages.newConversation": "新对话",
      "staffPortal.alerts.noAlerts": "没有活动警报",
      
      // Contact Page
      "contact.caption": "联系我们",
      "contact.title": "我们在这里帮助您",
      "contact.description": "对临终关怀有疑问、需要支持或想了解更多关于我们服务的信息？随时与我们联系。我们在这里为您服务。",
      "contact.phone": "电话",
      "contact.available247": "全天候服务",
      "contact.email": "电子邮件",
      "contact.responseTime": "我们在24小时内回复",
      "contact.hours": "营业时间",
      "contact.24hours": "每天24小时",
      "contact.7days": "每周7天",
      "contact.sendMessage": "给我们发送消息",
      "contact.thankYou": "谢谢您",
      "contact.fullName": "全名",
      "contact.subject": "主题",
      "contact.message": "消息",
      "contact.sending": "发送中...",
      "contact.sendMessageBtn": "发送消息",
      
      // Translation Dashboard
      "translationDashboard.title": "翻译进度仪表板",
      "translationDashboard.subtitle": "跟踪所有页面和语言的国际化覆盖",
      "translationDashboard.coverage": "覆盖率",
      "translationDashboard.keysTranslated": "已翻译",
      "translationDashboard.pages": "页面",
      "translationDashboard.totalPages": "总页面/组件",
      "translationDashboard.languagesComplete": "90%+完成的语言",
      "translationDashboard.missingKeys": "缺失键",
      "translationDashboard.copyMissing": "复制缺失",
      
      // Admin Dashboard
      "adminDashboard.title": "管理仪表板",
      "adminDashboard.subtitle": "监控网站分析、聊天使用和联系消息",
      "adminDashboard.refreshData": "刷新数据",
      "adminDashboard.accessRequired": "需要管理员权限",
      "adminDashboard.stats.totalVisits": "总访问量",
      "adminDashboard.stats.chatSessions": "聊天会话",
      "adminDashboard.stats.totalMessages": "总消息",
      "adminDashboard.stats.uniqueUsers": "独立用户",
      
      // Video Library (Chinese)
      "videoLibrary.backToHome": "返回首页",
      "videoLibrary.title": "视频库",
      "videoLibrary.description": "探索我们完整功能演示的收藏。每个视频提供完整、流畅的体验，展示Goodwin Cares的功能。",
      "videoLibrary.fullVideos": "{{count}} 完整视频",
      "videoLibrary.takeInteractiveTour": "参加互动导览",
      "videoLibrary.searchPlaceholder": "搜索视频...",
      "videoLibrary.fullLengthTitle": "完整功能视频",
      "videoLibrary.fullLengthDescription": "每个视频无缝播放 - 品牌介绍、完整功能展示和专业结尾。在约36秒内体验每个功能的完整故事。",
      "videoLibrary.allFeatures": "所有功能",
      "videoLibrary.noVideosFound": "未找到视频",
      "videoLibrary.tryAdjusting": "尝试调整您的搜索或筛选条件",
      "videoLibrary.cta.title": "准备体验关怀护理吗？",
      "videoLibrary.cta.description": "加入数千个信任Goodwin Cares的家庭和医疗专业人员。",
      "videoLibrary.cta.getStarted": "开始使用",
      "videoLibrary.cta.takeTour": "参加导览",
      "videoLibrary.category.all": "全部",
      "videoLibrary.category.overview": "概述",
      "videoLibrary.category.communication": "沟通",
      "videoLibrary.category.scheduling": "日程安排",
      "videoLibrary.category.clinical": "临床",
      "videoLibrary.category.training": "培训",
      "videoLibrary.category.analytics": "分析",
      
      // Programs Page (Chinese)
      "programs.hero.badge": "超越传统护理",
      "programs.hero.title": "补充",
      "programs.hero.titleHighlight": "项目",
      "programs.benefits": "益处",
      "programs.servicesWeHelp": "我们帮助的服务",
      "programs.call": "致电",
      "programs.visitWebsite": "访问网站",
      "programs.cta.title": "对我们的项目有疑问吗？",
      "programs.cta.contactOnline": "在线联系我们",
      "programs.massage.title": "临终按摩疗法",
      "programs.doula.title": "临终陪伴服务",
      "programs.aftercare.title": "全程后续护理",
      
      // Moods (Chinese)
      "moods.happy": "开心",
      "moods.calm": "平静",
      "moods.grateful": "感恩",
      "moods.hopeful": "充满希望",
      "moods.content": "满足",
      "moods.anxious": "焦虑",
      "moods.sad": "悲伤",
      "moods.frustrated": "沮丧",
      "moods.tired": "疲惫",
      "moods.scared": "害怕",
      "moods.angry": "愤怒"
    }
  },
  vi: {
    translation: {
      // Common
      "app.name": "Goodwin Cares",
      "app.tagline": "Hỗ Trợ Chăm Sóc Cuối Đời",
      "common.loading": "Đang tải...",
      "common.save": "Lưu",
      "common.cancel": "Hủy",
      "common.close": "Đóng",
      "common.submit": "Gửi",
      "common.yes": "Có",
      "common.no": "Không",
      "common.refresh": "Làm mới",
      
      // Navigation
      "nav.home": "Trang chủ",
      "nav.dashboard": "Bảng điều khiển",
      "nav.chat": "Hỏi AI",
      "nav.symptoms": "Triệu chứng",
      "nav.medications": "Thuốc",
      "nav.calendar": "Lịch",
      "nav.signIn": "Đăng nhập",
      "nav.signOut": "Đăng xuất",
      
      // Dashboard
      "dashboard.greeting.morning": "Chào buổi sáng",
      "dashboard.greeting.afternoon": "Chào buổi chiều",
      "dashboard.greeting.evening": "Chào buổi tối",
      "dashboard.checkin": "Đăng ký",
      "dashboard.checkedIn": "Đã đăng ký",
      "dashboard.medications": "Thuốc",
      "dashboard.careTeam": "Đội ngũ chăm sóc",
      "dashboard.manual": "Thủ công",
      "dashboard.voice": "Giọng nói",
      
      // Check-in
      "checkin.title": "Đăng ký hàng ngày",
      "checkin.overall": "Hôm nay bạn cảm thấy thế nào?",
      "checkin.pain": "Mức độ đau",
      "checkin.sleep": "Chất lượng giấc ngủ",
      "checkin.mood": "Tâm trạng",
      "checkin.complete": "Hoàn thành đăng ký",
      
      // Moods
      "mood.peaceful": "Bình yên",
      "mood.happy": "Vui vẻ",
      "mood.okay": "Bình thường",
      "mood.tired": "Mệt mỏi",
      "mood.anxious": "Lo lắng",
      "mood.sad": "Buồn",
      
      // Emergency
      "emergency.title": "Liên hệ khẩn cấp",
      "emergency.nurseLine": "Đường dây y tá 24/7",
      "emergency.call911": "Gọi 911",
      
      // Auth
      "auth.signIn": "Đăng nhập",
      "auth.signUp": "Đăng ký",
      "auth.welcomeBack": "Chào mừng trở lại!",
      
      // Family Portal
      "familyPortal.title": "Cổng Gia Đình",
      "familyPortal.subtitle": "Kết nối với đội ngũ chăm sóc người thân của bạn",
      "familyPortal.newNotifications": "thông báo mới",
      "familyPortal.messageTeam": "Nhắn Tin Đội Ngũ",
      "familyPortal.callNurse": "Gọi Y Tá",
      "familyPortal.requestChaplain": "Yêu Cầu Tuyên Úy",
      "familyPortal.scheduleVideoCall": "Đặt Lịch Gọi Video",
      "familyPortal.overview": "Tổng Quan",
      "familyPortal.updates": "Cập Nhật",
      "familyPortal.resources": "Tài Nguyên",
      "familyPortal.careTimeline": "Dòng Thời Gian Chăm Sóc",
      "familyPortal.emergencyLine": "Đường Dây Khẩn Cấp 24/7",
      "familyPortal.callNow": "Gọi Ngay",
      
      // Resources Page
      "resources.title": "Cộng Đồng",
      "resources.titleHighlight": "Tài Nguyên",
      "resources.searchPlaceholder": "Tìm kiếm tài nguyên...",
      "resources.allResources": "Tất Cả Tài Nguyên",
      "resources.noResults": "Không tìm thấy tài nguyên phù hợp.",
      "resources.needHelp": "Cần Giúp Tìm Tài Nguyên?",
      "resources.chatWithAI": "Trò Chuyện Với Trợ Lý AI",
      
      // Staff Roster
      "staffRoster.title": "Danh Sách Nhân Viên",
      "staffRoster.subtitle": "Tất cả nhân viên đã đăng ký trong hệ thống",
      "staffRoster.loading": "Đang tải Danh Sách Nhân Viên...",
      "staffRoster.totalStaff": "Tổng Nhân Viên",
      "staffRoster.allRoles": "Tất Cả Vai Trò",
      "staffRoster.allStatus": "Tất Cả Trạng Thái",
      "staffRoster.clearFilters": "Xóa Bộ Lọc",
      "staffRoster.staffMembers": "Thành Viên Nhân Viên",
      "staffRoster.noResults": "Không tìm thấy nhân viên phù hợp với bộ lọc",
      "staffRoster.status.active": "Hoạt Động",
      "staffRoster.status.inactive": "Không Hoạt Động",
      "staffRoster.status.onLeave": "Nghỉ Phép",
      
      // Language Tour
      "languageTour.title": "Đã Đổi Ngôn Ngữ!",
      "languageTour.welcome": "Chào mừng",
      "languageTour.keyPhrases": "Các cụm từ chính bằng ngôn ngữ của bạn:",
      "languageTour.home": "Trang Chủ",
      "languageTour.help": "Nhận Trợ Giúp",
      "languageTour.messages": "Tin Nhắn",
      "languageTour.profile": "Hồ Sơ",
      "languageTour.gotIt": "Đã hiểu!",
      
      // Voice Chat
      "chat.recordingStarted": "Bắt đầu ghi âm - hãy nói",
      "chat.microphoneDenied": "Quyền truy cập micro bị từ chối. Vui lòng bật quyền micro.",
      "chat.transcriptionComplete": "Chuyển đổi hoàn tất",
      "chat.noSpeechDetected": "Không phát hiện giọng nói. Vui lòng thử lại.",
      "chat.transcriptionFailed": "Chuyển đổi âm thanh thất bại. Vui lòng thử lại.",
      "chat.recording": "Đang ghi",
      "chat.transcribing": "Đang chuyển đổi...",
      "chat.speaking": "Đang nói...",
      "chat.ttsDisabled": "Phản hồi giọng nói đã tắt",
      "chat.ttsEnabled": "Phản hồi giọng nói đã bật",
      "chat.ttsOn": "Giọng Bật",
      "chat.ttsOff": "Giọng Tắt",
      "chat.speakNow": "Hãy nói...",
      "chat.voiceCommand.cleared": "Đã xóa chat bằng lệnh giọng nói",
      "chat.voiceCommand.newStarted": "Đã bắt đầu cuộc trò chuyện mới bằng lệnh giọng nói",
      "chat.voiceCommand.stopped": "Đã dừng giọng nói bằng lệnh",
      "chat.voiceCommand.hint": "Thử nói: \"xóa chat\", \"cuộc trò chuyện mới\", hoặc \"ngừng nói\"",
      
      // Staff Portal
      "staffPortal.title": "Cổng Nhân Viên",
      "staffPortal.subtitle": "Quản lý bệnh nhân và liên lạc",
      "staffPortal.tabs.caseload": "Bệnh Nhân Của Tôi",
      "staffPortal.tabs.messages": "Tin Nhắn",
      "staffPortal.tabs.alerts": "Cảnh Báo Lâm Sàng",
      "staffPortal.caseload.search": "Tìm kiếm bệnh nhân...",
      "staffPortal.caseload.assignPatient": "Giao Bệnh Nhân",
      "staffPortal.caseload.noPatients": "Không có bệnh nhân trong danh sách",
      "staffPortal.messages.newConversation": "Cuộc Trò Chuyện Mới",
      "staffPortal.alerts.noAlerts": "Không có cảnh báo",
      
      // Contact Page
      "contact.caption": "Liên Hệ",
      "contact.title": "Chúng Tôi Ở Đây Để Giúp Đỡ",
      "contact.description": "Có câu hỏi về chăm sóc cuối đời, cần hỗ trợ, hoặc muốn tìm hiểu thêm về dịch vụ của chúng tôi? Hãy liên hệ với chúng tôi bất cứ lúc nào.",
      "contact.phone": "Điện Thoại",
      "contact.available247": "Phục vụ 24/7",
      "contact.email": "Email",
      "contact.responseTime": "Chúng tôi phản hồi trong vòng 24 giờ",
      "contact.hours": "Giờ Làm Việc",
      "contact.24hours": "24 giờ mỗi ngày",
      "contact.7days": "7 ngày mỗi tuần",
      "contact.sendMessage": "Gửi Tin Nhắn Cho Chúng Tôi",
      "contact.thankYou": "Cảm Ơn Bạn",
      "contact.fullName": "Họ Tên",
      "contact.subject": "Chủ Đề",
      "contact.message": "Tin Nhắn",
      "contact.sending": "Đang gửi...",
      "contact.sendMessageBtn": "Gửi Tin Nhắn",
      
      // Translation Dashboard
      "translationDashboard.title": "Bảng Tiến Độ Dịch Thuật",
      "translationDashboard.subtitle": "Theo dõi phạm vi quốc tế hóa trên tất cả trang và ngôn ngữ",
      "translationDashboard.coverage": "Phạm Vi",
      "translationDashboard.keysTranslated": "khóa đã dịch",
      "translationDashboard.pages": "trang",
      "translationDashboard.totalPages": "Tổng Số Trang/Thành Phần",
      "translationDashboard.languagesComplete": "Ngôn Ngữ Hoàn Thành 90%+",
      "translationDashboard.missingKeys": "Khóa Thiếu",
      "translationDashboard.copyMissing": "Sao Chép Thiếu",
      
      // Admin Dashboard
      "adminDashboard.title": "Bảng Điều Khiển Quản Trị",
      "adminDashboard.subtitle": "Giám sát phân tích trang web, sử dụng chat và tin nhắn liên hệ",
      "adminDashboard.refreshData": "Làm Mới Dữ Liệu",
      "adminDashboard.accessRequired": "Cần Quyền Quản Trị",
      "adminDashboard.stats.totalVisits": "Tổng Lượt Truy Cập",
      "adminDashboard.stats.chatSessions": "Phiên Chat",
      
      // Video Library (Vietnamese)
      "videoLibrary.backToHome": "Quay lại Trang chủ",
      "videoLibrary.title": "Thư viện Video",
      "videoLibrary.description": "Khám phá bộ sưu tập các bản demo đầy đủ của chúng tôi. Mỗi video cung cấp trải nghiệm hoàn chỉnh, liền mạch thể hiện khả năng của Goodwin Cares.",
      "videoLibrary.fullVideos": "{{count}} Video Đầy đủ",
      "videoLibrary.takeInteractiveTour": "Tham gia Tour Tương tác",
      "videoLibrary.searchPlaceholder": "Tìm kiếm video...",
      "videoLibrary.allFeatures": "Tất cả Tính năng",
      "videoLibrary.noVideosFound": "Không tìm thấy video",
      "videoLibrary.cta.title": "Sẵn sàng Trải nghiệm Chăm sóc Chu đáo?",
      "videoLibrary.cta.getStarted": "Bắt đầu",
      "videoLibrary.cta.takeTour": "Tham gia Tour",
      
      // Programs Page (Vietnamese)
      "programs.hero.badge": "Vượt xa Chăm sóc Truyền thống",
      "programs.benefits": "Lợi ích",
      "programs.call": "Gọi",
      "programs.visitWebsite": "Truy cập Website",
      "programs.cta.title": "Có Câu hỏi về Chương trình của Chúng tôi?",
      "programs.cta.contactOnline": "Liên hệ Trực tuyến",
      "programs.massage.title": "Liệu pháp Massage Cuối đời",
      "programs.doula.title": "Dịch vụ Doula Cuối đời",
      "programs.aftercare.title": "Chăm sóc Hậu sự Toàn diện",
      
      // Moods (Vietnamese)
      "moods.happy": "Vui vẻ",
      "moods.calm": "Bình tĩnh",
      "moods.grateful": "Biết ơn",
      "moods.hopeful": "Hy vọng",
      "moods.content": "Hài lòng",
      "moods.anxious": "Lo lắng",
      "moods.sad": "Buồn",
      "moods.frustrated": "Thất vọng",
      "moods.tired": "Mệt mỏi",
      "moods.scared": "Sợ hãi",
      "moods.angry": "Tức giận",
      
      // Demo Page - Interactive Tour (Vietnamese)
      "demo.welcome.title": "Chào Mừng Đến Với Goodwin Cares",
      "demo.welcome.subtitle": "Chăm Sóc Từ Bi, Được Hỗ Trợ Bởi Công Nghệ",
      "demo.welcome.description": "Một nền tảng hỗ trợ chăm sóc cuối đời và người chăm sóc toàn diện kết hợp trí tuệ AI với lòng trắc ẩn của con người để mang lại trải nghiệm chăm sóc tốt nhất.",
      "demo.welcome.patientCare": "Chăm Sóc Lấy Bệnh Nhân Làm Trung Tâm",
      "demo.welcome.hipaa": "Tuân Thủ HIPAA",
      "demo.welcome.aiInsights": "Phân Tích AI",
      "demo.welcome.familyEngagement": "Gắn Kết Gia Đình",
      "demo.patientCare.title": "Chăm Sóc Bệnh Nhân Toàn Diện",
      "demo.patientCare.subtitle": "Tất cả những gì gia đình cần ở một nơi",
      "demo.patientCare.description": "Bệnh nhân và người chăm sóc có thể theo dõi triệu chứng, thuốc và liên lạc trực tiếp với đội ngũ chăm sóc thông qua bảng điều khiển trực quan.",
      "demo.patientCare.checkins": "Đăng Ký Hàng Ngày",
      "demo.patientCare.checkinsDesc": "Theo dõi đau, tâm trạng và triệu chứng",
      "demo.patientCare.medications": "Nhắc Nhở Thuốc",
      "demo.patientCare.medicationsDesc": "Không bao giờ bỏ lỡ một liều",
      "demo.patientCare.messaging": "Nhắn Tin Trực Tiếp",
      "demo.patientCare.messagingDesc": "Trò chuyện với đội ngũ chăm sóc",
      "demo.patientCare.symptoms": "Theo Dõi Triệu Chứng",
      "demo.patientCare.symptomsDesc": "Xu hướng sức khỏe trực quan",
      "demo.communication.title": "Giao Tiếp Thời Gian Thực",
      "demo.communication.subtitle": "Kết nối với đội ngũ chăm sóc của bạn",
      "demo.communication.description": "Tin nhắn an toàn, cuộc gọi video và thông báo tức thì giữ cho mọi người được thông báo và kết nối.",
      "demo.communication.telehealth": "Khám Từ Xa",
      "demo.communication.telehealthDesc": "Gặp mặt từ nhà",
      "demo.communication.secureMsg": "Tin Nhắn An Toàn",
      "demo.communication.secureMsgDesc": "Chat tuân thủ HIPAA",
      "demo.communication.notifications": "Thông Báo Đẩy",
      "demo.communication.notificationsDesc": "Cảnh báo thời gian thực",
      "demo.communication.triage": "Phân Loại Cuộc Gọi",
      "demo.communication.triageDesc": "Hỗ trợ khẩn cấp 24/7",
      "demo.ai.title": "Trí Tuệ AI",
      "demo.ai.subtitle": "Các tính năng thông minh tạo nên sự khác biệt",
      "demo.ai.description": "AI của chúng tôi phân tích dữ liệu bệnh nhân để cung cấp thông tin hữu ích và giúp nhân viên chăm sóc chủ động.",
      "demo.ai.chatAssistant": "Trợ Lý Chat AI",
      "demo.ai.chatAssistantDesc": "Nhận câu trả lời ngay lập tức",
      "demo.ai.suggestions": "Gợi Ý Thông Minh",
      "demo.ai.suggestionsDesc": "Phản hồi AI cho tin nhắn",
      "demo.ai.predictive": "Cảnh Báo Dự Đoán",
      "demo.ai.predictiveDesc": "Hệ thống cảnh báo sớm",
      "demo.ai.autoDocs": "Tài Liệu Tự Động",
      "demo.ai.autoDocsDesc": "Ghi chú đơn giản hóa",
      "demo.scheduling.title": "Lập Lịch Thông Minh",
      "demo.scheduling.subtitle": "Tối ưu hóa việc cung cấp chăm sóc",
      "demo.scheduling.description": "Lập lịch thăm khám tối ưu hóa bằng AI đảm bảo nhân viên phù hợp đến đúng bệnh nhân vào đúng thời điểm.",
      "demo.scheduling.calendar": "Xem Lịch",
      "demo.scheduling.calendarDesc": "Quản lý lịch trực quan",
      "demo.scheduling.geo": "Tối Ưu Địa Lý",
      "demo.scheduling.geoDesc": "Định tuyến hiệu quả",
      "demo.scheduling.aiSuggestions": "Gợi Ý AI",
      "demo.scheduling.aiSuggestionsDesc": "Khung giờ tối ưu",
      "demo.scheduling.staff": "Quản Lý Nhân Viên",
      "demo.scheduling.staffDesc": "Sẵn có và nghỉ phép",
      "demo.training.title": "Học Viện Hospice",
      "demo.training.subtitle": "Học tập liên tục vì sự xuất sắc",
      "demo.training.description": "Nền tảng đào tạo toàn diện với chương trình giảng dạy theo chuẩn ELNEC, khóa học tương tác, câu hỏi trắc nghiệm và tạo chứng chỉ CEU.",
      "demo.training.courses": "56+ Khóa Học",
      "demo.training.coursesDesc": "11 lộ trình đào tạo",
      "demo.training.ceu": "Chứng Chỉ CEU",
      "demo.training.ceuDesc": "Chứng chỉ có thể tải xuống",
      "demo.training.badges": "Huy Hiệu Thành Tích",
      "demo.training.badgesDesc": "Học tập game hóa",
      "demo.training.progress": "Theo Dõi Tiến Độ",
      "demo.training.progressDesc": "Cá nhân và nhóm",
      "demo.analytics.title": "Phân Tích và Thông Tin",
      "demo.analytics.subtitle": "Cải thiện chăm sóc dựa trên dữ liệu",
      "demo.analytics.description": "Bảng điều khiển toàn diện về hiệu suất nhân viên, gắn kết gia đình và các chỉ số hoạt động.",
      "demo.analytics.performance": "Hiệu Suất Nhân Viên",
      "demo.analytics.performanceDesc": "Thời gian phản hồi và kết quả",
      "demo.analytics.engagement": "Gắn Kết Gia Đình",
      "demo.analytics.engagementDesc": "Chỉ số tương tác",
      "demo.analytics.cahps": "Dự Đoán CAHPS",
      "demo.analytics.cahpsDesc": "Điểm chất lượng",
      "demo.analytics.clinical": "Thông Tin Lâm Sàng",
      "demo.analytics.clinicalDesc": "Mô hình chăm sóc",
      "demo.getStarted.title": "Sẵn Sàng Thay Đổi Chăm Sóc?",
      "demo.getStarted.subtitle": "Tham gia gia đình Goodwin Cares",
      "demo.getStarted.description": "Dù bạn là bệnh nhân, người chăm sóc hay chuyên gia y tế, Goodwin Cares cung cấp các công cụ bạn cần.",
      "demo.getStarted.forPatients": "Cho Bệnh Nhân",
      "demo.getStarted.forPatientsDesc": "Đăng ký hỗ trợ chăm sóc",
      "demo.getStarted.forProviders": "Cho Nhà Cung Cấp",
      "demo.getStarted.forProvidersDesc": "Tham gia mạng lưới chăm sóc",
      "demo.getStarted.forPartners": "Cho Đối Tác",
      "demo.getStarted.forPartnersDesc": "Tích hợp y tế",
      "demo.getStarted.forFamilies": "Cho Gia Đình",
      "demo.getStarted.forFamiliesDesc": "Tài nguyên người chăm sóc"
    }
  },
  ko: {
    translation: {
      // Common
      "app.name": "Goodwin Cares",
      "app.tagline": "호스피스 돌봄 지원",
      "common.loading": "로딩 중...",
      "common.save": "저장",
      "common.cancel": "취소",
      "common.close": "닫기",
      "common.submit": "제출",
      "common.refresh": "새로고침",
      
      // Navigation
      "nav.home": "홈",
      "nav.dashboard": "대시보드",
      "nav.chat": "AI에게 물어보기",
      "nav.symptoms": "증상",
      "nav.medications": "약물",
      "nav.calendar": "캘린더",
      "nav.signIn": "로그인",
      "nav.signOut": "로그아웃",
      
      // Dashboard
      "dashboard.greeting.morning": "좋은 아침입니다",
      "dashboard.greeting.afternoon": "좋은 오후입니다",
      "dashboard.greeting.evening": "좋은 저녁입니다",
      "dashboard.checkin": "체크인",
      "dashboard.checkedIn": "체크인 완료",
      "dashboard.medications": "약물",
      "dashboard.careTeam": "돌봄 팀",
      "dashboard.manual": "수동",
      "dashboard.voice": "음성",
      
      // Check-in
      "checkin.title": "일일 체크인",
      "checkin.overall": "오늘 기분이 어떠세요?",
      "checkin.pain": "통증 수준",
      "checkin.sleep": "수면 품질",
      "checkin.mood": "현재 기분",
      "checkin.complete": "체크인 완료",
      
      // Moods
      "mood.peaceful": "평화로움",
      "mood.happy": "행복함",
      "mood.okay": "괜찮음",
      "mood.tired": "피곤함",
      "mood.anxious": "불안함",
      "mood.sad": "슬픔",
      
      // Emergency
      "emergency.title": "긴급 연락처",
      "emergency.nurseLine": "24시간 간호사 핫라인",
      "emergency.call911": "911 전화",
      
      // Auth
      "auth.signIn": "로그인",
      "auth.signUp": "회원가입",
      "auth.welcomeBack": "다시 오신 것을 환영합니다!",
      
      // Family Portal
      "familyPortal.title": "가족 포털",
      "familyPortal.subtitle": "사랑하는 분의 돌봄 팀과 연결하세요",
      "familyPortal.newNotifications": "새 알림",
      "familyPortal.messageTeam": "팀에게 메시지",
      "familyPortal.callNurse": "간호사 호출",
      "familyPortal.requestChaplain": "목사 요청",
      "familyPortal.scheduleVideoCall": "화상 통화 예약",
      "familyPortal.overview": "개요",
      "familyPortal.updates": "업데이트",
      "familyPortal.resources": "리소스",
      "familyPortal.careTimeline": "돌봄 타임라인",
      "familyPortal.emergencyLine": "24시간 긴급 전화",
      "familyPortal.callNow": "지금 전화",
      
      // Resources Page
      "resources.title": "커뮤니티",
      "resources.titleHighlight": "리소스",
      "resources.searchPlaceholder": "리소스 검색...",
      "resources.allResources": "모든 리소스",
      "resources.noResults": "조건에 맞는 리소스를 찾을 수 없습니다.",
      "resources.needHelp": "리소스 찾기 도움이 필요하세요?",
      "resources.chatWithAI": "AI 어시스턴트와 채팅",
      
      // Staff Roster
      "staffRoster.title": "직원 명단",
      "staffRoster.subtitle": "시스템에 로그인한 모든 직원",
      "staffRoster.loading": "직원 명단 로딩 중...",
      "staffRoster.totalStaff": "총 직원",
      "staffRoster.allRoles": "모든 역할",
      "staffRoster.allStatus": "모든 상태",
      "staffRoster.clearFilters": "필터 지우기",
      "staffRoster.staffMembers": "직원 구성원",
      "staffRoster.noResults": "필터에 맞는 직원을 찾을 수 없습니다",
      "staffRoster.status.active": "활성",
      "staffRoster.status.inactive": "비활성",
      "staffRoster.status.onLeave": "휴가 중",
      
      // Language Tour
      "languageTour.title": "언어가 변경되었습니다!",
      "languageTour.welcome": "환영합니다",
      "languageTour.keyPhrases": "선택한 언어의 주요 문구:",
      "languageTour.home": "홈",
      "languageTour.help": "도움 받기",
      "languageTour.messages": "메시지",
      "languageTour.profile": "프로필",
      "languageTour.gotIt": "알겠습니다!",
      
      // Voice Chat
      "chat.recordingStarted": "녹음 시작 - 말씀하세요",
      "chat.microphoneDenied": "마이크 접근이 거부되었습니다. 마이크 권한을 활성화해 주세요.",
      "chat.transcriptionComplete": "음성 변환 완료",
      "chat.noSpeechDetected": "음성이 감지되지 않았습니다. 다시 시도해 주세요.",
      "chat.transcriptionFailed": "오디오 변환에 실패했습니다. 다시 시도해 주세요.",
      "chat.recording": "녹음 중",
      "chat.transcribing": "변환 중...",
      "chat.speaking": "말하는 중...",
      "chat.ttsDisabled": "음성 응답 비활성화됨",
      "chat.ttsEnabled": "음성 응답 활성화됨",
      "chat.ttsOn": "음성 켜기",
      "chat.ttsOff": "음성 끄기",
      "chat.speakNow": "말씀하세요...",
      "chat.voiceCommand.cleared": "음성 명령으로 채팅이 지워졌습니다",
      "chat.voiceCommand.newStarted": "음성 명령으로 새 대화가 시작되었습니다",
      "chat.voiceCommand.stopped": "명령으로 음성이 중지되었습니다",
      "chat.voiceCommand.hint": "\"채팅 지우기\", \"새 대화\", 또는 \"말 그만\"이라고 말해보세요",
      
      // Staff Portal
      "staffPortal.title": "직원 포털",
      "staffPortal.subtitle": "케이스로드 및 커뮤니케이션 관리",
      "staffPortal.tabs.caseload": "내 케이스로드",
      "staffPortal.tabs.messages": "메시지",
      "staffPortal.tabs.alerts": "임상 경고",
      "staffPortal.caseload.search": "환자 검색...",
      "staffPortal.caseload.assignPatient": "환자 배정",
      "staffPortal.caseload.noPatients": "케이스로드에 환자가 없습니다",
      "staffPortal.messages.newConversation": "새 대화",
      "staffPortal.alerts.noAlerts": "활성 경고 없음",
      
      // Contact Page
      "contact.caption": "연락하기",
      "contact.title": "도움을 드리겠습니다",
      "contact.description": "호스피스 케어에 대한 질문이 있거나, 지원이 필요하거나, 저희 서비스에 대해 더 알고 싶으시면 언제든지 연락해 주세요.",
      "contact.phone": "전화",
      "contact.available247": "연중무휴",
      "contact.email": "이메일",
      "contact.responseTime": "24시간 이내 응답",
      "contact.hours": "운영 시간",
      "contact.24hours": "하루 24시간",
      "contact.7days": "주 7일",
      "contact.sendMessage": "메시지 보내기",
      "contact.thankYou": "감사합니다",
      "contact.fullName": "성명",
      "contact.subject": "제목",
      "contact.message": "메시지",
      "contact.sending": "전송 중...",
      "contact.sendMessageBtn": "메시지 전송",
      
      // Translation Dashboard
      "translationDashboard.title": "번역 진행 대시보드",
      "translationDashboard.subtitle": "모든 페이지와 언어의 국제화 범위 추적",
      "translationDashboard.coverage": "범위",
      "translationDashboard.keysTranslated": "키 번역됨",
      "translationDashboard.pages": "페이지",
      "translationDashboard.totalPages": "총 페이지/구성요소",
      "translationDashboard.languagesComplete": "90%+ 완료 언어",
      "translationDashboard.missingKeys": "누락된 키",
      "translationDashboard.copyMissing": "누락 복사",
      
      // Admin Dashboard
      "adminDashboard.title": "관리자 대시보드",
      "adminDashboard.subtitle": "사이트 분석, 채팅 사용량 및 연락처 메시지 모니터링",
      "adminDashboard.refreshData": "데이터 새로고침",
      "adminDashboard.accessRequired": "관리자 권한 필요",
      "adminDashboard.stats.totalVisits": "총 방문수",
      "adminDashboard.stats.chatSessions": "채팅 세션",
      
      // Video Library (Korean)
      "videoLibrary.backToHome": "홈으로 돌아가기",
      "videoLibrary.title": "비디오 라이브러리",
      "videoLibrary.description": "전체 기능 데모 컬렉션을 탐색하세요. 각 비디오는 Goodwin Cares의 기능을 보여주는 완전하고 매끄러운 경험을 제공합니다.",
      "videoLibrary.fullVideos": "{{count}}개 전체 비디오",
      "videoLibrary.takeInteractiveTour": "인터랙티브 투어하기",
      "videoLibrary.searchPlaceholder": "비디오 검색...",
      "videoLibrary.allFeatures": "모든 기능",
      "videoLibrary.noVideosFound": "비디오를 찾을 수 없습니다",
      "videoLibrary.cta.title": "따뜻한 케어를 경험할 준비가 되셨나요?",
      "videoLibrary.cta.getStarted": "시작하기",
      "videoLibrary.cta.takeTour": "투어하기",
      
      // Programs Page (Korean)
      "programs.hero.badge": "전통적인 케어를 넘어서",
      "programs.benefits": "혜택",
      "programs.call": "전화하기",
      "programs.visitWebsite": "웹사이트 방문",
      "programs.cta.title": "프로그램에 대해 질문이 있으신가요?",
      "programs.cta.contactOnline": "온라인으로 문의하기",
      "programs.massage.title": "임종 마사지 요법",
      "programs.doula.title": "임종 둘라 서비스",
      "programs.aftercare.title": "완전한 사후 관리",
      
      // Moods (Korean)
      "moods.happy": "행복",
      "moods.calm": "평온",
      "moods.grateful": "감사",
      "moods.hopeful": "희망적",
      "moods.content": "만족",
      "moods.anxious": "불안",
      "moods.sad": "슬픔",
      "moods.frustrated": "좌절",
      "moods.tired": "피곤",
      "moods.scared": "두려움",
      "moods.angry": "화남",
      
      // Demo Page - Interactive Tour (Korean)
      "demo.welcome.title": "Goodwin Cares에 오신 것을 환영합니다",
      "demo.welcome.subtitle": "기술로 구현하는 따뜻한 케어",
      "demo.welcome.description": "AI 기반 지능과 인간의 연민을 결합하여 최고의 케어 경험을 제공하는 종합 호스피스 및 돌봄 지원 플랫폼입니다.",
      "demo.welcome.patientCare": "환자 중심 케어",
      "demo.welcome.hipaa": "HIPAA 준수",
      "demo.welcome.aiInsights": "AI 기반 분석",
      "demo.welcome.familyEngagement": "가족 참여",
      "demo.patientCare.title": "종합적인 환자 케어",
      "demo.patientCare.subtitle": "가족에게 필요한 모든 것을 한 곳에서",
      "demo.patientCare.description": "환자와 돌봄 제공자는 직관적인 대시보드를 통해 증상, 약물을 추적하고 케어 팀과 직접 소통할 수 있습니다.",
      "demo.patientCare.checkins": "일일 체크인",
      "demo.patientCare.checkinsDesc": "통증, 기분, 증상 추적",
      "demo.patientCare.medications": "약물 알림",
      "demo.patientCare.medicationsDesc": "복용 시간을 놓치지 마세요",
      "demo.patientCare.messaging": "직접 메시지",
      "demo.patientCare.messagingDesc": "케어 팀과 대화하세요",
      "demo.patientCare.symptoms": "증상 추적",
      "demo.patientCare.symptomsDesc": "시각적 건강 트렌드",
      "demo.communication.title": "실시간 커뮤니케이션",
      "demo.communication.subtitle": "케어 팀과 연결 유지",
      "demo.communication.description": "보안 메시지, 화상 통화, 즉각적인 알림으로 모두가 연결되고 정보를 공유합니다.",
      "demo.communication.telehealth": "원격 진료",
      "demo.communication.telehealthDesc": "집에서 대면 진료",
      "demo.communication.secureMsg": "보안 메시지",
      "demo.communication.secureMsgDesc": "HIPAA 준수 채팅",
      "demo.communication.notifications": "푸시 알림",
      "demo.communication.notificationsDesc": "실시간 알림",
      "demo.communication.triage": "전화 분류",
      "demo.communication.triageDesc": "24시간 긴급 지원",
      "demo.ai.title": "AI 기반 인텔리전스",
      "demo.ai.subtitle": "차이를 만드는 스마트 기능",
      "demo.ai.description": "AI가 환자 데이터를 분석하여 실행 가능한 인사이트를 제공하고 직원이 선제적 케어를 제공하도록 돕습니다.",
      "demo.ai.chatAssistant": "AI 채팅 어시스턴트",
      "demo.ai.chatAssistantDesc": "즉각적인 답변 받기",
      "demo.ai.suggestions": "스마트 제안",
      "demo.ai.suggestionsDesc": "AI 메시지 응답",
      "demo.ai.predictive": "예측 알림",
      "demo.ai.predictiveDesc": "조기 경보 시스템",
      "demo.ai.autoDocs": "자동 문서화",
      "demo.ai.autoDocsDesc": "간소화된 기록",
      "demo.scheduling.title": "스마트 스케줄링",
      "demo.scheduling.subtitle": "케어 제공 최적화",
      "demo.scheduling.description": "AI 최적화 방문 스케줄링으로 적합한 직원이 적합한 환자에게 적시에 도착합니다.",
      "demo.scheduling.calendar": "캘린더 보기",
      "demo.scheduling.calendarDesc": "시각적 일정 관리",
      "demo.scheduling.geo": "지리적 최적화",
      "demo.scheduling.geoDesc": "효율적인 경로",
      "demo.scheduling.aiSuggestions": "AI 제안",
      "demo.scheduling.aiSuggestionsDesc": "최적의 시간대",
      "demo.scheduling.staff": "직원 관리",
      "demo.scheduling.staffDesc": "가용성 및 휴가",
      "demo.training.title": "호스피스 아카데미",
      "demo.training.subtitle": "탁월함을 위한 지속적 학습",
      "demo.training.description": "ELNEC 기반 커리큘럼, 대화형 강좌, 퀴즈 및 CEU 인증서 발급을 포함한 종합 교육 플랫폼입니다.",
      "demo.training.courses": "56개 이상의 강좌",
      "demo.training.coursesDesc": "11개 교육 트랙",
      "demo.training.ceu": "CEU 인증서",
      "demo.training.ceuDesc": "다운로드 가능한 자격증",
      "demo.training.badges": "성취 배지",
      "demo.training.badgesDesc": "게임화된 학습",
      "demo.training.progress": "진행 추적",
      "demo.training.progressDesc": "개인 및 팀",
      "demo.analytics.title": "분석 및 인사이트",
      "demo.analytics.subtitle": "데이터 기반 케어 개선",
      "demo.analytics.description": "직원 성과, 가족 참여 및 운영 지표에 대한 종합 대시보드입니다.",
      "demo.analytics.performance": "직원 성과",
      "demo.analytics.performanceDesc": "응답 시간 및 결과",
      "demo.analytics.engagement": "가족 참여",
      "demo.analytics.engagementDesc": "상호작용 지표",
      "demo.analytics.cahps": "CAHPS 예측기",
      "demo.analytics.cahpsDesc": "품질 점수",
      "demo.analytics.clinical": "임상 인사이트",
      "demo.analytics.clinicalDesc": "케어 패턴",
      "demo.getStarted.title": "케어를 혁신할 준비가 되셨나요?",
      "demo.getStarted.subtitle": "Goodwin Cares 가족이 되세요",
      "demo.getStarted.description": "환자, 돌봄 제공자, 의료 전문가 모두에게 필요한 도구를 제공합니다.",
      "demo.getStarted.forPatients": "환자용",
      "demo.getStarted.forPatientsDesc": "케어 지원 신청",
      "demo.getStarted.forProviders": "제공자용",
      "demo.getStarted.forProvidersDesc": "케어 네트워크 참여",
      "demo.getStarted.forPartners": "파트너용",
      "demo.getStarted.forPartnersDesc": "의료 통합",
      "demo.getStarted.forFamilies": "가족용",
      "demo.getStarted.forFamiliesDesc": "돌봄 제공자 리소스"
    }
  },
  tl: {
    translation: {
      // Common (Tagalog/Filipino)
      "app.name": "Goodwin Cares",
      "app.tagline": "Maawaing Suporta sa Hospice",
      "common.loading": "Naglo-load...",
      "common.save": "I-save",
      "common.cancel": "Kanselahin",
      "common.close": "Isara",
      "common.submit": "Isumite",
      "common.refresh": "I-refresh",
      
      // Navigation
      "nav.home": "Home",
      "nav.dashboard": "Dashboard",
      "nav.chat": "Magtanong sa AI",
      "nav.symptoms": "Mga Sintomas",
      "nav.medications": "Mga Gamot",
      "nav.signIn": "Mag-sign In",
      "nav.signOut": "Mag-sign Out",
      
      // Dashboard
      "dashboard.greeting.morning": "Magandang umaga",
      "dashboard.greeting.afternoon": "Magandang hapon",
      "dashboard.greeting.evening": "Magandang gabi",
      "dashboard.checkin": "Mag-check In",
      "dashboard.checkedIn": "Naka-check In",
      "dashboard.medications": "Mga Gamot",
      "dashboard.careTeam": "Care Team",
      "dashboard.manual": "Manual",
      "dashboard.voice": "Boses",
      
      // Check-in
      "checkin.title": "Pang-araw-araw na Check-in",
      "checkin.overall": "Kumusta ang pakiramdam mo ngayon?",
      "checkin.pain": "Antas ng Sakit",
      "checkin.sleep": "Kalidad ng Tulog",
      "checkin.mood": "Kasalukuyang Mood",
      "checkin.complete": "Kumpletuhin ang Check-in",
      
      // Moods
      "mood.peaceful": "Mapayapa",
      "mood.happy": "Masaya",
      "mood.okay": "Ayos lang",
      "mood.tired": "Pagod",
      "mood.anxious": "Balisa",
      "mood.sad": "Malungkot",
      
      // Emergency
      "emergency.title": "Mga Emergency Contact",
      "emergency.nurseLine": "24/7 Nurse Line",
      "emergency.call911": "Tumawag sa 911",
      
      // Auth
      "auth.signIn": "Mag-sign In",
      "auth.welcomeBack": "Welcome back!",
      
      // Family Portal
      "familyPortal.title": "Portal ng Pamilya",
      "familyPortal.subtitle": "Manatiling konektado sa care team ng inyong mahal sa buhay",
      "familyPortal.newNotifications": "bagong notifikasyon",
      "familyPortal.messageTeam": "Magmensahe sa Team",
      "familyPortal.callNurse": "Tawagan ang Nurse",
      "familyPortal.requestChaplain": "Humiling ng Chaplain",
      "familyPortal.scheduleVideoCall": "Mag-schedule ng Video Call",
      "familyPortal.overview": "Pangkalahatan",
      "familyPortal.updates": "Mga Update",
      "familyPortal.resources": "Mga Resources",
      "familyPortal.careTimeline": "Care Timeline",
      "familyPortal.emergencyLine": "24/7 Emergency Line",
      "familyPortal.callNow": "Tumawag Ngayon",
      
      // Resources Page
      "resources.title": "Komunidad",
      "resources.titleHighlight": "Mga Resources",
      "resources.searchPlaceholder": "Maghanap ng resources...",
      "resources.allResources": "Lahat ng Resources",
      "resources.noResults": "Walang nakitang resources na tumutugma sa iyong pamantayan.",
      "resources.needHelp": "Kailangan ng Tulong sa Paghahanap ng Resources?",
      "resources.chatWithAI": "Makipag-chat sa AI Assistant",
      
      // Staff Roster
      "staffRoster.title": "Listahan ng Staff",
      "staffRoster.subtitle": "Lahat ng staff members na naka-sign in sa sistema",
      "staffRoster.loading": "Naglo-load ng Listahan ng Staff...",
      "staffRoster.totalStaff": "Kabuuang Staff",
      "staffRoster.allRoles": "Lahat ng Roles",
      "staffRoster.allStatus": "Lahat ng Status",
      "staffRoster.clearFilters": "I-clear ang Filters",
      "staffRoster.staffMembers": "Mga Staff Member",
      "staffRoster.noResults": "Walang nakitang staff members na tumutugma sa iyong mga filter",
      "staffRoster.status.active": "Aktibo",
      "staffRoster.status.inactive": "Hindi Aktibo",
      "staffRoster.status.onLeave": "Naka-leave",
      
      // Language Tour
      "languageTour.title": "Nagbago ang Wika!",
      "languageTour.welcome": "Maligayang pagdating",
      "languageTour.keyPhrases": "Mga pangunahing parirala sa iyong wika:",
      "languageTour.home": "Home",
      "languageTour.help": "Kumuha ng Tulong",
      "languageTour.messages": "Mga Mensahe",
      "languageTour.profile": "Profile",
      "languageTour.gotIt": "Okay na!",
      
      // Voice Chat
      "chat.recordingStarted": "Nagsimula na ang pag-record - magsalita na",
      "chat.microphoneDenied": "Hindi pinapayagan ang access sa microphone. Pakibukas ang microphone permissions.",
      "chat.transcriptionComplete": "Kumpleto na ang transcription",
      "chat.noSpeechDetected": "Walang nadetect na pagsasalita. Pakisubukan ulit.",
      "chat.transcriptionFailed": "Hindi na-transcribe ang audio. Pakisubukan ulit.",
      "chat.recording": "Nagre-record",
      "chat.transcribing": "Nagta-transcribe...",
      "chat.speaking": "Nagsasalita...",
      "chat.ttsDisabled": "Na-disable ang voice responses",
      "chat.ttsEnabled": "Na-enable ang voice responses",
      "chat.ttsOn": "Boses On",
      "chat.ttsOff": "Boses Off",
      "chat.speakNow": "Magsalita na...",
      "chat.voiceCommand.cleared": "Na-clear ang chat sa pamamagitan ng voice command",
      "chat.voiceCommand.newStarted": "Nagsimula ang bagong usapan sa pamamagitan ng voice command",
      "chat.voiceCommand.stopped": "Huminto ang boses sa pamamagitan ng command",
      "chat.voiceCommand.hint": "Subukang sabihin: \"i-clear ang chat\", \"bagong usapan\", o \"tumigil ka na\"",
      
      // Staff Portal
      "staffPortal.title": "Portal ng Staff",
      "staffPortal.subtitle": "Pamahalaan ang iyong caseload at komunikasyon",
      "staffPortal.tabs.caseload": "Aking Caseload",
      "staffPortal.tabs.messages": "Mga Mensahe",
      "staffPortal.tabs.alerts": "Clinical Alerts",
      "staffPortal.caseload.search": "Maghanap ng pasyente...",
      "staffPortal.caseload.assignPatient": "I-assign ang Pasyente",
      "staffPortal.caseload.noPatients": "Walang pasyente sa iyong caseload",
      "staffPortal.messages.newConversation": "Bagong Usapan",
      "staffPortal.alerts.noAlerts": "Walang active na alerts",
      
      // Contact Page
      "contact.caption": "Makipag-ugnayan",
      "contact.title": "Nandito Kami Para Tumulong",
      "contact.description": "May mga tanong tungkol sa hospice care, kailangan ng suporta, o gustong malaman pa ang tungkol sa aming mga serbisyo? Makipag-ugnayan sa amin anumang oras.",
      "contact.phone": "Telepono",
      "contact.available247": "Available 24/7",
      "contact.email": "Email",
      "contact.responseTime": "Sumasagot kami sa loob ng 24 oras",
      "contact.hours": "Oras",
      "contact.24hours": "24 oras bawat araw",
      "contact.7days": "7 araw bawat linggo",
      "contact.sendMessage": "Magpadala sa Amin ng Mensahe",
      "contact.thankYou": "Salamat",
      "contact.fullName": "Buong Pangalan",
      "contact.subject": "Paksa",
      "contact.message": "Mensahe",
      "contact.sending": "Nagpapadala...",
      "contact.sendMessageBtn": "Ipadala ang Mensahe",
      
      // Translation Dashboard
      "translationDashboard.title": "Dashboard ng Progreso ng Pagsasalin",
      "translationDashboard.subtitle": "Subaybayan ang saklaw ng internasyonalisasyon sa lahat ng pahina at wika",
      "translationDashboard.coverage": "Saklaw",
      "translationDashboard.keysTranslated": "keys na naisalin",
      "translationDashboard.pages": "pahina",
      "translationDashboard.totalPages": "Kabuuang Pahina/Komponent",
      "translationDashboard.languagesComplete": "Mga Wikang 90%+ Kumpleto",
      "translationDashboard.missingKeys": "Nawawalang Keys",
      "translationDashboard.copyMissing": "Kopyahin Nawawala",
      
      // Admin Dashboard
      "adminDashboard.title": "Admin Dashboard",
      "adminDashboard.subtitle": "I-monitor ang analytics ng site, paggamit ng chat at mga mensahe ng contact",
      "adminDashboard.refreshData": "I-refresh ang Data",
      "adminDashboard.accessRequired": "Kailangan ng Admin Access",
      "adminDashboard.stats.totalVisits": "Kabuuang Pagbisita",
      "adminDashboard.stats.chatSessions": "Mga Chat Session",
      
      // Video Library (Tagalog)
      "videoLibrary.backToHome": "Bumalik sa Home",
      "videoLibrary.title": "Library ng Video",
      "videoLibrary.description": "Galugarin ang aming koleksyon ng buong feature demos. Bawat video ay nagbibigay ng kumpleto at seamless na karanasan na nagpapakita ng kakayahan ng Goodwin Cares.",
      "videoLibrary.fullVideos": "{{count}} Buong Video",
      "videoLibrary.takeInteractiveTour": "Kumuha ng Interactive Tour",
      "videoLibrary.searchPlaceholder": "Maghanap ng video...",
      "videoLibrary.allFeatures": "Lahat ng Features",
      "videoLibrary.noVideosFound": "Walang nakitang video",
      "videoLibrary.cta.title": "Handa ka na bang Maranasan ang Maawaing Pag-aalaga?",
      "videoLibrary.cta.getStarted": "Magsimula",
      "videoLibrary.cta.takeTour": "Kumuha ng Tour",
      
      // Programs Page (Tagalog)
      "programs.hero.badge": "Higit sa Tradisyonal na Pag-aalaga",
      "programs.benefits": "Mga Benepisyo",
      "programs.call": "Tumawag",
      "programs.visitWebsite": "Bisitahin ang Website",
      "programs.cta.title": "May Mga Tanong Tungkol sa Aming Mga Programa?",
      "programs.cta.contactOnline": "Makipag-ugnayan Online",
      "programs.massage.title": "End-of-Life Massage Therapy",
      "programs.doula.title": "End-of-Life Doula Services",
      "programs.aftercare.title": "Full Circle Aftercare",
      
      // Moods (Tagalog)
      "moods.happy": "Masaya",
      "moods.calm": "Kalmado",
      "moods.grateful": "Nagpapasalamat",
      "moods.hopeful": "May Pag-asa",
      "moods.content": "Kontento",
      "moods.anxious": "Balisa",
      "moods.sad": "Malungkot",
      "moods.frustrated": "Frustrated",
      "moods.tired": "Pagod",
      "moods.scared": "Takot",
      "moods.angry": "Galit",
      
      // Demo Page - Interactive Tour (Tagalog)
      "demo.welcome.title": "Maligayang Pagdating sa Goodwin Cares",
      "demo.welcome.subtitle": "Maawaing Pag-aalaga, Pinapagana ng Teknolohiya",
      "demo.welcome.description": "Isang komprehensibong platform ng suporta sa hospice at caregiver na pinagsasama ang AI-powered intelligence at pagmamalasakit ng tao para sa pinakamahusay na karanasan sa pag-aalaga.",
      "demo.welcome.patientCare": "Pag-aalaga na Nakasentro sa Pasyente",
      "demo.welcome.hipaa": "Sumusunod sa HIPAA",
      "demo.welcome.aiInsights": "AI-Powered Analytics",
      "demo.welcome.familyEngagement": "Pakikilahok ng Pamilya",
      "demo.patientCare.title": "Komprehensibong Pag-aalaga sa Pasyente",
      "demo.patientCare.subtitle": "Lahat ng kailangan ng mga pamilya sa isang lugar",
      "demo.patientCare.description": "Maaaring i-track ng mga pasyente at caregiver ang mga sintomas, gamot, at direktang makipag-ugnayan sa kanilang care team sa pamamagitan ng intuitive dashboard.",
      "demo.patientCare.checkins": "Araw-araw na Check-in",
      "demo.patientCare.checkinsDesc": "Subaybayan ang sakit, mood, at sintomas",
      "demo.patientCare.medications": "Paalala sa Gamot",
      "demo.patientCare.medicationsDesc": "Huwag palampasin ang anumang dosis",
      "demo.patientCare.messaging": "Direktang Pagmemensahe",
      "demo.patientCare.messagingDesc": "Makipag-chat sa iyong care team",
      "demo.patientCare.symptoms": "Pagsubaybay sa Sintomas",
      "demo.patientCare.symptomsDesc": "Visual na mga trend ng kalusugan",
      "demo.communication.title": "Real-Time na Komunikasyon",
      "demo.communication.subtitle": "Manatiling konektado sa iyong care team",
      "demo.communication.description": "Ang secure na messaging, video calls, at instant notifications ay nagpapanatiling may alam at konektado ang lahat.",
      "demo.communication.telehealth": "Mga Telehealth Visit",
      "demo.communication.telehealthDesc": "Harapan mula sa bahay",
      "demo.communication.secureMsg": "Secure na Messaging",
      "demo.communication.secureMsgDesc": "HIPAA-compliant chat",
      "demo.communication.notifications": "Push Notifications",
      "demo.communication.notificationsDesc": "Real-time na alerto",
      "demo.communication.triage": "Call Triage",
      "demo.communication.triageDesc": "24/7 urgent support",
      "demo.ai.title": "AI-Powered Intelligence",
      "demo.ai.subtitle": "Mga smart feature na gumagawa ng pagkakaiba",
      "demo.ai.description": "Ang aming AI ay nag-aanalisa ng data ng pasyente para magbigay ng actionable insights at tulungan ang staff na magbigay ng proactive care.",
      "demo.ai.chatAssistant": "AI Chat Assistant",
      "demo.ai.chatAssistantDesc": "Makakuha ng instant na sagot",
      "demo.ai.suggestions": "Matalinong Mungkahi",
      "demo.ai.suggestionsDesc": "AI message responses",
      "demo.ai.predictive": "Predictive Alerts",
      "demo.ai.predictiveDesc": "Early warning system",
      "demo.ai.autoDocs": "Auto Documentation",
      "demo.ai.autoDocsDesc": "Simplified na mga tala",
      "demo.scheduling.title": "Smart Scheduling",
      "demo.scheduling.subtitle": "I-optimize ang pagbibigay ng pag-aalaga",
      "demo.scheduling.description": "Ang AI-optimized na pag-schedule ng pagbisita ay tinitiyak na ang tamang staff ay umaabot sa tamang pasyente sa tamang oras.",
      "demo.scheduling.calendar": "Calendar View",
      "demo.scheduling.calendarDesc": "Visual schedule management",
      "demo.scheduling.geo": "Geographic Optimization",
      "demo.scheduling.geoDesc": "Efficient routing",
      "demo.scheduling.aiSuggestions": "AI Suggestions",
      "demo.scheduling.aiSuggestionsDesc": "Optimal time slots",
      "demo.scheduling.staff": "Staff Management",
      "demo.scheduling.staffDesc": "Availability at time-off",
      "demo.training.title": "Hospice Academy",
      "demo.training.subtitle": "Patuloy na pag-aaral para sa kahusayan",
      "demo.training.description": "Komprehensibong training platform na may ELNEC-aligned curriculum, interactive courses, quizzes, at CEU certificate generation.",
      "demo.training.courses": "56+ na Kurso",
      "demo.training.coursesDesc": "11 training tracks",
      "demo.training.ceu": "CEU Certificates",
      "demo.training.ceuDesc": "Downloadable credentials",
      "demo.training.badges": "Achievement Badges",
      "demo.training.badgesDesc": "Gamified learning",
      "demo.training.progress": "Progress Tracking",
      "demo.training.progressDesc": "Individual at team",
      "demo.analytics.title": "Analytics at Insights",
      "demo.analytics.subtitle": "Data-driven care improvement",
      "demo.analytics.description": "Komprehensibong dashboards para sa staff performance, family engagement, at operational metrics.",
      "demo.analytics.performance": "Staff Performance",
      "demo.analytics.performanceDesc": "Response times at outcomes",
      "demo.analytics.engagement": "Family Engagement",
      "demo.analytics.engagementDesc": "Interaction metrics",
      "demo.analytics.cahps": "CAHPS Predictor",
      "demo.analytics.cahpsDesc": "Quality scores",
      "demo.analytics.clinical": "Clinical Insights",
      "demo.analytics.clinicalDesc": "Care patterns",
      "demo.getStarted.title": "Handa Ka Na Bang Baguhin ang Pag-aalaga?",
      "demo.getStarted.subtitle": "Sumali sa pamilya ng Goodwin Cares",
      "demo.getStarted.description": "Kung ikaw man ay pasyente, caregiver, o healthcare professional, ang Goodwin Cares ay nagbibigay ng mga tool na kailangan mo.",
      "demo.getStarted.forPatients": "Para sa mga Pasyente",
      "demo.getStarted.forPatientsDesc": "Mag-sign up para sa care support",
      "demo.getStarted.forProviders": "Para sa mga Provider",
      "demo.getStarted.forProvidersDesc": "Sumali sa aming care network",
      "demo.getStarted.forPartners": "Para sa mga Partner",
      "demo.getStarted.forPartnersDesc": "Healthcare integration",
      "demo.getStarted.forFamilies": "Para sa mga Pamilya",
      "demo.getStarted.forFamiliesDesc": "Caregiver resources"
    }
  },
  fr: {
    translation: {
      // Common (French)
      "app.name": "Goodwin Cares",
      "app.tagline": "Soutien Compatissant en Soins Palliatifs",
      "common.loading": "Chargement...",
      "common.save": "Enregistrer",
      "common.cancel": "Annuler",
      "common.delete": "Supprimer",
      "common.edit": "Modifier",
      "common.close": "Fermer",
      "common.submit": "Soumettre",
      "common.back": "Retour",
      "common.next": "Suivant",
      "common.yes": "Oui",
      "common.no": "Non",
      "common.search": "Rechercher",
      "common.filter": "Filtrer",
      "common.refresh": "Actualiser",
      
      // Navigation
      "nav.home": "Accueil",
      "nav.dashboard": "Tableau de bord",
      "nav.chat": "Demander à l'IA",
      "nav.symptoms": "Symptômes",
      "nav.journal": "Journal",
      "nav.medications": "Médicaments",
      "nav.calendar": "Calendrier",
      "nav.resources": "Ressources",
      "nav.education": "Éducation",
      "nav.videos": "Vidéos de Formation",
      "nav.contact": "Contact",
      "nav.messages": "Messages",
      "nav.memories": "Souvenirs",
      "nav.compliance": "Sécurité",
      "nav.signIn": "Se Connecter",
      "nav.signOut": "Se Déconnecter",
      
      // Dashboard
      "dashboard.greeting.morning": "Bonjour",
      "dashboard.greeting.afternoon": "Bon après-midi",
      "dashboard.greeting.evening": "Bonsoir",
      "dashboard.checkin": "Enregistrement",
      "dashboard.checkedIn": "Enregistré",
      "dashboard.streak": "jours consécutifs",
      "dashboard.medications": "Médicaments",
      "dashboard.upcoming": "À venir",
      "dashboard.careTeam": "Équipe de Soins",
      "dashboard.quickActions": "Actions Rapides",
      "dashboard.askAI": "Demander à l'IA",
      "dashboard.insights": "Aperçus de Soins",
      "dashboard.generateInsights": "Générer",
      "dashboard.manual": "Manuel",
      "dashboard.voice": "Voix",
      
      // Check-in
      "checkin.title": "Enregistrement Quotidien",
      "checkin.overall": "Comment vous sentez-vous en général?",
      "checkin.pain": "Niveau de Douleur",
      "checkin.sleep": "Qualité du Sommeil",
      "checkin.appetite": "Appétit",
      "checkin.mobility": "Mobilité",
      "checkin.mood": "Humeur Actuelle",
      "checkin.symptoms": "Des symptômes aujourd'hui?",
      "checkin.notes": "Des notes?",
      "checkin.complete": "Terminer l'Enregistrement",
      "checkin.voiceTitle": "Enregistrement Vocal",
      
      // Moods
      "mood.peaceful": "Paisible",
      "mood.happy": "Heureux",
      "mood.okay": "Correct",
      "mood.tired": "Fatigué",
      "mood.anxious": "Anxieux",
      "mood.sad": "Triste",
      
      // Symptoms
      "symptom.pain": "Douleur",
      "symptom.nausea": "Nausée",
      "symptom.fatigue": "Fatigue",
      "symptom.breathlessness": "Essoufflement",
      "symptom.constipation": "Constipation",
      "symptom.anxiety": "Anxiété",
      "symptom.confusion": "Confusion",
      "symptom.appetiteLoss": "Perte d'Appétit",
      "symptom.insomnia": "Insomnie",
      "symptom.weakness": "Faiblesse",
      
      // Medications
      "medications.title": "Médicaments",
      "medications.add": "Ajouter un Médicament",
      "medications.todaySchedule": "Horaire d'Aujourd'hui",
      "medications.allMedications": "Tous les Médicaments",
      "medications.markTaken": "Marquer Pris",
      "medications.skip": "Passer",
      "medications.taken": "Pris",
      "medications.name": "Nom du Médicament",
      "medications.dosage": "Dosage",
      "medications.frequency": "Fréquence",
      "medications.times": "Horaires",
      "medications.instructions": "Instructions Spéciales",
      
      // Calendar
      "calendar.title": "Calendrier de Soins",
      "calendar.addEvent": "Ajouter un Événement",
      "calendar.upcoming": "Événements à Venir",
      "calendar.appointment": "Rendez-vous",
      "calendar.visit": "Visite à Domicile",
      "calendar.reminder": "Rappel",
      "calendar.milestone": "Étape",
      
      // Emergency
      "emergency.title": "Contacts d'Urgence",
      "emergency.hospice": "Goodwin Hospice",
      "emergency.nurseLine": "Ligne Infirmière 24/7",
      "emergency.call911": "Appeler le 911",
      
      // Voice Commands
      "voice.listening": "Écoute...",
      "voice.speak": "Parler",
      "voice.stop": "Arrêter",
      "voice.repeat": "Répéter",
      "voice.notSupported": "Reconnaissance vocale non supportée",
      "voice.micDenied": "Accès au microphone refusé",
      
      // Auth
      "auth.signIn": "Se Connecter",
      "auth.signUp": "S'inscrire",
      "auth.email": "Email",
      "auth.password": "Mot de passe",
      "auth.name": "Nom",
      "auth.forgotPassword": "Mot de passe oublié?",
      "auth.welcomeBack": "Bon retour!",
      
      // Session
      "session.expiring": "Session Expire Bientôt",
      "session.extend": "Prolonger la Session",
      "session.remaining": "Temps restant",
      
      // Compliance
      "compliance.title": "Conformité HIPAA",
      "compliance.2fa": "Authentification à Deux Facteurs",
      "compliance.enabled": "Activé",
      "compliance.disabled": "Désactivé",
      "compliance.sessionTimeout": "Expiration de Session",
      "compliance.auditLogging": "Journal d'Audit",
      "compliance.encryption": "Données Cryptées",
      
      // Family Portal
      "familyPortal.title": "Portail Familial",
      "familyPortal.subtitle": "Restez connecté avec l'équipe de soins de votre proche",
      "familyPortal.newNotifications": "nouvelles notifications",
      "familyPortal.enhancedCare": "Soins de Confort Améliorés",
      "familyPortal.enhancedCareDesc": "Notre équipe de soins fournit des soins axés sur le confort. Nous sommes là pour soutenir votre proche et votre famille pendant cette période.",
      "familyPortal.nurseLineLabel": "Ligne Infirmière 24/7",
      "familyPortal.messageTeam": "Message à l'Équipe",
      "familyPortal.sendMessage": "Envoyer un Message à l'Équipe de Soins",
      "familyPortal.messageDesc": "Votre message sera envoyé à toute l'équipe de soins",
      "familyPortal.yourName": "Votre Nom",
      "familyPortal.yourNamePlaceholder": "Votre nom",
      "familyPortal.relationship": "Relation",
      "familyPortal.relationshipPlaceholder": "ex., Fille, Fils, Conjoint",
      "familyPortal.markUrgent": "Marquer comme urgent",
      "familyPortal.callNurse": "Appeler l'Infirmière",
      "familyPortal.requestChaplain": "Demander un Aumônier",
      "familyPortal.scheduleVideoCall": "Planifier un Appel Vidéo",
      "familyPortal.overview": "Aperçu",
      "familyPortal.updates": "Mises à jour",
      "familyPortal.resources": "Ressources",
      "familyPortal.careTimeline": "Chronologie des Soins",
      "familyPortal.yourCareTeam": "Votre Équipe de Soins",
      "familyPortal.primaryNurse": "Infirmière Principale",
      "familyPortal.attendingPhysician": "Médecin Traitant",
      "familyPortal.socialWorker": "Travailleur Social",
      "familyPortal.supportServices": "Services de Soutien",
      "familyPortal.contact": "Contacter",
      "familyPortal.message": "Message",
      "familyPortal.request": "Demander",
      "familyPortal.upcomingVisits": "Visites à Venir",
      "familyPortal.scheduled": "Programmé",
      "familyPortal.visitScheduleAppear": "Le calendrier des visites apparaîtra ici",
      "familyPortal.currentFocusAreas": "Domaines d'Attention Actuels",
      "familyPortal.recentNotifications": "Notifications Récentes",
      "familyPortal.noNotifications": "Aucune notification",
      "familyPortal.careUpdates": "Mises à Jour des Soins",
      "familyPortal.recentUpdates": "Mises à jour récentes de votre équipe de soins",
      "familyPortal.noUpdates": "Aucune mise à jour",
      "familyPortal.from": "De",
      "familyPortal.download": "Télécharger",
      "familyPortal.careTimelineActivities": "Activités de soins récentes",
      "familyPortal.timelineAppear": "La chronologie des soins apparaîtra ici",
      "familyPortal.emergencyLine": "Ligne d'Urgence 24/7",
      "familyPortal.callNow": "Appeler Maintenant",
      
      // Resources Page
      "resources.location": "Virginie du Nord",
      "resources.title": "Ressources",
      "resources.titleHighlight": "Communautaires",
      "resources.description": "Services de soutien locaux pour les familles en soins palliatifs dans la région de Virginie du Nord. Trouvez transport, repas, conseil et plus.",
      "resources.searchPlaceholder": "Rechercher des ressources...",
      "resources.allResources": "Toutes les Ressources",
      "resources.noResults": "Aucune ressource trouvée correspondant à vos critères.",
      "resources.needHelp": "Besoin d'Aide pour Trouver des Ressources?",
      "resources.helpDescription": "Notre assistant IA peut vous aider à trouver les bonnes ressources communautaires selon vos besoins spécifiques. Décrivez simplement ce que vous recherchez.",
      "resources.chatWithAI": "Discuter avec l'Assistant IA",
      
      // Staff Roster
      "staffRoster.title": "Liste du Personnel",
      "staffRoster.subtitle": "Tous les membres du personnel connectés au système",
      "staffRoster.loading": "Chargement de la Liste du Personnel...",
      "staffRoster.totalStaff": "Personnel Total",
      "staffRoster.searchPlaceholder": "Rechercher par nom, email ou discipline...",
      "staffRoster.allRoles": "Tous les Rôles",
      "staffRoster.allStatus": "Tous les Statuts",
      "staffRoster.clearFilters": "Effacer les Filtres",
      "staffRoster.staffMembers": "Membres du Personnel",
      "staffRoster.noResults": "Aucun membre du personnel trouvé correspondant à vos filtres",
      "staffRoster.lastLogin": "Dernière connexion",
      "staffRoster.completed": "terminé",
      "staffRoster.pending": "en attente",
      "staffRoster.sendEmail": "Envoyer un Email",
      "staffRoster.viewTraining": "Voir la Formation",
      "staffRoster.viewProfile": "Voir le profil de",
      "staffRoster.roles.admin": "Administrateur",
      "staffRoster.roles.nurse": "Infirmière",
      "staffRoster.roles.rn": "Infirmière Diplômée",
      "staffRoster.roles.lpn": "Infirmière Auxiliaire",
      "staffRoster.roles.cna": "Aide-Soignant",
      "staffRoster.roles.socialWorker": "Travailleur Social",
      "staffRoster.roles.chaplain": "Aumônier",
      "staffRoster.roles.physician": "Médecin",
      "staffRoster.roles.staff": "Personnel",
      "staffRoster.roles.volunteer": "Bénévole",
      "staffRoster.status.active": "Actif",
      "staffRoster.status.inactive": "Inactif",
      "staffRoster.status.onLeave": "En Congé",
      
      // Demo Page - Interactive Tour (French)
      "demo.welcome.title": "Bienvenue chez Goodwin Cares",
      "demo.welcome.subtitle": "Soins Compatissants, Propulsés par la Technologie",
      "demo.welcome.description": "Une plateforme complète de soutien aux soins palliatifs et aux aidants qui combine l'intelligence alimentée par l'IA avec la compassion humaine.",
      "demo.welcome.patientCare": "Soins Centrés sur le Patient",
      "demo.welcome.hipaa": "Conforme HIPAA",
      "demo.welcome.aiInsights": "Analyses IA",
      "demo.welcome.familyEngagement": "Engagement Familial",
      "demo.patientCare.title": "Soins Complets aux Patients",
      "demo.patientCare.subtitle": "Tout ce dont les familles ont besoin en un seul endroit",
      "demo.patientCare.description": "Les patients et les aidants peuvent suivre les symptômes, les médicaments et communiquer directement avec leur équipe de soins.",
      "demo.patientCare.checkins": "Check-ins Quotidiens",
      "demo.patientCare.checkinsDesc": "Suivez douleur, humeur et symptômes",
      "demo.patientCare.medications": "Rappels de Médicaments",
      "demo.patientCare.medicationsDesc": "Ne manquez jamais une dose",
      "demo.patientCare.messaging": "Messagerie Directe",
      "demo.patientCare.messagingDesc": "Discutez avec votre équipe de soins",
      "demo.patientCare.symptoms": "Suivi des Symptômes",
      "demo.patientCare.symptomsDesc": "Tendances de santé visuelles",
      "demo.communication.title": "Communication en Temps Réel",
      "demo.communication.subtitle": "Restez connecté avec votre équipe de soins",
      "demo.communication.description": "Messagerie sécurisée, appels vidéo et notifications instantanées gardent tout le monde informé et connecté.",
      "demo.communication.telehealth": "Visites de Télésanté",
      "demo.communication.telehealthDesc": "Face à face depuis la maison",
      "demo.communication.secureMsg": "Messagerie Sécurisée",
      "demo.communication.secureMsgDesc": "Chat conforme HIPAA",
      "demo.communication.notifications": "Notifications Push",
      "demo.communication.notificationsDesc": "Alertes en temps réel",
      "demo.communication.triage": "Triage Téléphonique",
      "demo.communication.triageDesc": "Support urgent 24/7",
      "demo.ai.title": "Intelligence Alimentée par l'IA",
      "demo.ai.subtitle": "Des fonctionnalités intelligentes qui font la différence",
      "demo.ai.description": "Notre IA analyse les données des patients pour fournir des informations exploitables et aider le personnel à prodiguer des soins proactifs.",
      "demo.ai.chatAssistant": "Assistant Chat IA",
      "demo.ai.chatAssistantDesc": "Obtenez des réponses instantanées",
      "demo.ai.suggestions": "Suggestions Intelligentes",
      "demo.ai.suggestionsDesc": "Réponses IA aux messages",
      "demo.ai.predictive": "Alertes Prédictives",
      "demo.ai.predictiveDesc": "Système d'alerte précoce",
      "demo.ai.autoDocs": "Documentation Auto",
      "demo.ai.autoDocsDesc": "Notes simplifiées",
      "demo.scheduling.title": "Planification Intelligente",
      "demo.scheduling.subtitle": "Optimisez la prestation de soins",
      "demo.scheduling.description": "La planification des visites optimisée par l'IA assure que le bon personnel atteigne le bon patient au bon moment.",
      "demo.scheduling.calendar": "Vue Calendrier",
      "demo.scheduling.calendarDesc": "Gestion visuelle des horaires",
      "demo.scheduling.geo": "Optimisation Géographique",
      "demo.scheduling.geoDesc": "Routage efficace",
      "demo.scheduling.aiSuggestions": "Suggestions IA",
      "demo.scheduling.aiSuggestionsDesc": "Créneaux horaires optimaux",
      "demo.scheduling.staff": "Gestion du Personnel",
      "demo.scheduling.staffDesc": "Disponibilité et congés",
      "demo.training.title": "Académie Hospice",
      "demo.training.subtitle": "Apprentissage continu pour l'excellence",
      "demo.training.description": "Plateforme de formation complète avec curriculum aligné ELNEC, cours interactifs, quiz et génération de certificats CEU.",
      "demo.training.courses": "56+ Cours",
      "demo.training.coursesDesc": "11 parcours de formation",
      "demo.training.ceu": "Certificats CEU",
      "demo.training.ceuDesc": "Diplômes téléchargeables",
      "demo.training.badges": "Badges de Réussite",
      "demo.training.badgesDesc": "Apprentissage ludifié",
      "demo.training.progress": "Suivi de Progression",
      "demo.training.progressDesc": "Individuel et équipe",
      "demo.analytics.title": "Analyses et Insights",
      "demo.analytics.subtitle": "Amélioration des soins basée sur les données",
      "demo.analytics.description": "Tableaux de bord complets pour la performance du personnel, l'engagement familial et les métriques opérationnelles.",
      "demo.analytics.performance": "Performance du Personnel",
      "demo.analytics.performanceDesc": "Temps de réponse et résultats",
      "demo.analytics.engagement": "Engagement Familial",
      "demo.analytics.engagementDesc": "Métriques d'interaction",
      "demo.analytics.cahps": "Prédicteur CAHPS",
      "demo.analytics.cahpsDesc": "Scores de qualité",
      "demo.analytics.clinical": "Insights Cliniques",
      "demo.analytics.clinicalDesc": "Modèles de soins",
      "demo.getStarted.title": "Prêt à Transformer les Soins?",
      "demo.getStarted.subtitle": "Rejoignez la famille Goodwin Cares",
      "demo.getStarted.description": "Que vous soyez patient, aidant ou professionnel de santé, Goodwin Cares fournit les outils dont vous avez besoin.",
      "demo.getStarted.forPatients": "Pour les Patients",
      "demo.getStarted.forPatientsDesc": "Inscrivez-vous pour le soutien aux soins",
      "demo.getStarted.forProviders": "Pour les Prestataires",
      "demo.getStarted.forProvidersDesc": "Rejoignez notre réseau de soins",
      "demo.getStarted.forPartners": "Pour les Partenaires",
      "demo.getStarted.forPartnersDesc": "Intégration santé",
      "demo.getStarted.forFamilies": "Pour les Familles",
      "demo.getStarted.forFamiliesDesc": "Ressources pour aidants",
      
      // Language Tour
      "languageTour.title": "Langue Changée !",
      "languageTour.welcome": "Bienvenue",
      "languageTour.keyPhrases": "Phrases clés dans votre langue :",
      "languageTour.home": "Accueil",
      "languageTour.help": "Obtenir de l'Aide",
      "languageTour.messages": "Messages",
      "languageTour.profile": "Profil",
      "languageTour.gotIt": "Compris !",
      
      // Voice Chat
      "chat.recordingStarted": "Enregistrement démarré - parlez maintenant",
      "chat.microphoneDenied": "Accès au microphone refusé. Veuillez activer les permissions du microphone.",
      "chat.transcriptionComplete": "Transcription terminée",
      "chat.noSpeechDetected": "Aucune parole détectée. Veuillez réessayer.",
      "chat.transcriptionFailed": "Échec de la transcription audio. Veuillez réessayer.",
      "chat.recording": "Enregistrement",
      "chat.transcribing": "Transcription...",
      "chat.speaking": "Lecture...",
      "chat.ttsDisabled": "Réponses vocales désactivées",
      "chat.ttsEnabled": "Réponses vocales activées",
      "chat.ttsOn": "Voix On",
      "chat.ttsOff": "Voix Off",
      "chat.speakNow": "Parlez maintenant...",
      "chat.voiceCommand.cleared": "Chat effacé par commande vocale",
      "chat.voiceCommand.newStarted": "Nouvelle conversation démarrée par commande vocale",
      "chat.voiceCommand.stopped": "Voix arrêtée par commande",
      "chat.voiceCommand.hint": "Essayez de dire: \"effacer le chat\", \"nouvelle conversation\", ou \"arrête de parler\"",
      
      // Staff Portal
      "staffPortal.title": "Portail du Personnel",
      "staffPortal.subtitle": "Gérez vos patients et communications",
      "staffPortal.tabs.caseload": "Mes Patients",
      "staffPortal.tabs.messages": "Messages",
      "staffPortal.tabs.alerts": "Alertes Cliniques",
      "staffPortal.caseload.search": "Rechercher des patients...",
      "staffPortal.caseload.assignPatient": "Assigner un Patient",
      "staffPortal.caseload.noPatients": "Aucun patient dans votre charge",
      "staffPortal.messages.newConversation": "Nouvelle Conversation",
      "staffPortal.alerts.noAlerts": "Aucune alerte active",
      
      // Contact Page
      "contact.caption": "Contactez-nous",
      "contact.title": "Nous Sommes Là pour Vous Aider",
      "contact.description": "Vous avez des questions sur les soins palliatifs, besoin de soutien, ou souhaitez en savoir plus sur nos services? Contactez-nous à tout moment. Nous sommes là pour vous.",
      "contact.phone": "Téléphone",
      "contact.available247": "Disponible 24h/24",
      "contact.email": "Email",
      "contact.responseTime": "Nous répondons sous 24 heures",
      "contact.hours": "Horaires",
      "contact.24hours": "24 heures sur 24",
      "contact.7days": "7 jours sur 7",
      "contact.compassionateNote": "Nous comprenons que vous contacter en ce moment peut être difficile. Sachez que notre équipe est là avec compassion et sans jugement. Vos questions et préoccupations comptent pour nous.",
      "contact.sendMessage": "Envoyez-nous un Message",
      "contact.thankYou": "Merci",
      "contact.messageReceived": "Votre message a été reçu. Nous vous contacterons bientôt.",
      "contact.sendAnother": "Envoyer un Autre Message",
      "contact.fullName": "Nom Complet",
      "contact.namePlaceholder": "Votre nom",
      "contact.emailAddress": "Adresse Email",
      "contact.emailPlaceholder": "votre@email.com",
      "contact.phoneNumber": "Numéro de Téléphone",
      "contact.subject": "Sujet",
      "contact.subjectPlaceholder": "Comment pouvons-nous vous aider?",
      "contact.message": "Message",
      "contact.messagePlaceholder": "Dites-nous comment nous pouvons vous aider...",
      "contact.sending": "Envoi en cours...",
      "contact.sendMessageBtn": "Envoyer le Message",
      
      // Translation Dashboard
      "translationDashboard.title": "Tableau de Bord des Traductions",
      "translationDashboard.subtitle": "Suivre la couverture d'internationalisation sur toutes les pages et langues",
      "translationDashboard.coverage": "Couverture",
      "translationDashboard.keysTranslated": "clés traduites",
      "translationDashboard.pages": "pages",
      "translationDashboard.totalPages": "Total Pages/Composants",
      "translationDashboard.languagesComplete": "Langues 90%+ Complètes",
      "translationDashboard.missingKeys": "Clés Manquantes",
      "translationDashboard.copyMissing": "Copier Manquantes",
      "translationDashboard.exportSuccess": "Clés manquantes exportées avec succès",
      "translationDashboard.copiedToClipboard": "Clés manquantes copiées",
      
      // Admin Dashboard
      "adminDashboard.title": "Tableau de Bord Admin",
      "adminDashboard.subtitle": "Surveiller les analyses du site, l'utilisation du chat et les messages de contact",
      "adminDashboard.refreshData": "Actualiser les Données",
      "adminDashboard.accessRequired": "Accès Admin Requis",
      "adminDashboard.noPrivileges": "Votre compte n'a pas les privilèges d'administrateur.",
      "adminDashboard.pleaseSignIn": "Veuillez vous connecter avec un compte admin.",
      "adminDashboard.signInAsAdmin": "Se Connecter en Admin",
      "adminDashboard.stats.totalVisits": "Visites Totales",
      "adminDashboard.stats.chatSessions": "Sessions Chat",
      "adminDashboard.stats.totalMessages": "Messages Totaux",
      "adminDashboard.stats.uniqueUsers": "Utilisateurs Uniques",
      "adminDashboard.stats.contactMessages": "Messages Contact",
      
      // Staff Portal (French)
      "staffPortal.title": "Portail du Personnel",
      "staffPortal.subtitle": "Gérez vos patients et communiquez avec votre équipe",
      "staffPortal.caseload.assignedPatients": "Patients Assignés",
      "staffPortal.caseload.addPatient": "Ajouter un Patient",
      "staffPortal.caseload.noPatientsAssigned": "Aucun patient assigné",
      "staffPortal.tabs.moodTrends": "Tendances d'Humeur",
      "staffPortal.tabs.system": "Système",
      "staffPortal.clinicalAlerts.title": "Alertes de Documentation Clinique",
      "staffPortal.clinicalAlerts.allClear": "Tout est Clair!",
      "staffPortal.documentation.bestPractices": "Meilleures Pratiques de Documentation",
      "staffPortal.appreciation.yourStats": "Vos Statistiques d'Appréciation",
      "staffPortal.appreciation.recentAppreciations": "Appréciations Récentes",
      "staffPortal.smsSettings.title": "Paramètres de Notifications SMS",
      "staffPortal.addPatient.title": "Ajouter un Nouveau Patient",
      "staffPortal.addPatient.success": "Patient Créé avec Succès!",
      "staffPortal.addPatient.fullName": "Nom Complet",
      "staffPortal.addPatient.email": "Adresse Email",
      "staffPortal.addPatient.phone": "Téléphone",
      "staffPortal.addPatient.createPatient": "Créer le Patient",
      
      // Programs Page (French)
      "programs.hero.badge": "Au-delà des Soins Traditionnels",
      "programs.hero.title": "Programmes",
      "programs.hero.titleHighlight": "Complémentaires",
      "programs.benefits": "Avantages",
      "programs.servicesWeHelp": "Services avec lesquels Nous Aidons",
      "programs.call": "Appeler",
      "programs.visitWebsite": "Visiter le Site Web",
      "programs.cta.title": "Des Questions sur Nos Programmes?",
      "programs.cta.contactOnline": "Nous Contacter en Ligne",
      "programs.massage.title": "Thérapie par Massage en Fin de Vie",
      "programs.massage.subtitle": "Confort par le Toucher Guérisseur",
      "programs.doula.title": "Services de Doula en Fin de Vie",
      "programs.doula.subtitle": "Accompagnement Compatissant dans la Transition",
      "programs.aftercare.title": "Suivi Complet",
      "programs.aftercare.subtitle": "Nous Pouvons Être Votre Guide",
      
      // Moods (French)
      "moods.happy": "Heureux",
      "moods.calm": "Calme",
      "moods.grateful": "Reconnaissant",
      "moods.hopeful": "Optimiste",
      "moods.content": "Satisfait",
      "moods.anxious": "Anxieux",
      "moods.sad": "Triste",
      "moods.frustrated": "Frustré",
      "moods.tired": "Fatigué",
      "moods.scared": "Effrayé",
      "moods.angry": "En Colère"
    }
  },
  ar: {
    translation: {
      // Common (Arabic - RTL)
      "app.name": "غودوين كيرز",
      "app.tagline": "دعم الرعاية التلطيفية الرحيم",
      "common.loading": "جاري التحميل...",
      "common.save": "حفظ",
      "common.cancel": "إلغاء",
      "common.delete": "حذف",
      "common.edit": "تعديل",
      "common.close": "إغلاق",
      "common.submit": "إرسال",
      "common.back": "رجوع",
      "common.next": "التالي",
      "common.yes": "نعم",
      "common.no": "لا",
      "common.search": "بحث",
      "common.filter": "تصفية",
      "common.refresh": "تحديث",
      
      // Navigation
      "nav.home": "الرئيسية",
      "nav.dashboard": "لوحة التحكم",
      "nav.chat": "اسأل الذكاء الاصطناعي",
      "nav.symptoms": "الأعراض",
      "nav.journal": "اليوميات",
      "nav.medications": "الأدوية",
      "nav.calendar": "التقويم",
      "nav.resources": "الموارد",
      "nav.education": "التعليم",
      "nav.videos": "فيديوهات التدريب",
      "nav.contact": "اتصل بنا",
      "nav.messages": "الرسائل",
      "nav.memories": "الذكريات",
      "nav.compliance": "الأمان",
      "nav.signIn": "تسجيل الدخول",
      "nav.signOut": "تسجيل الخروج",
      
      // Dashboard
      "dashboard.greeting.morning": "صباح الخير",
      "dashboard.greeting.afternoon": "مساء الخير",
      "dashboard.greeting.evening": "مساء الخير",
      "dashboard.checkin": "تسجيل الحضور",
      "dashboard.checkedIn": "تم التسجيل",
      "dashboard.streak": "أيام متتالية",
      "dashboard.medications": "الأدوية",
      "dashboard.upcoming": "القادم",
      "dashboard.careTeam": "فريق الرعاية",
      "dashboard.quickActions": "إجراءات سريعة",
      "dashboard.askAI": "اسأل الذكاء الاصطناعي",
      "dashboard.insights": "رؤى الرعاية",
      "dashboard.generateInsights": "توليد",
      "dashboard.manual": "يدوي",
      "dashboard.voice": "صوتي",
      
      // Check-in
      "checkin.title": "التسجيل اليومي",
      "checkin.overall": "كيف تشعر بشكل عام؟",
      "checkin.pain": "مستوى الألم",
      "checkin.sleep": "جودة النوم",
      "checkin.appetite": "الشهية",
      "checkin.mobility": "الحركة",
      "checkin.mood": "المزاج الحالي",
      "checkin.symptoms": "هل هناك أعراض اليوم؟",
      "checkin.notes": "هل هناك ملاحظات؟",
      "checkin.complete": "إكمال التسجيل",
      "checkin.voiceTitle": "التسجيل الصوتي",
      
      // Moods
      "mood.peaceful": "مسالم",
      "mood.happy": "سعيد",
      "mood.okay": "بخير",
      "mood.tired": "متعب",
      "mood.anxious": "قلق",
      "mood.sad": "حزين",
      
      // Symptoms
      "symptom.pain": "ألم",
      "symptom.nausea": "غثيان",
      "symptom.fatigue": "إرهاق",
      "symptom.breathlessness": "ضيق التنفس",
      "symptom.constipation": "إمساك",
      "symptom.anxiety": "قلق",
      "symptom.confusion": "ارتباك",
      "symptom.appetiteLoss": "فقدان الشهية",
      "symptom.insomnia": "أرق",
      "symptom.weakness": "ضعف",
      
      // Medications
      "medications.title": "الأدوية",
      "medications.add": "إضافة دواء",
      "medications.todaySchedule": "جدول اليوم",
      "medications.allMedications": "جميع الأدوية",
      "medications.markTaken": "تحديد كمأخوذ",
      "medications.skip": "تخطي",
      "medications.taken": "مأخوذ",
      "medications.name": "اسم الدواء",
      "medications.dosage": "الجرعة",
      "medications.frequency": "التكرار",
      "medications.times": "الأوقات",
      "medications.instructions": "تعليمات خاصة",
      
      // Calendar
      "calendar.title": "تقويم الرعاية",
      "calendar.addEvent": "إضافة حدث",
      "calendar.upcoming": "الأحداث القادمة",
      "calendar.appointment": "موعد",
      "calendar.visit": "زيارة منزلية",
      "calendar.reminder": "تذكير",
      "calendar.milestone": "معلم",
      
      // Emergency
      "emergency.title": "جهات اتصال الطوارئ",
      "emergency.hospice": "غودوين هوسبيس",
      "emergency.nurseLine": "خط الممرضات 24/7",
      "emergency.call911": "اتصل بـ 911",
      
      // Voice Commands
      "voice.listening": "جاري الاستماع...",
      "voice.speak": "تحدث",
      "voice.stop": "توقف",
      "voice.repeat": "كرر",
      "voice.notSupported": "التعرف على الصوت غير مدعوم",
      "voice.micDenied": "تم رفض الوصول للميكروفون",
      
      // Auth
      "auth.signIn": "تسجيل الدخول",
      "auth.signUp": "إنشاء حساب",
      "auth.email": "البريد الإلكتروني",
      "auth.password": "كلمة المرور",
      "auth.name": "الاسم",
      "auth.forgotPassword": "نسيت كلمة المرور؟",
      "auth.welcomeBack": "مرحباً بعودتك!",
      
      // Session
      "session.expiring": "ستنتهي الجلسة قريباً",
      "session.extend": "تمديد الجلسة",
      "session.remaining": "الوقت المتبقي",
      
      // Compliance
      "compliance.title": "امتثال HIPAA",
      "compliance.2fa": "المصادقة الثنائية",
      "compliance.enabled": "مفعل",
      "compliance.disabled": "معطل",
      "compliance.sessionTimeout": "انتهاء مهلة الجلسة",
      "compliance.auditLogging": "سجل التدقيق",
      "compliance.encryption": "بيانات مشفرة",
      
      // Family Portal
      "familyPortal.title": "بوابة العائلة",
      "familyPortal.subtitle": "ابق على اتصال مع فريق رعاية أحبائك",
      "familyPortal.newNotifications": "إشعارات جديدة",
      "familyPortal.enhancedCare": "رعاية راحة معززة",
      "familyPortal.enhancedCareDesc": "يقدم فريق الرعاية لدينا رعاية تركز على الراحة. نحن هنا لدعم أحبائك وعائلتك خلال هذا الوقت.",
      "familyPortal.nurseLineLabel": "خط الممرضات 24/7",
      "familyPortal.messageTeam": "مراسلة الفريق",
      "familyPortal.sendMessage": "إرسال رسالة لفريق الرعاية",
      "familyPortal.messageDesc": "سيتم إرسال رسالتك إلى فريق الرعاية بأكمله",
      "familyPortal.yourName": "اسمك",
      "familyPortal.yourNamePlaceholder": "اسمك",
      "familyPortal.relationship": "العلاقة",
      "familyPortal.relationshipPlaceholder": "مثل: ابنة، ابن، زوج",
      "familyPortal.markUrgent": "تحديد كعاجل",
      "familyPortal.callNurse": "الاتصال بالممرضة",
      "familyPortal.requestChaplain": "طلب قسيس",
      "familyPortal.scheduleVideoCall": "جدولة مكالمة فيديو",
      "familyPortal.overview": "نظرة عامة",
      "familyPortal.updates": "التحديثات",
      "familyPortal.resources": "الموارد",
      "familyPortal.careTimeline": "الجدول الزمني للرعاية",
      "familyPortal.yourCareTeam": "فريق الرعاية الخاص بك",
      "familyPortal.primaryNurse": "الممرضة الرئيسية",
      "familyPortal.attendingPhysician": "الطبيب المعالج",
      "familyPortal.socialWorker": "الأخصائي الاجتماعي",
      "familyPortal.supportServices": "خدمات الدعم",
      "familyPortal.contact": "اتصل",
      "familyPortal.message": "رسالة",
      "familyPortal.request": "طلب",
      "familyPortal.upcomingVisits": "الزيارات القادمة",
      "familyPortal.scheduled": "مجدول",
      "familyPortal.visitScheduleAppear": "سيظهر جدول الزيارات هنا",
      "familyPortal.currentFocusAreas": "مجالات التركيز الحالية",
      "familyPortal.recentNotifications": "الإشعارات الأخيرة",
      "familyPortal.noNotifications": "لا توجد إشعارات بعد",
      "familyPortal.careUpdates": "تحديثات الرعاية",
      "familyPortal.recentUpdates": "التحديثات الأخيرة من فريق الرعاية",
      "familyPortal.noUpdates": "لا توجد تحديثات بعد",
      "familyPortal.from": "من",
      "familyPortal.download": "تحميل",
      "familyPortal.careTimelineActivities": "أنشطة الرعاية الأخيرة",
      "familyPortal.timelineAppear": "سيظهر الجدول الزمني للرعاية هنا",
      "familyPortal.emergencyLine": "خط الطوارئ 24/7",
      "familyPortal.callNow": "اتصل الآن",
      
      // Resources Page
      "resources.location": "شمال فرجينيا",
      "resources.title": "موارد",
      "resources.titleHighlight": "المجتمع",
      "resources.description": "خدمات الدعم المحلية لعائلات الرعاية التلطيفية في منطقة شمال فرجينيا.",
      "resources.searchPlaceholder": "البحث عن الموارد...",
      "resources.allResources": "جميع الموارد",
      "resources.noResults": "لم يتم العثور على موارد تطابق معاييرك.",
      "resources.needHelp": "تحتاج مساعدة في إيجاد الموارد؟",
      "resources.helpDescription": "يمكن لمساعدنا الذكي مساعدتك في العثور على الموارد المجتمعية المناسبة بناءً على احتياجاتك.",
      "resources.chatWithAI": "الدردشة مع المساعد الذكي",
      
      // Staff Roster
      "staffRoster.title": "قائمة الموظفين",
      "staffRoster.subtitle": "جميع الموظفين المسجلين في النظام",
      "staffRoster.loading": "جاري تحميل قائمة الموظفين...",
      "staffRoster.totalStaff": "إجمالي الموظفين",
      "staffRoster.searchPlaceholder": "البحث بالاسم أو البريد أو التخصص...",
      "staffRoster.allRoles": "جميع الأدوار",
      "staffRoster.allStatus": "جميع الحالات",
      "staffRoster.clearFilters": "مسح الفلاتر",
      "staffRoster.staffMembers": "أعضاء الطاقم",
      "staffRoster.noResults": "لم يتم العثور على موظفين يطابقون الفلاتر",
      "staffRoster.lastLogin": "آخر تسجيل دخول",
      "staffRoster.completed": "مكتمل",
      "staffRoster.pending": "قيد الانتظار",
      "staffRoster.sendEmail": "إرسال بريد",
      "staffRoster.viewTraining": "عرض التدريب",
      "staffRoster.viewProfile": "عرض الملف الشخصي",
      "staffRoster.roles.admin": "مسؤول",
      "staffRoster.roles.nurse": "ممرضة",
      "staffRoster.roles.rn": "ممرضة مسجلة",
      "staffRoster.roles.lpn": "ممرضة عملية",
      "staffRoster.roles.cna": "مساعد تمريض",
      "staffRoster.roles.socialWorker": "أخصائي اجتماعي",
      "staffRoster.roles.chaplain": "قسيس",
      "staffRoster.roles.physician": "طبيب",
      "staffRoster.roles.staff": "موظف",
      "staffRoster.roles.volunteer": "متطوع",
      "staffRoster.status.active": "نشط",
      "staffRoster.status.inactive": "غير نشط",
      "staffRoster.status.onLeave": "في إجازة",
      
      // Language Tour
      "languageTour.title": "تم تغيير اللغة!",
      "languageTour.welcome": "مرحباً",
      "languageTour.keyPhrases": "العبارات الرئيسية بلغتك:",
      "languageTour.home": "الرئيسية",
      "languageTour.help": "احصل على مساعدة",
      "languageTour.messages": "الرسائل",
      "languageTour.profile": "الملف الشخصي",
      "languageTour.gotIt": "فهمت!",
      
      // Voice Chat
      "chat.recordingStarted": "بدأ التسجيل - تحدث الآن",
      "chat.microphoneDenied": "تم رفض الوصول للميكروفون. يرجى تمكين أذونات الميكروفون.",
      "chat.transcriptionComplete": "اكتملت النسخ",
      "chat.noSpeechDetected": "لم يتم اكتشاف كلام. يرجى المحاولة مرة أخرى.",
      "chat.transcriptionFailed": "فشل نسخ الصوت. يرجى المحاولة مرة أخرى.",
      "chat.recording": "جاري التسجيل",
      "chat.transcribing": "جاري النسخ...",
      "chat.speaking": "جاري التحدث...",
      "chat.ttsDisabled": "تم تعطيل الردود الصوتية",
      "chat.ttsEnabled": "تم تمكين الردود الصوتية",
      "chat.ttsOn": "الصوت مفعل",
      "chat.ttsOff": "الصوت معطل",
      "chat.speakNow": "تحدث الآن...",
      "chat.voiceCommand.cleared": "تم مسح الدردشة بالأمر الصوتي",
      "chat.voiceCommand.newStarted": "بدأت محادثة جديدة بالأمر الصوتي",
      "chat.voiceCommand.stopped": "توقف الصوت بالأمر",
      "chat.voiceCommand.hint": "حاول قول: \"مسح الدردشة\"، \"محادثة جديدة\"، أو \"توقف عن الكلام\"",
      
      // Staff Portal
      "staffPortal.title": "بوابة الموظفين",
      "staffPortal.subtitle": "إدارة حالاتك والاتصالات",
      "staffPortal.tabs.caseload": "حالاتي",
      "staffPortal.tabs.messages": "الرسائل",
      "staffPortal.tabs.alerts": "التنبيهات السريرية",
      "staffPortal.caseload.search": "البحث عن المرضى...",
      "staffPortal.caseload.assignPatient": "تعيين مريض",
      "staffPortal.caseload.noPatients": "لا يوجد مرضى في قائمتك",
      "staffPortal.messages.newConversation": "محادثة جديدة",
      "staffPortal.alerts.noAlerts": "لا توجد تنبيهات نشطة",
      
      // Contact Page
      "contact.caption": "تواصل معنا",
      "contact.title": "نحن هنا للمساعدة",
      "contact.description": "هل لديك أسئلة حول رعاية المسنين، تحتاج دعماً، أو تريد معرفة المزيد عن خدماتنا؟ تواصل معنا في أي وقت. نحن هنا من أجلك.",
      "contact.phone": "الهاتف",
      "contact.available247": "متاح على مدار الساعة",
      "contact.email": "البريد الإلكتروني",
      "contact.responseTime": "نرد خلال 24 ساعة",
      "contact.hours": "ساعات العمل",
      "contact.24hours": "24 ساعة في اليوم",
      "contact.7days": "7 أيام في الأسبوع",
      "contact.compassionateNote": "نتفهم أن التواصل في هذا الوقت قد يكون صعباً. اعلم أن فريقنا هنا بتعاطف وبدون حكم. أسئلتك ومخاوفك مهمة لنا.",
      "contact.sendMessage": "أرسل لنا رسالة",
      "contact.thankYou": "شكراً لك",
      "contact.messageReceived": "تم استلام رسالتك. سنتواصل معك قريباً.",
      "contact.sendAnother": "إرسال رسالة أخرى",
      "contact.fullName": "الاسم الكامل",
      "contact.namePlaceholder": "اسمك",
      "contact.emailAddress": "عنوان البريد الإلكتروني",
      "contact.emailPlaceholder": "your@email.com",
      "contact.phoneNumber": "رقم الهاتف",
      "contact.subject": "الموضوع",
      "contact.subjectPlaceholder": "كيف يمكننا مساعدتك؟",
      "contact.message": "الرسالة",
      "contact.messagePlaceholder": "أخبرنا كيف يمكننا مساعدتك...",
      "contact.sending": "جاري الإرسال...",
      "contact.sendMessageBtn": "إرسال الرسالة",
      
      // Translation Dashboard
      "translationDashboard.title": "لوحة تقدم الترجمة",
      "translationDashboard.subtitle": "تتبع تغطية التدويل عبر جميع الصفحات واللغات",
      "translationDashboard.coverage": "التغطية",
      "translationDashboard.keysTranslated": "مفاتيح مترجمة",
      "translationDashboard.pages": "صفحات",
      "translationDashboard.totalPages": "إجمالي الصفحات/المكونات",
      "translationDashboard.languagesComplete": "لغات مكتملة 90%+",
      "translationDashboard.missingKeys": "المفاتيح المفقودة",
      "translationDashboard.copyMissing": "نسخ المفقودة",
      
      // Admin Dashboard
      "adminDashboard.title": "لوحة الإدارة",
      "adminDashboard.subtitle": "مراقبة تحليلات الموقع واستخدام الدردشة ورسائل الاتصال",
      "adminDashboard.refreshData": "تحديث البيانات",
      "adminDashboard.accessRequired": "مطلوب صلاحية المسؤول",
      "adminDashboard.noPrivileges": "حسابك ليس لديه صلاحيات المسؤول.",
      "adminDashboard.pleaseSignIn": "يرجى تسجيل الدخول بحساب مسؤول.",
      "adminDashboard.signInAsAdmin": "تسجيل الدخول كمسؤول",
      "adminDashboard.stats.totalVisits": "إجمالي الزيارات",
      "adminDashboard.stats.chatSessions": "جلسات الدردشة",
      "adminDashboard.stats.totalMessages": "إجمالي الرسائل",
      "adminDashboard.stats.uniqueUsers": "المستخدمون الفريدون",
      "adminDashboard.stats.contactMessages": "رسائل الاتصال",
      
      // Video Library (Arabic)
      "videoLibrary.backToHome": "العودة للرئيسية",
      "videoLibrary.title": "مكتبة الفيديو",
      "videoLibrary.description": "استكشف مجموعتنا من العروض التوضيحية الكاملة. يوفر كل فيديو تجربة سلسة وكاملة تعرض قدرات Goodwin Cares.",
      "videoLibrary.fullVideos": "{{count}} فيديو كامل",
      "videoLibrary.takeInteractiveTour": "خذ الجولة التفاعلية",
      "videoLibrary.searchPlaceholder": "البحث في الفيديوهات...",
      "videoLibrary.fullLengthTitle": "فيديوهات الميزات الكاملة",
      "videoLibrary.fullLengthDescription": "يتم تشغيل كل فيديو بسلاسة دون انقطاع - مقدمة العلامة التجارية، عرض الميزات الكامل، والخاتمة المحترفة.",
      "videoLibrary.allFeatures": "جميع الميزات",
      "videoLibrary.noVideosFound": "لم يتم العثور على فيديوهات",
      "videoLibrary.tryAdjusting": "حاول تعديل معايير البحث أو التصفية",
      "videoLibrary.cta.title": "هل أنت مستعد لتجربة الرعاية الرحيمة؟",
      "videoLibrary.cta.description": "انضم إلى آلاف العائلات والمتخصصين في الرعاية الصحية الذين يثقون في Goodwin Cares.",
      "videoLibrary.cta.getStarted": "ابدأ الآن",
      "videoLibrary.cta.takeTour": "خذ الجولة",
      "videoLibrary.category.all": "الكل",
      "videoLibrary.category.overview": "نظرة عامة",
      "videoLibrary.category.communication": "التواصل",
      "videoLibrary.category.scheduling": "الجدولة",
      "videoLibrary.category.clinical": "السريري",
      "videoLibrary.category.training": "التدريب",
      "videoLibrary.category.analytics": "التحليلات",
      
      // Programs Page (Arabic)
      "programs.hero.badge": "ما وراء الرعاية التقليدية",
      "programs.hero.title": "البرامج",
      "programs.hero.titleHighlight": "التكميلية",
      "programs.benefits": "الفوائد",
      "programs.servicesWeHelp": "الخدمات التي نساعد بها",
      "programs.call": "اتصل",
      "programs.visitWebsite": "زيارة الموقع",
      "programs.cta.title": "هل لديك أسئلة حول برامجنا؟",
      "programs.cta.contactOnline": "تواصل معنا عبر الإنترنت",
      "programs.massage.title": "العلاج بالتدليك في نهاية الحياة",
      "programs.doula.title": "خدمات الدولا في نهاية الحياة",
      "programs.aftercare.title": "الرعاية اللاحقة الكاملة",
      
      // Demo Page - Interactive Tour (Arabic)
      "demo.welcome.title": "مرحباً بك في Goodwin Cares",
      "demo.welcome.subtitle": "رعاية رحيمة مدعومة بالتكنولوجيا",
      "demo.welcome.description": "منصة شاملة لدعم رعاية المسنين ومقدمي الرعاية تجمع بين الذكاء الاصطناعي والتعاطف الإنساني.",
      "demo.welcome.patientCare": "رعاية تتمحور حول المريض",
      "demo.welcome.hipaa": "متوافق مع HIPAA",
      "demo.welcome.aiInsights": "رؤى مدعومة بالذكاء الاصطناعي",
      "demo.welcome.familyEngagement": "مشاركة العائلة",
      "demo.patientCare.title": "رعاية شاملة للمرضى",
      "demo.patientCare.subtitle": "كل ما تحتاجه العائلات في مكان واحد",
      "demo.patientCare.description": "يمكن للمرضى ومقدمي الرعاية تتبع الأعراض والأدوية والتواصل مباشرة مع فريق الرعاية.",
      "demo.patientCare.checkins": "تسجيل يومي",
      "demo.patientCare.checkinsDesc": "تتبع الألم والمزاج والأعراض",
      "demo.patientCare.medications": "تذكيرات الأدوية",
      "demo.patientCare.medicationsDesc": "لا تفوت أي جرعة",
      "demo.patientCare.messaging": "رسائل مباشرة",
      "demo.patientCare.messagingDesc": "تحدث مع فريق الرعاية",
      "demo.patientCare.symptoms": "تتبع الأعراض",
      "demo.patientCare.symptomsDesc": "اتجاهات الصحة المرئية",
      "demo.communication.title": "التواصل في الوقت الحقيقي",
      "demo.communication.subtitle": "ابق على اتصال مع فريق الرعاية",
      "demo.communication.description": "الرسائل الآمنة ومكالمات الفيديو والإشعارات الفورية تبقي الجميع على اطلاع.",
      "demo.communication.telehealth": "زيارات الرعاية عن بُعد",
      "demo.communication.telehealthDesc": "وجهاً لوجه من المنزل",
      "demo.communication.secureMsg": "رسائل آمنة",
      "demo.communication.secureMsgDesc": "دردشة متوافقة مع HIPAA",
      "demo.communication.notifications": "إشعارات فورية",
      "demo.communication.notificationsDesc": "تنبيهات في الوقت الحقيقي",
      "demo.communication.triage": "فرز المكالمات",
      "demo.communication.triageDesc": "دعم طارئ 24/7",
      "demo.ai.title": "ذكاء مدعوم بالذكاء الاصطناعي",
      "demo.ai.subtitle": "ميزات ذكية تُحدث فرقاً",
      "demo.ai.description": "يحلل ذكاؤنا الاصطناعي بيانات المرضى لتقديم رؤى قابلة للتنفيذ ومساعدة الموظفين على تقديم رعاية استباقية.",
      "demo.ai.chatAssistant": "مساعد دردشة AI",
      "demo.ai.chatAssistantDesc": "احصل على إجابات فورية",
      "demo.ai.suggestions": "اقتراحات ذكية",
      "demo.ai.suggestionsDesc": "ردود رسائل AI",
      "demo.ai.predictive": "تنبيهات تنبؤية",
      "demo.ai.predictiveDesc": "نظام إنذار مبكر",
      "demo.ai.autoDocs": "توثيق تلقائي",
      "demo.ai.autoDocsDesc": "ملاحظات مبسطة",
      "demo.scheduling.title": "جدولة ذكية",
      "demo.scheduling.subtitle": "تحسين تقديم الرعاية",
      "demo.scheduling.description": "جدولة الزيارات المُحسّنة بالذكاء الاصطناعي تضمن وصول الموظف المناسب للمريض المناسب في الوقت المناسب.",
      "demo.scheduling.calendar": "عرض التقويم",
      "demo.scheduling.calendarDesc": "إدارة مرئية للجدول",
      "demo.scheduling.geo": "تحسين جغرافي",
      "demo.scheduling.geoDesc": "توجيه فعال",
      "demo.scheduling.aiSuggestions": "اقتراحات AI",
      "demo.scheduling.aiSuggestionsDesc": "فترات زمنية مثالية",
      "demo.scheduling.staff": "إدارة الموظفين",
      "demo.scheduling.staffDesc": "التوفر والإجازات",
      "demo.training.title": "أكاديمية المسنين",
      "demo.training.subtitle": "تعلم مستمر للتميز",
      "demo.training.description": "منصة تدريب شاملة مع مناهج متوافقة مع ELNEC ودورات تفاعلية واختبارات وشهادات CEU.",
      "demo.training.courses": "56+ دورة",
      "demo.training.coursesDesc": "11 مسار تدريبي",
      "demo.training.ceu": "شهادات CEU",
      "demo.training.ceuDesc": "شهادات قابلة للتحميل",
      "demo.training.badges": "شارات الإنجاز",
      "demo.training.badgesDesc": "تعلم مُلعب",
      "demo.training.progress": "تتبع التقدم",
      "demo.training.progressDesc": "فردي وجماعي",
      "demo.analytics.title": "التحليلات والرؤى",
      "demo.analytics.subtitle": "تحسين الرعاية القائم على البيانات",
      "demo.analytics.description": "لوحات معلومات شاملة لأداء الموظفين ومشاركة العائلة ومقاييس التشغيل.",
      "demo.analytics.performance": "أداء الموظفين",
      "demo.analytics.performanceDesc": "أوقات الاستجابة والنتائج",
      "demo.analytics.engagement": "مشاركة العائلة",
      "demo.analytics.engagementDesc": "مقاييس التفاعل",
      "demo.analytics.cahps": "تنبؤ CAHPS",
      "demo.analytics.cahpsDesc": "درجات الجودة",
      "demo.analytics.clinical": "رؤى سريرية",
      "demo.analytics.clinicalDesc": "أنماط الرعاية",
      "demo.getStarted.title": "هل أنت مستعد لتحويل الرعاية؟",
      "demo.getStarted.subtitle": "انضم إلى عائلة Goodwin Cares",
      "demo.getStarted.description": "سواء كنت مريضاً أو مقدم رعاية أو متخصصاً في الرعاية الصحية، يوفر لك Goodwin Cares الأدوات التي تحتاجها.",
      "demo.getStarted.forPatients": "للمرضى",
      "demo.getStarted.forPatientsDesc": "سجل للحصول على دعم الرعاية",
      "demo.getStarted.forProviders": "لمقدمي الخدمات",
      "demo.getStarted.forProvidersDesc": "انضم إلى شبكة الرعاية لدينا",
      "demo.getStarted.forPartners": "للشركاء",
      "demo.getStarted.forPartnersDesc": "تكامل الرعاية الصحية",
      "demo.getStarted.forFamilies": "للعائلات",
      "demo.getStarted.forFamiliesDesc": "موارد مقدمي الرعاية",
      
      // Moods (Arabic)
      "moods.happy": "سعيد",
      "moods.calm": "هادئ",
      "moods.grateful": "ممتن",
      "moods.hopeful": "متفائل",
      "moods.content": "راضٍ",
      "moods.anxious": "قلق",
      "moods.sad": "حزين",
      "moods.frustrated": "محبط",
      "moods.tired": "متعب",
      "moods.scared": "خائف",
      "moods.angry": "غاضب",
      
      // Common
      "common.returnHome": "العودة للرئيسية"
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  });

export default i18n;
